/**
 * /mira — Les Concierges® two-pane Mira surface
 *
 * Pattern source: TDC-Gift-Two-Pane-Pattern-for-LC.md (23 May 2026)
 * Adapted from /mira-search-v2 on thedoggycompany.com
 *
 * Layout: 1.35fr : 1fr grid on desktop (Mira left, panels right)
 *         Single column with tab strip on mobile
 *
 * v1 scope:
 *   - Left:  Mira conversation (hand-curated greeting + chat with mocked Claude responses)
 *   - Right: Suggested for you (6 pillars) · Verified venues (6 seed cards) · Talk to a human
 *
 * Backing brain (v1): hybrid — local fallback responses for now, wire to canonical
 * MiraBrainResponse Pydantic schema in Week 2 per TDC's day-1 guard #6.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ThemeProvider, useTheme, DEFAULT_THEME } from "@/components/ThemeProvider";
import { useSpeechToText } from "@/hooks/useSpeechToText";
import { toast } from "sonner";
import { mergeActiveContext } from "@/lib/mira-active-context";
import { BACKEND_URL } from "@/lib/backend-url";
// Design tokens now live in /styles/tdc-design-tokens.css + lc-brand-overrides.css
// (imported globally via /index.css). This page no longer needs lc-tokens.css.

// ─────────────────────────────────────────────────────────────────────────────
// SEED DATA · static for v1 — replaced by API calls in Week 2
// ─────────────────────────────────────────────────────────────────────────────

// v3.13 — Session-token-aware fetch helper. Reads the visitor session_token
// issued by /mira/visitor/identify (stored in localStorage) and adds it to
// every Mira API call as `Authorization: Bearer …`. This is how the tenant
// seal stays locked end-to-end on the client. Module-scope so MiraChat and
// MiraPage / basket-send helpers both reach it.
function _authHeadersGlobal() {
  const h = { "Content-Type": "application/json" };
  try {
    const tok = typeof window !== "undefined" ? window.localStorage.getItem("lc_session_token") : null;
    if (tok) h["Authorization"] = `Bearer ${tok}`;
  } catch (_e) { /* localStorage may be unavailable in SSR / private mode */ }
  return h;
}

// v3.46.21 / R32 — "Direct-book intent" silently posts an Ops-side ticket
// the moment a member taps any external link (Visit Website ↗, etc.) so
// the desk team gets the intent in real time and can VIP-call the venue
// while the member is on the venue's site. Returns a Promise that
// resolves with the ticket_id (or null on error — we never block the open).
// Per R32, the member never sees third-party booking buttons; this fire
// remains in place for visit-website opens (research, not a booking).
async function _fireDirectBookIntent(payload) {
  try {
    const res = await fetch(`${BACKEND_URL}/api/mira/direct-book-intent`, {
      method: "POST",
      headers: _authHeadersGlobal(),
      body: JSON.stringify(payload),
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data?.ticket_id || null;
  } catch (_e) {
    return null;
  }
}

// v3.32 — Build a rich basket item from a venue_hint / product_hint card.
// Every field Mira surfaced gets carried into the basket. The 6-field strip
// in v3.14 was the root cause behind A1 (thin brief panel), A2 (thin ops
// note to the concierge), and A3 (no provenance) — fixed in one place.
//
// Used at three call-sites (VenueHintCardCompact, VenueHintCard,
// ProductHintCard). Auto-add also uses it so the brief panel fills as
// Mira composes (Option α).
function buildBasketItemFromCard(card, kind = "venue") {
  if (!card) return null;
  const isProduct = kind === "product";
  const archive = card.archive_status || (card.in_cn_db ? "verified_archive" : "unknown");
  const placesVerified = !!card.places_verified;
  // Derive a source tag for routing / display
  let source = "mira_web_search"; // default — Mira surfaced via web research
  if (placesVerified) source = "mira_places_verified";
  else if (archive === "verified_archive") source = "mira_archive";
  else if (kind === "product") source = "mira_sourcing";
  return {
    kind,
    // Identity
    name: card.name,
    city: card.city || card.location || "",
    area: card.area || "",
    // Editorial
    blurb: card.blurb || "",
    why: card.blurb || "",  // alias for back-compat
    category: card.category || (isProduct ? null : "venue"),
    subcategory: card.subcategory || null,
    pillar: card.pillar || null,
    pillar_name: card.pillar_name || null,
    supplier_class: isProduct ? (card.supplier_class || null) : null,
    // Provenance (the safety tag for the concierge desk)
    archive_status: archive,
    places_verified: placesVerified,
    source,
    // Live Google Places data (when verified)
    place_id: card.place_id || null,
    formatted_address: card.formatted_address || null,
    rating: card.rating || null,
    user_ratings_total: card.user_ratings_total || null,
    rating_source: card.rating_source || null,
    lat: card.lat || null,
    lng: card.lng || null,
    image_url: card.image_url || null,
    map_url: card.map_url || null,
    photo_html_attribution: card.photo_html_attribution || null,
    website_url: card.website_url || null,
    // Action plan — R32-clean: book_direct is always null on the member
    // surface (third-party URLs live on actions._ops_intel for the Ops
    // Docket / Zoho team intelligence only). visit_website is the only
    // external link still allowed; primary action is always concierge.
    actions: card.actions || null,
  };
}

// v3.32 — Provenance metadata for the safety tag rendered on every brief /
// ops item. Tells the concierge what to do (whet vs trust) at a glance.
// 4 states drawn from Mira's data discipline:
//   verified_google  — Google Places confirmed (place_id + rating exist)
//   verified_archive — Les Concierges' own trusted-vendor archive
//   member_named     — the member explicitly named this place
//   lead_unverified  — Mira surfaced via web_search; concierge must whet
//
// v3.34 / H1 — Member-facing tag now reads just "Verified". The detail
// (· Google / · LC archive / · concierge to whet) is OFF-brand on the
// member surface — it names a third party, makes Concierge® look like
// it's Googling. Concierge desk still receives the full provenance detail
// in the Ops Docket (server-side render is untouched). Use mode="desk"
// when rendering inside the admin / Ops view; default "member" everywhere
// the user can see.
// v3.46.4 — Provenance discipline (founder-locked 25 May 2026):
//   MEMBER SURFACE → "Verified" or nothing. The Concierge® promise is
//   "we handle everything" — surfacing the work-in-progress ("to be
//   sourced", "your choice") breaks the magic by letting the member
//   see the kitchen. Mira presents a considered set; the team verifies
//   anything unverified quietly behind the scenes.
//
//   DESK SURFACE (Ops Docket, Zoho, admin) → full honest provenance.
//   The team that ACTS on the brief needs to know which leads still
//   require sourcing, which are archive-verified, which are
//   Google-verified, and which were member-named. Returns the full
//   four-tier label set.
//
//   Returns null when the tier should be hidden from the member.
//   `ProvenanceTag` below handles null gracefully.
function getProvenance(item, mode = "member") {
  const desk = mode === "desk";
  if (!item) {
    return desk
      ? { kind: "lead_unverified", label: "lead · unverified", color: "#C9973A" }
      : null;
  }
  if (item.source === "member_named") {
    return desk
      ? { kind: "member_named", label: "member named", color: "#6E7B72" }
      : null;
  }
  // § 14.19 visual extension (FIR-20260605-007 · 5 Jun 2026 PM late).
  // Backend dispatch authoritatively downgrades `archive_status`
  // to "unknown" for surfaces that fail the 8-gate verification
  // contract (brain-suggested DINE candidates, STAY room-feature
  // turns, etc.). On the MEMBER surface, honour that decision —
  // the chip cannot say VERIFIED even when Places verified the
  // bare existence of the name. Desk surface continues to see the
  // honest provenance for ops workflow.
  //
  // F19 update (3 Jun 2026 PM late · FIR-20260603-009 · binding):
  // The MEMBER surface DOES carry an affirmative chip for "unknown"
  // — "Concierge® to verify" — so the card is visible and the
  // member feels a real person is on it. This replaces the prior
  // silent-null behaviour to honour F19 prose ↔ card parity.
  if (!desk && item.archive_status === "unknown") {
    return {
      kind: "concierge_to_verify",
      label: "Concierge® to verify",
      color: "var(--lc-text-secondary, #6E7B72)",
    };
  }
  if (item.places_verified || item.rating_source === "google_places") {
    return {
      kind: "verified_google",
      label: desk ? "verified · Concierge®" : "Verified",
      color: "#0F3D2E",
    };
  }
  if (item.archive_status === "verified_archive") {
    return {
      kind: "verified_archive",
      label: desk ? "verified · LC archive" : "Verified",
      color: "#0F3D2E",
    };
  }
  return desk
    ? { kind: "lead_unverified", label: "lead · web search, unverified", color: "#C9973A" }
    : null;
}

// v3.32 — Small inline pill rendering a provenance tag on a brief item or
// card. Honours the canonical palette — never a saturated brass fill.
// v3.34 / H1 — Defaults to member-facing mode; pass mode="desk" only on
// admin / concierge views.
// v3.46.4 — Returns null when getProvenance is null (member surface +
// non-verified tier). The wrapper renders nothing — magic preserved.
function ProvenanceTag({ item, compact = false, mode = "member" }) {
  const prov = getProvenance(item, mode);
  if (!prov) return null;
  return (
    <span
      data-testid={`provenance-${prov.kind}`}
      data-provenance={prov.kind}
      title={prov.label}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        fontFamily: "var(--lc-font-body)",
        fontSize: compact ? "0.62rem" : "0.66rem",
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        color: prov.color,
        padding: compact ? "1px 6px" : "2px 8px",
        borderRadius: 999,
        background: "transparent",
        border: `1px solid ${prov.color}33`,
        whiteSpace: "nowrap",
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: prov.color,
          display: "inline-block",
        }}
      />
      {prov.label}
    </span>
  );
}

// v3.46.21 / R32 — CardActionRow renders the 2-action layout per the
//   NO-BOOKING-BUTTON doctrine (26 May 2026 founder lock):
//     Choose (BasketToggle) · Visit website ↗ · Tell me more →
//   No third-party booking button ever reaches the member surface — the
//   relationship runs through the desk. Any provider URL (OpenTable / etc.)
//   the policy resolver computed lives on actions._ops_intel — team
//   intelligence only, NEVER rendered here.
//
// Visibility per category — driven by the `actions` plan attached to the
// card by mira_actions.resolve_card_actions on the server side.
//
// Hierarchy:
//   primary  → brass-filled button (loud, lead with this)
//   secondary → bordered ghost (visible but quieter)
//   faded    → very muted text-link (only for fallback / hotel direct-book)
//   hidden   → not rendered at all (e.g. flights have NO direct-book)
function CardActionRow({ place, idx, basketItem, added, addToBasket, removeFromBasket, onArrange, brief, compact = false }) {
  const actions = place?.actions || null;
  // v3.46.18 — R32 NO-BOOKING-BUTTON DOCTRINE (26 May 2026 founder lock).
  // The member never sees direct-booking buttons — primary_action is
  // always "send_to_concierge". Any third-party URL the resolver computed
  // lives on actions._ops_intel for the Ops Docket (Zoho), never rendered
  // here. visit_website (the venue's own information page) is the only
  // external link still allowed — it's research, not a booking widget.
  const primary = "send_to_concierge";
  // v3.16 — Visit-website fallback when the venue isn't on a known booking
  // platform (e.g. Gandhi in Saint-Tropez has its own website but no OpenTable
  // listing). Rendered as a second CTA — visibility from the resolver.
  const visitWebsite = actions?.visit_website;
  const websiteVisible = !!(visitWebsite?.url);
  const websiteVisibility = visitWebsite?.visibility || "primary";
  const conciergeLabel = actions?.send_to_concierge?.label || "Tell me more →";
  const conciergeVisibility = actions?.send_to_concierge?.visibility || "primary";

  const fontSize = compact ? "0.62rem" : "0.7rem";

  // v3.16 — Visit website opens the venue's own page. Also fires a silent
  // intent ticket so the concierge desk knows the member is on the venue
  // site (so they can VIP-call ahead of the member's enquiry).
  const handleVisitWebsite = async () => {
    if (!visitWebsite?.url) return;
    _fireDirectBookIntent({
      venue_name: place.name,
      venue_city: place.city || null,
      venue_area: place.area || null,
      pillar_name: place.pillar_name || null,
      subcategory: place.subcategory || null,
      provider: "venue_website",
      book_url: visitWebsite.url,
      preferred_date: brief?.date || null,
      preferred_time: brief?.time || null,
      preferred_party_size: brief?.party_size || null,
      occasion: brief?.occasion || null,
      session_id: brief?.session_id || null,
      visitor_id: brief?.visitor_id || null,
      member_name: brief?.member_name || null,
      member_email: brief?.member_email || null,
      member_phone: brief?.member_phone || null,
    }).catch(() => {});
    try {
      window.open(visitWebsite.url, "_blank", "noopener,noreferrer");
    } catch (_e) { /* popup blocked — concierge alert still fired */ }
  };

  // Style per visibility — shared between visit-website and any future
  // member-facing info CTAs.
  const ctaStyle = (visibility) => {
    if (visibility === "primary") {
      return {
        background: "var(--lc-highlight)",
        color: "var(--lc-cream)",
        border: "1px solid var(--lc-highlight)",
        padding: compact ? "0.22rem 0.65rem" : "0.45rem 0.95rem",
        fontWeight: 600,
      };
    }
    if (visibility === "secondary") {
      return {
        background: "transparent",
        color: "var(--lc-highlight)",
        border: "1px solid var(--lc-highlight)",
        padding: compact ? "0.2rem 0.6rem" : "0.4rem 0.85rem",
        fontWeight: 500,
      };
    }
    // faded
    return {
      background: "transparent",
      color: "var(--lc-text-muted)",
      border: "none",
      padding: 0,
      borderBottom: "1px dotted var(--lc-border)",
      fontWeight: 400,
      fontSize: compact ? "0.6rem" : "0.65rem",
    };
  };
  const websiteStyle = ctaStyle(websiteVisibility);

  return (
    <div
      style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: compact ? 6 : 10, marginTop: compact ? 6 : 12, flexWrap: "wrap",
      }}
      data-testid={`card-actions-${idx}`}
    >
      <div style={{ display: "flex", alignItems: "center", gap: compact ? 6 : 8, flexWrap: "wrap" }}>
        <BasketToggle
          item={basketItem}
          added={added}
          onAdd={() => addToBasket(basketItem)}
          onRemove={() => removeFromBasket(basketItem)}
          testid={`basket-toggle-${idx}`}
          variant="primary"
          compact={compact}
        />
        {websiteVisible && (
          <button
            type="button"
            data-testid={`visit-website-${idx}`}
            data-visibility={websiteVisibility}
            className="lc-text-body"
            onClick={handleVisitWebsite}
            style={{
              ...websiteStyle,
              borderRadius: "var(--lc-radius-sm)",
              fontSize: fontSize,
              cursor: "pointer",
              letterSpacing: "0.02em",
              transition: "transform var(--lc-dur) var(--lc-ease)",
            }}
            title="Open the venue's own website — concierge silently alerted."
          >
            {visitWebsite.label || "Visit website ↗"}
          </button>
        )}
      </div>
      {/* v3.46.18 — R32 no-booking-button doctrine. Ask Concierge® is the
          ONLY primary action on every card. The relationship runs through
          the desk; we never surface third-party booking widgets to the
          member. (Third-party URLs the resolver computed live on
          actions._ops_intel for the Zoho Ops Docket — team intelligence
          only, never rendered here.) */}
      <button
        type="button"
        data-testid={`ask-concierge-${idx}`}
        data-visibility={conciergeVisibility}
        className="lc-text-body"
        onClick={() => onArrange && onArrange({ name: place.name, city: place.city })}
        style={{
          background: conciergeVisibility === "primary" ? "var(--lc-highlight)" : "transparent",
          color: conciergeVisibility === "primary" ? "var(--lc-cream, #FAF8F5)" : "var(--lc-text-muted)",
          border: conciergeVisibility === "primary"
            ? "1px solid var(--lc-highlight)"
            : "none",
          fontSize: compact ? "0.62rem" : "0.7rem",
          padding: conciergeVisibility === "primary"
            ? (compact ? "0.22rem 0.7rem" : "0.45rem 0.95rem")
            : 0,
          borderRadius: conciergeVisibility === "primary" ? "var(--lc-radius-sm)" : 0,
          cursor: "pointer",
          textDecoration: conciergeVisibility === "primary" ? "none" : "underline",
          textUnderlineOffset: "2px",
          fontWeight: conciergeVisibility === "primary" ? 600 : 400,
          letterSpacing: "0.02em",
          transition: "transform var(--lc-dur) var(--lc-ease)",
          whiteSpace: "nowrap",
        }}
      >
        {conciergeLabel}
      </button>
    </div>
  );
}

// v3.11 — Canonical 23-pillar taxonomy (Zoho_Pillars_cat_and_Sub_cat.xlsx,
// source-of-truth ordering. Each pillar carries a behaviour-mode tooltip
// (Curatorial, Precision Executor, etc.) that the right-pane rail surfaces
// as a hint and that the brain server-side enforces via the
// PILLAR_CAPTURE_REGISTRY (mira_brain.py).
//
// v3.12 — Added captureFields[] — the BriefBuilder reads this to render the
// FULL expected checklist (gathered ✓, asking ◯, pending dim ◯) rather than
// only what the brain happens to surface on a given turn. Keeps the member
// transparent about what disciplined gathering they're inside.
const PILLAR_DEFS = {
  Concierge:    { label: "Concierge",    hint: "intent · urgency · outcome · context",                  mode: "Interpretive",          captureFields: ["intent","urgency","outcome","context"] },
  Dine:         { label: "Dine",         hint: "city · date · guests · cuisine · occasion · dietary",    mode: "Curatorial",            captureFields: ["city","date","time","party_size","cuisine","occasion","dietary"] },
  Travel:       { label: "Travel",       hint: "origin · destination · dates · purpose · companions",   mode: "Narrative Planner",     captureFields: ["origin","destination","date","purpose","companions"] },
  Stay:         { label: "Stay",         hint: "city · dates · nights · guest mix · comfort band",      mode: "Comfort Architect",     captureFields: ["city","date","nights","guest_mix","budget"] },
  Airport:      { label: "Airport",      hint: "flight · terminal · arrival or departure · assist",     mode: "Precision Executor",    captureFields: ["flight_number","terminal","direction","assistance_type"] },
  Move:         { label: "Move",         hint: "pickup · drop · time · vehicle · luggage",              mode: "Logistics Controller",  captureFields: ["pickup","drop","time","vehicle_type","luggage"] },
  Enjoy:        { label: "Enjoy",        hint: "event · city · date · seats · tone",                    mode: "Experience Composer",   captureFields: ["event_type","city","date","seats","tone"] },
  Club:         { label: "Club",         hint: "club · access · guests · date",                         mode: "Social Cartographer",   captureFields: ["club_name","access_type","party_size","date"] },
  Care:         { label: "Care",         hint: "issue · home location · urgency",                       mode: "Guardian",              captureFields: ["issue","home_location","urgency"] },
  "Shop Assist":{ label: "Shop Assist",  hint: "item · budget · timeline · delivery",                   mode: "Taste-Driven Shopper",  captureFields: ["item_type","budget","timeline","delivery_address"] },
  Work:         { label: "Work",         hint: "objective · timeline · stakeholders · output",          mode: "Strategic Assistant",   captureFields: ["objective","timeline","stakeholders","output_format"] },
  Fit:          { label: "Fit",          hint: "goal · availability · trainer preference",              mode: "Habit Architect",       captureFields: ["health_goal","availability","trainer_preference"] },
  Exclusive:    { label: "Exclusive",    hint: "access · geography · time sensitivity",                 mode: "Gatekeeper",            captureFields: ["access_type","geography","time_sensitivity"] },
  Emergency:    { label: "Emergency",    hint: "crisis · location · immediate risk",                    mode: "Command Mode",          captureFields: ["crisis_nature","location","immediate_risk"] },
  Unique:       { label: "Unique",       hint: "emotional goal · constraints · symbolism",              mode: "Creative Orchestrator", captureFields: ["emotional_goal","constraints","symbolism"] },
  Dreamweaver:  { label: "Dreamweaver",  hint: "milestone · time horizon · personal meaning",           mode: "Life Designer",         captureFields: ["milestone","time_horizon","personal_meaning"] },
  Advisory:     { label: "Advisory",     hint: "topic · decision stage · sensitivity",                  mode: "Thought Partner",       captureFields: ["topic","decision_stage","sensitivity"] },
  Paperwork:    { label: "Paperwork",    hint: "document · authority · deadline · city",                mode: "Compliance Navigator",  captureFields: ["document_type","authority","deadline","city"] },
  Referrals:    { label: "Referrals",    hint: "service · trust level · geography",                     mode: "Trust Broker",          captureFields: ["service_needed","trust_level","geography"] },
  "Mira AI":    { label: "Mira AI",      hint: "what Mira should think or create",                      mode: "Meta-Intelligence",     captureFields: ["request","purpose"] },
  Support:      { label: "Support",      hint: "issue · device · severity",                             mode: "System Stabiliser",     captureFields: ["issue_type","device","severity"] },
  Engagement:   { label: "Engagement",   hint: "trigger event · member state",                          mode: "Relationship Keeper",   captureFields: ["trigger_event","member_state"] },
  Feedback:     { label: "Feedback",     hint: "experience type · sentiment",                           mode: "Reflection Engine",     captureFields: ["experience_type","sentiment"] },
};

// Legacy 6-pillar + 11-pillar → 23-pillar mapping (back-compat for older
// activePillar values still present in saved sessions / older brain outputs)
const LEGACY_PILLAR_MAP = {
  // 6-pillar legacy
  everyday_life: "Concierge",
  family_care: "Care",
  travel: "Travel",
  documentation: "Paperwork",
  moments_of_life: "Enjoy",
  wellness: "Fit",
  // 11-pillar legacy (Escape + Transport renamed)
  Escape: "Travel",
  Transport: "Paperwork",  // documentation flavour; Move covers ground transport
};

// v3.10 — SEED_VENUES removed. The right panel NEVER shows hard-coded
// placeholder venues. During gathering: empty (or Brief Builder only).
// Options surface ONLY from real venue_hints / product_hints returned by
// the brain. Empty-and-honest over fake-and-full.

// ─────────────────────────────────────────────────────────────────────────
// v3.28 — Member-facing pillar set + 5-cluster taxonomy.
//
// Four pillars are kept in the brain but NEVER shown to the member:
//   Mira AI, Support, Engagement, Feedback
// These are operational / meta / internal.
//
// The 19 member-facing pillars are organised into 5 editorial clusters
// (founder-approved 24 May 2026 — names in Mira's editorial register):
//
//   Journeys           — Travel · Stay · Airport · Move
//   The Table          — Dine · Enjoy · Club
//   Looking After      — Care · Fit · Paperwork · Emergency
//   The Extraordinary  — Exclusive · Unique · Dreamweaver
//   Counsel            — Concierge · Advisory · Work · Referrals · Shop Assist
//
// Each cluster surfaces a faint italic sub-line of its pillar names
// (always visible) per the brief's "spread before the eye" register —
// the breadth signal lands without requiring a tap.
// ─────────────────────────────────────────────────────────────────────────
const INTERNAL_ONLY_PILLARS = new Set([
  "Mira AI", "Support", "Engagement", "Feedback",
]);

const PILLAR_CLUSTERS = [
  { key: "journeys",          name: "Journeys",          pillars: ["Travel", "Stay", "Airport", "Move"] },
  { key: "the_table",         name: "The Table",         pillars: ["Dine", "Enjoy", "Club"] },
  { key: "looking_after",     name: "Looking After",     pillars: ["Care", "Fit", "Paperwork", "Emergency"] },
  { key: "the_extraordinary", name: "The Extraordinary", pillars: ["Exclusive", "Unique", "Dreamweaver"] },
  { key: "counsel",           name: "Counsel",           pillars: ["Concierge", "Advisory", "Work", "Referrals", "Shop Assist"] },
];

// v3.21 — Provenance labels per the Mykonos spec.
// Warm, member-facing, NEVER "archive". The internal token (verified_archive
// etc.) is kept for code stability but the rendered string is updated.
//
// F19 (3 Jun 2026 PM late · FIR-20260603-009 · binding):
//   Brain-suggested venues (not in the Concierge® LC archive) render the
//   present-tense concierge label "Concierge® to verify" instead of
//   technical status words. Same truth as the prior "Mira curated" label
//   (nothing claimed verified), but in service register, not engineer
//   register. This honours "the member must never feel the machinery."
//
//   The archive_status remains the source of truth for behaviour
//   (downgrades, dispatcher routing); only the rendered string changes.
const ARCHIVE_BADGES = {
  verified_archive: { label: "Verified by Concierge®",     color: "var(--lc-highlight)",      style: "solid" },
  verified_places:  { label: "Concierge® to verify",       color: "var(--lc-text-secondary)", style: "dotted" },
  web_found:        { label: "Concierge® to verify",       color: "var(--lc-text-secondary)", style: "dotted" },
  mira_imagined:    { label: "Concierge® to verify",       color: "var(--lc-text-secondary)", style: "dotted" },
  unknown:          { label: "Concierge® to verify",       color: "var(--lc-text-secondary)", style: "dotted" },
};

function ArchiveStatusBadge({ status }) {
  const cfg = ARCHIVE_BADGES[status] || ARCHIVE_BADGES.unknown;
  const isItalic = cfg.style === "italic";
  const borderStyle = cfg.style === "dotted" ? "dotted" : "solid";
  return (
    <span
      data-testid={`archive-badge-${status || "unknown"}`}
      className="lc-text-body"
      style={{
        display: "inline-block",
        fontSize: "0.6rem",
        letterSpacing: "0.04em",
        color: cfg.color,
        fontStyle: isItalic ? "italic" : "normal",
        fontWeight: isItalic ? 400 : 500,
        borderBottom: isItalic ? "none" : `1px ${borderStyle} ${cfg.color}`,
        paddingBottom: 1,
      }}
    >
      {cfg.label}
    </span>
  );
}

const STARTER_CHIPS = [
  { id: "c1", text: "Mum's cardiology test in Bangalore",     pillar: "wellness" },
  { id: "c2", text: "Anniversary dinner — quiet, vegetarian", pillar: "moments_of_life" },
  // v3.29 — Per the mobile spec §Screen-1: 1-2 chips only on first
  // viewport so the input stays reachable. The full set still loads
  // on ≥lg desktop (where there's vertical room).
];

const STARTER_CHIPS_DESKTOP = [
  { id: "c1", text: "Mum's cardiology test in Bangalore",     pillar: "wellness" },
  { id: "c2", text: "Renew my Schengen visa",                 pillar: "documentation" },
  { id: "c3", text: "Anniversary dinner — quiet, vegetarian", pillar: "moments_of_life" },
  { id: "c4", text: "Aftercare for my child this term",       pillar: "family_care" },
];

// v3.6 — Render Mira's reply with **bold** segments as real <strong>.
// Splits on `**…**` runs and keeps everything else as plain text.
// v3.6 — Inline-markdown renderer for Mira's chat bubble.
// Handles BOTH **bold** and *italic* markdown verbatim — they're the only
// two inline marks the brain emits (per R24 in the system prompt). NEVER
// wraps the whole bubble in italic — italics now arrive ONLY from the
// model's explicit *...* markers (e.g. the 2-8 word mood opener).
//
// v3.19 — was renderWithBold. Renamed + extended to fix the live
// "*Travel*" rendering bug (single-asterisk italics were passing
// through as literal `*Word*` text inside a CSS-italicised bubble).
function renderInlineMarkdown(text) {
  if (text === null || text === undefined) return null;
  const str = String(text);
  if (!str) return null;
  // Split on bold OR italic markers. Bold takes priority by matching
  // `**…**` before `*…*` thanks to the explicit double-asterisk pattern.
  // The non-greedy capture and `[^*\n]` class prevent us swallowing
  // multi-line spans or eating a stray asterisk inside a word.
  const parts = str.split(/(\*\*[^*\n]+?\*\*|\*[^*\n]+?\*)/g);
  return parts.map((p, i) => {
    if (!p) return null;
    if (p.startsWith("**") && p.endsWith("**") && p.length > 4) {
      return (
        <strong
          key={i}
          style={{ fontWeight: 600, color: "var(--lc-text-primary)" }}
          data-testid="mira-bold"
        >
          {p.slice(2, -2)}
        </strong>
      );
    }
    if (p.startsWith("*") && p.endsWith("*") && p.length > 2 && !p.startsWith("**")) {
      return (
        <em
          key={i}
          style={{ fontStyle: "italic", color: "inherit" }}
          data-testid="mira-italic"
        >
          {p.slice(1, -1)}
        </em>
      );
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

// Back-compat alias — keep older callers working without a sweep.
const renderWithBold = renderInlineMarkdown;

// v3.9.2 — Chips are plain pill text; strip any **markdown** from chip strings
// so they don't render as raw asterisks.
function stripChipMarkdown(text) {
  if (!text) return "";
  return String(text).replace(/\*\*([^*\n]+?)\*\*/g, "$1").replace(/\*([^*\n]+?)\*/g, "$1");
}

// v3.6 — Single item in the chat-control popover menu
function MenuItem({ onClick, label, icon, testid }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        width: "100%",
        padding: "11px 14px",
        background: "transparent",
        border: "none",
        cursor: "pointer",
        fontSize: 13,
        color: "var(--lc-text-secondary)",
        textAlign: "left",
        transition: "background 120ms ease",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(201, 169, 106, 0.06)")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
      data-testid={testid}
    >
      <span style={{ color: "var(--lc-text-muted)", fontSize: 14 }}>{icon}</span>
      <span>{label}</span>
    </button>
  );
}

// v3.6 — Recent chats list modal
function RecentChatsModal({ sessions, onClose, onResume }) {
  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 60,
        background: "rgba(11, 16, 32, 0.45)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
      onClick={onClose}
      data-testid="recent-chats-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--lc-cream)",
          borderRadius: 8,
          maxWidth: 520,
          width: "100%",
          maxHeight: "80vh",
          overflow: "auto",
          padding: "28px 24px 20px",
          border: "1px solid var(--lc-border-soft)",
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="lc-eyebrow">Continuity</div>
            <h3 className="lc-text-display" style={{ fontSize: 22, marginTop: 4 }}>
              Recent conversations
            </h3>
          </div>
          <button
            onClick={onClose}
            style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: 18, color: "var(--lc-text-muted)" }}
            data-testid="recent-chats-close"
          >
            ✕
          </button>
        </div>
        {sessions.length === 0 ? (
          <p className="lc-text-body lc-text-muted" data-testid="recent-chats-empty">
            No earlier conversations yet — your first request will appear here next time.
          </p>
        ) : (
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {sessions.map((s) => (
              <li key={s.session_id} style={{ borderBottom: "1px solid var(--lc-border-soft)" }}>
                <button
                  onClick={() => onResume(s)}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "14px 12px",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    transition: "background 140ms ease",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(201, 169, 106, 0.05)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  data-testid={`recent-session-${s.session_id}`}
                >
                  <p style={{ fontSize: 14, color: "var(--lc-text-primary)", fontWeight: 500 }}>
                    {s.subject || "Untitled"}
                  </p>
                  <p style={{ fontSize: 11, color: "var(--lc-text-muted)", letterSpacing: "0.04em", marginTop: 4 }}>
                    {s.status === "ended" ? "ended" : "active"} ·{" "}
                    {s.created_at ? new Date(s.created_at).toLocaleString() : ""}
                    {typeof s.message_count === "number" ? ` · ${s.message_count} message${s.message_count === 1 ? "" : "s"}` : ""}
                    <span style={{ float: "right", color: "var(--lc-accent)", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600 }}>
                      Resume →
                    </span>
                  </p>
                </button>
              </li>
            ))}
          </ul>
        )}
        <p style={{ fontSize: 11, color: "var(--lc-text-muted)", marginTop: 16, fontStyle: "italic" }}>
          Mira keeps the texture of past conversations in mind without re-stating them.
        </p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MIRA's BRAIN — wired to /api/mira/understand (Claude 4.5 Sonnet via Emergent LLM Key)
// Local fallback removed in v2.6 — backend handles graceful degradation.
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// MIRA CHAT (Left Pane)
// ─────────────────────────────────────────────────────────────────────────────

function MiraChat({ onComposingChange, onResponseMeta, externalMessage, onHandoffSend, submitState }) {
  const { theme } = useTheme();
  const [messages, setMessages] = useState([
    { role: "mira", text: theme.mira_intro },
  ]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  // v3.18 — voice input. Web Speech API drives a native multilingual STT
  // surface. The mic transcript fills the input box; the member can edit
  // before sending. Mira sees plain text — she doesn't know it was spoken.
  // Unsupported browsers (Firefox / older Safari) hide the mic entirely.
  const voice = useSpeechToText({
    onResult: (txt) => {
      setInput((prev) => {
        const head = prev.trim();
        return head ? `${head} ${txt}` : txt;
      });
    },
  });
  // v3.2 — once-per-session flags + stable session_id for the backend
  const [sessionId, setSessionId] = useState(() =>
    `mira-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
  );
  const [governingUsed, setGoverningUsed] = useState(false);
  const [inspirationsUsed, setInspirationsUsed] = useState(false);
  // v3.6 — visitor memory + chat controls
  const [visitorId] = useState(() => {
    let v = localStorage.getItem("lc_visitor_id");
    if (!v) {
      v = `v-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem("lc_visitor_id", v);
    }
    return v;
  });
  const [visitor, setVisitor] = useState(null); // {is_returning, display_name, recent_sessions_count}
  const [menuOpen, setMenuOpen] = useState(false);
  const [recentOpen, setRecentOpen] = useState(false);
  const [recentSessions, setRecentSessions] = useState([]);
  const transcriptRef = useRef(null);

  // v3.6 — Identify the visitor on first mount. Sets returning flag + display name.
  // v3.13 — Reads the signed session_token returned by the server and stores it.
  // Every subsequent /api/mira/* call presents it as Authorization: Bearer to
  // keep the tenant seal locked end-to-end. tenant_id is NEVER sent in bodies
  // anymore — the server resolves tenant from the token / URL context.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const display = localStorage.getItem("lc_visitor_name") || null;
        const res = await fetch(`${BACKEND_URL}/api/mira/visitor/identify`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            visitor_id: visitorId,
            display_name: display,
          }),
        });
        if (!res.ok) return;
        const data = await res.json();
        if (data?.session_token) {
          localStorage.setItem("lc_session_token", data.session_token);
          localStorage.setItem("lc_resolved_tenant", data.tenant_id || "lesconcierge");
        }
        if (!cancelled) setVisitor(data);
      } catch (e) {
        /* non-fatal */
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // v3.13 — helper for adding Authorization header to every Mira API call
  // (kept inside MiraChat for the linter; the module-level _authHeadersGlobal
  // mirror is what MiraPage / basket-send helpers use)
  function _authHeaders() {
    return _authHeadersGlobal();
  }

  // Open the session record on the backend (idempotent) on first user message
  const sessionStartedRef = useRef(false);
  // v3.32 — Mirror of `messages` for synchronous read inside the send()
  // closure. The brief panel and the basket-send payload both need the
  // latest transcript without waiting for React batching.
  const messagesRef = useRef([]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);
  async function ensureSessionStarted(firstSubject) {
    if (sessionStartedRef.current) return;
    sessionStartedRef.current = true;
    try {
      await fetch(`${BACKEND_URL}/api/mira/visitor/session-start`, {
        method: "POST",
        headers: _authHeaders(),
        body: JSON.stringify({
          visitor_id: visitorId,
          session_id: sessionId,
          subject: firstSubject?.slice(0, 80) || "New conversation",
        }),
      });
    } catch {/* non-fatal */}
  }

  // v3.6 — Chat-control handlers
  function startNewChat() {
    // Persist the existing session as ended (best-effort), then reset state.
    if (sessionStartedRef.current) {
      fetch(`${BACKEND_URL}/api/mira/visitor/session-end`, {
        method: "POST",
        headers: _authHeaders(),
        body: JSON.stringify({ visitor_id: visitorId, session_id: sessionId }),
      }).catch(() => {});
    }
    setMessages([{ role: "mira", text: theme.mira_intro }]);
    setGoverningUsed(false);
    setInspirationsUsed(false);
    setSessionId(`mira-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
    sessionStartedRef.current = false;
    setMenuOpen(false);
    // v3.34 — S1: clear BOTH the conversation AND the brief panel. Previously
    // a "Start new chat" only reset activeContext but left the basket items
    // showing on the right — confusing and dangerous (carryover items could
    // get sent to the concierge on the next handoff). Now we also signal
    // MiraPageInner to drop the basket via reset_basket:true.
    if (onResponseMeta) onResponseMeta({ phase: "gathering", reset: true, reset_basket: true });
  }

  function endChat() {
    if (sessionStartedRef.current) {
      fetch(`${BACKEND_URL}/api/mira/visitor/session-end`, {
        method: "POST",
        headers: _authHeaders(),
        body: JSON.stringify({ visitor_id: visitorId, session_id: sessionId }),
      }).catch(() => {});
    }
    setMessages((m) => [
      ...m,
      {
        role: "mira",
        text:
          "Thank you — it has been a pleasure assisting you. This conversation will now refresh, and our live Concierge® team will continue handling everything for you via your chosen contact method. When you return, you'll be able to start fresh with complete clarity for your next request.",
      },
    ]);
    setMenuOpen(false);
  }

  async function openRecent() {
    setMenuOpen(false);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/mira/visitor/${encodeURIComponent(visitorId)}/sessions`
      );
      const data = await res.json();
      setRecentSessions(data.sessions || []);
      setRecentOpen(true);
    } catch {
      setRecentSessions([]);
      setRecentOpen(true);
    }
  }

  useEffect(() => {
    if (transcriptRef.current) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
    }
  }, [messages, thinking]);

  // v3.19 — re-entry guard. send() is reachable from THREE paths: keyboard
  // Enter, Send-button click, and the externalMessage useEffect below
  // (venue-card "Arrange this", refine chips, etc.). React 18 StrictMode in
  // dev re-fires the effect; a fast double-tap can also race the click and
  // the keydown. This ref + dedupe key ensures send() is idempotent.
  const sendingRef = useRef(false);
  const lastExternalRef = useRef(null);

  // Allow parent (e.g. venue card "Arrange this", right-pane Send
  // brief button) to inject a message OR fire the itinerary handoff
  // OR push a Concierge® send acknowledgement into the chat.
  useEffect(() => {
    if (!externalMessage) return;
    if (lastExternalRef.current === externalMessage) return;
    lastExternalRef.current = externalMessage;
    if (externalMessage.action === "handoff") {
      handleHandoffYes();
      return;
    }
    // v3.46.18 — Send-as-Ceremony · in-chat acknowledgement.
    // When the green "Send brief to Concierge® team" button is pressed,
    // the parent fires this action so Mira posts an explicit, voice-
    // matched confirmation directly in the chat — no member ever has to
    // wonder whether their brief reached the team. Toast handles the
    // bottom-of-screen cue; this fills the chat thread with the same
    // truth so the conversation itself ends on "sent · reference · we'll
    // revert" — the unmistakable handoff moment.
    if (externalMessage.action === "send_confirmation") {
      const ref = externalMessage.ticketId || "—";
      const summary = (externalMessage.briefSummary || "").trim();
      const summaryClause = summary ? ` — ${summary}.` : ".";
      setMessages((m) => [
        ...m,
        {
          role: "mira",
          text: `Your brief is with our live Concierge® team${summaryClause} Reference \`${ref}\`. They will revert with confirmation shortly.`,
          handoff: true,
          tripState: "handed_off",
        },
      ]);
      return;
    }
    if (externalMessage.text) {
      send(externalMessage.text);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalMessage]);

  async function send(text) {
    const userText = (text || input).trim();
    if (!userText) return;
    // v3.19 — re-entry guard. Prevents the same userText from appending
    // twice when a fast click + keydown race, or StrictMode re-fires.
    if (sendingRef.current) return;
    sendingRef.current = true;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: userText }]);

    // v3.44 — Universal "Add to brief" intent.
    // Lets the member type "Add Bombay Gymkhana to my brief" and have
    // the item resolved + appended to the basket directly, zero LLM
    // tokens consumed on match. Runs BEFORE the gate-check so a member
    // who's already mid-club-flow can add a specific club by name.
    // When the city is ambiguous, the backend returns matched=true,
    // added=false, and Mira asks the member for the city herself.
    try {
      const aiRes = await fetch(`${BACKEND_URL}/api/lc/brief/add-item`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: userText }),
      });
      if (aiRes.ok) {
        const ai = await aiRes.json();
        if (ai.matched) {
          if (ai.added && ai.item) {
            // The chat-pane component (send lives here) is sibling to
            // the brief-panel component (basket state lives there). We
            // bridge via a CustomEvent on window — the basket-owning
            // component listens and appends. No prop-drilling needed.
            if (typeof window !== "undefined") {
              window.dispatchEvent(new CustomEvent("lc-basket-add", { detail: ai.item }));
            }
          }
          setMessages((m) => [...m, {
            role: "mira",
            text: ai.mira_ack,
            streaming: false,
            followUps: [],
          }]);
          setThinking(false);
          sendingRef.current = false;
          return;
        }
      }
    } catch (_) {
      // Add-intent failure is non-blocking — fall through to gate / brain.
    }

    // v3.43 / Phase C — Programme-benefit gate-check.
    // BEFORE we call Mira's brain, ask the backend whether this message
    // triggers any benefit gate (Club Social membership being the only
    // one in v1). If it does and we don't yet have an answer in
    // localStorage, render a chip block inline and STOP — the chips drive
    // the next step. If the answer is already 'yes', proceed normally.
    try {
      const stored = {};
      try {
        const v = (typeof window !== "undefined") ? localStorage.getItem("lc_clubsocial_member") : null;
        if (v) stored.club_access = v;
      } catch (_) { /* private mode */ }
      const gcRes = await fetch(`${BACKEND_URL}/api/lc/benefits/gate-check`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: userText, gate_answers: stored }),
      });
      if (gcRes.ok) {
        const gc = await gcRes.json();
        if (gc.gate_required && gc.gate) {
          // Append a Mira bubble that carries the gate prompt + chips.
          // BenefitGate component (rendered inline below) reads `gatePrompt`.
          setMessages((m) => [...m, {
            role: "mira",
            text: gc.gate.question,
            gatePrompt: gc.gate,
            originalUserText: userText,
            streaming: false,
            followUps: [],
          }]);
          setThinking(false);
          sendingRef.current = false;
          return;
        }
      }
    } catch (_) {
      // Gate-check failure is non-blocking — proceed to brain as usual.
    }

    setThinking(true);
    // v3.6 — register the session on the backend the first time the user speaks
    ensureSessionStarted(userText);

    // Build conversation history from current messages
    // v3.20 — attach the LAST itinerary_plan + last venue_hints so the
    // brain can refine an existing plan ("make day 2 quieter", "swap the
    // cruise to day 3"). Frontend keeps the past plan on the message;
    // backend reads it from history[].
    // v3.46.19 — Phase 1b multi-ask anchor (founder catch · Tokyo E2E).
    // The original slice(-8) was dropping the FIRST user turn on long
    // sessions, which is where multi-ask intent typically anchors
    // ("car waiting AND a table"). After 4+ refinement turns the brain
    // would only see the last 4 user turns — none of which mentioned
    // "car" — and would forget the Move pillar entirely.
    //
    // New rule: always include the FIRST user message + the last 8
    // messages. Same "anchor + recent" pattern as the brain-side fix
    // in mira_brain.py::_detect_multi_ask. Cross-session bleed is
    // impossible because each session resets messages to a fresh list.
    const filtered = messages.filter(
      (m) => m.role === "user" || m.role === "mira"
    );
    const recent = filtered.slice(-8);
    const firstUser = filtered.find((m) => m.role === "user");
    const anchorAndRecent = (
      firstUser && !recent.includes(firstUser)
        ? [firstUser, ...recent]
        : recent
    );
    const history = anchorAndRecent.map((m) => {
        const base = { role: m.role === "mira" ? "mira" : "user", text: m.text };
        if (m.role === "mira" && m.itineraryPlan) base.itinerary_plan = m.itineraryPlan;
        // v3.21 — ship trip_state + contextual_upsell so the brain can
        // enforce the R26 step progression on the next turn.
        if (m.role === "mira" && m.tripState) base.trip_state = m.tripState;
        if (m.role === "mira" && m.contextualUpsell) base.contextual_upsell = m.contextualUpsell;
        // v3.46.20 — Phase 1b structural accumulation (founder catch 27 May).
        // Echo the prior Mira turn's brief_items back through history so
        // the brain's _previous_brief_items() can carry satisfied line-
        // items forward. Without this, the Move (car) ask completed at
        // turn 3 would drop out by turn 5 when the brain focuses on Dine.
        if (m.role === "mira" && Array.isArray(m.briefItems) && m.briefItems.length) {
          base.brief_items = m.briefItems;
        }
        return base;
      });

    try {
      const start = Date.now();
      // v3.46.3 — Tell the parent we're composing so the right pane can
      // show the brass "Mira is shaping your picks…" pulse instead of the
      // idle empty-state placeholder during the JSON-tail generation gap.
      if (onComposingChange) onComposingChange(true);
      // v3.24 — streaming: append a placeholder Mira bubble immediately
      // so honest status messages and streamed tokens have a home. The
      // final scrubbed payload replaces the streamed text in-place on
      // `complete`. Total wall-clock is the same; the FEEL is alive.
      let placeholderIdx = -1;
      setMessages((m) => {
        placeholderIdx = m.length;
        return [...m, {
          role: "mira",
          text: "",
          streaming: true,
          // F16-5 (3 Jun 2026 PM late · binding): gathering must feel
          // immediate. Do NOT seed the placeholder with a status chip.
          // The chip will appear ONLY if the backend sends an explicit
          // delayed status event (≥ 600ms slow path). Fast gathering
          // turns return tokens before any chip would render.
          curatingStatus: null,
          followUps: [],
          handoff: false,
          itineraryPlan: null,
          contextualUpsell: null,
          tripState: null,
          venueHints: [],
        }];
      });
      // The placeholder bubble may render the chip later if backend sends
      // a delayed status SSE — hide the global "•••" indicator now to
      // avoid double-spinner.
      setThinking(false);

      const res = await fetch(`${BACKEND_URL}/api/mira/understand/stream`, {
        method: "POST",
        headers: { ..._authHeaders(), "Accept": "text/event-stream" },
        body: JSON.stringify({
          message: userText,
          conversation_history: history,
          session_id: sessionId,
          visitor_id: visitorId,
          governing_sentence_used: governingUsed,
          initial_inspirations_used: inspirationsUsed,
        }),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buf = "";
      let brain = null;
      let streamedText = "";
      // v3.26 — Throttle token-event commits via requestAnimationFrame.
      // Without this, each token triggers an independent React commit
      // (~200 commits per turn) which causes the right pane to flicker
      // during streaming. Coalescing to ~60Hz keeps the typewriter
      // smooth and stops the judder.
      let pendingFlush = false;
      const flushStream = () => {
        pendingFlush = false;
        const snap = streamedText;
        setMessages((m) => {
          const u = [...m];
          if (u[placeholderIdx]) {
            u[placeholderIdx] = {
              ...u[placeholderIdx],
              text: snap,
              curatingStatus: null,
            };
          }
          return u;
        });
      };
      const scheduleFlush = () => {
        if (pendingFlush) return;
        pendingFlush = true;
        if (typeof requestAnimationFrame !== "undefined") {
          requestAnimationFrame(flushStream);
        } else {
          setTimeout(flushStream, 16);
        }
      };

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let sepIdx;
        while ((sepIdx = buf.indexOf("\n\n")) !== -1) {
          const frame = buf.slice(0, sepIdx);
          buf = buf.slice(sepIdx + 2);
          let event = "message";
          let data = "";
          for (const line of frame.split("\n")) {
            if (line.startsWith("event: ")) event = line.slice(7).trim();
            else if (line.startsWith("data: ")) {
              if (data) data += "\n";
              data += line.slice(6);
            }
          }
          if (event === "status") {
            const status = data;
            // eslint-disable-next-line no-loop-func
            setMessages((m) => {
              const u = [...m];
              if (u[placeholderIdx]) {
                u[placeholderIdx] = { ...u[placeholderIdx], curatingStatus: status };
              }
              return u;
            });
          } else if (event === "token") {
            streamedText += data;
            scheduleFlush();
          } else if (event === "partial") {
            // v3.46.3 — Phase 1.2.1 partial reveal. Cards arrive
            // BEFORE places enrichment so the right-pane brief panel
            // populates immediately when prose finishes. The final
            // `complete` event below upgrades them with ratings /
            // images / verified place_id. Closes the gap that
            // appeared once Phase 1.2 streamed prose early.
            try {
              const partial = JSON.parse(data);
              const pmeta = partial._meta || {};
              if (onResponseMeta) {
                onResponseMeta({
                  phase: pmeta.phase || "curating",
                  pattern: pmeta.pattern || null,
                  pillar: partial.pillar,
                  pillar_name: pmeta.pillar_name || null,
                  subcategory: pmeta.subcategory || null,
                  contains_imagined: pmeta.contains_imagined || false,
                  gathered_details: pmeta.gathered_details || [],
                  needs_details: pmeta.needs_details || [],
                  venue_hints: partial.venue_hints || [],
                  product_hints: partial.product_hints || [],
                  brief_items: partial.brief_items || [],
                  procedural_steps: partial.procedural_steps || [],
                  adjacent_capabilities: partial.adjacent_capabilities || [],
                  sourcing_hint: partial.sourcing_hint || null,
                  mentioned_places: partial.mentioned_places || [],
                  primary_city: pmeta.primary_city || partial.primary_city || null,
                  handoff_moment: partial.handoff_moment || false,
                  // v3.46.22 · founder watching-day batch P0 — thread
                  // brain `intent` through so the right pane honours
                  // declines (see CityCapabilitiesPanel suppression).
                  intent: partial.intent || null,
                  itinerary_plan: partial.itinerary_plan || null,
                  trip_state: pmeta.trip_state || null,
                  contextual_upsell: partial.contextual_upsell || null,
                  // Provisional transcript — the final `complete`
                  // handler below overwrites with the post-scrub
                  // version. Kept consistent so basket sends never
                  // race on a missing snapshot.
                  transcript_snapshot: ((messagesRef.current || []).concat([
                    { role: "mira", text: streamedText },
                  ])).map((m) => ({ role: m.role, text: m.text || "" })),
                });
              }
              // Also stamp the venueHints / productHints / followUps onto
              // the assistant placeholder so any chat-side card surfaces
              // (e.g. ItineraryPackageCard) render immediately too.
              setMessages((m) => {
                const u = [...m];
                if (u[placeholderIdx]) {
                  u[placeholderIdx] = {
                    ...u[placeholderIdx],
                    venueHints: partial.venue_hints || [],
                    productHints: partial.product_hints || [],
                    followUps: partial.follow_ups || u[placeholderIdx].followUps || [],
                    itineraryPlan: partial.itinerary_plan || u[placeholderIdx].itineraryPlan || null,
                    contextualUpsell: partial.contextual_upsell || u[placeholderIdx].contextualUpsell || null,
                  };
                }
                return u;
              });
              // v3.46.3 — cards have landed; clear the right-pane pulse.
              if (onComposingChange) onComposingChange(false);
            } catch (e) {
              console.error("[mira] partial parse failed", e);
            }
          } else if (event === "complete") {
            try { brain = JSON.parse(data); }
            catch (e) { console.error("[mira] complete parse failed", e); }
            // v3.46.3 — fallback clear for turns with no venues (gather,
            // pure prose). Partial never fires in that case; complete is
            // the only signal that the turn is done.
            if (onComposingChange) onComposingChange(false);
          } else if (event === "error") {
            console.error("[mira] stream error:", data);
            if (onComposingChange) onComposingChange(false);
            throw new Error(data);
          }
        }
      }
      if (!brain) throw new Error("stream ended without a complete event");

      // Minimum 600ms thinking pause only meaningful when the stream
      // resolved instantly (legal envelope short-circuit, etc.).
      const elapsed = Date.now() - start;
      if (elapsed < 600) {
        await new Promise((r) => setTimeout(r, 600 - elapsed));
      }

      // Surface latency profile for the dev console (per user request
      // to see breakdown numbers per turn — v3.24).
      try {
        const prof = brain && brain._meta && brain._meta._profile;
        if (prof) {
          // eslint-disable-next-line no-console
          console.log(
            "[lat] total=%dms · brain=%d · places=%d · archive=%d · session=%d",
            Math.round(prof.total_ms || 0),
            Math.round(prof.brain_ms || 0),
            Math.round(prof.places_ms || 0),
            Math.round(prof.archive_ms || 0),
            Math.round(prof.session_save_ms || 0),
          );
        }
      } catch { /* non-fatal */ }

      setMessages((m) => {
        const next = [...m];
        if (next[placeholderIdx]) {
          // Replace the streamed text with the FINAL scrubbed reply_text.
          next[placeholderIdx] = {
            role: "mira",
            text: brain.reply_text,
            streaming: false,
            curatingStatus: null,
            followUps: brain.follow_ups || [],
            handoff: !!brain.handoff_moment,
            itineraryPlan: brain.itinerary_plan || null,
            contextualUpsell: brain.contextual_upsell || null,
            tripState: (brain._meta && brain._meta.trip_state) || null,
            venueHints: brain.venue_hints || [],
            // v3.46.20 — Phase 1b structural accumulation. Persist this
            // turn's brief_items on the Mira message so the next turn's
            // history payload carries them, and the backend's
            // _previous_brief_items() can carry satisfied line-items
            // forward. Without this, satisfied items drop out of state.
            briefItems: brain.brief_items || [],
          };
        }
        // v3.9 — persist transcript so this conversation can be resumed
        // from the "Recent chats" modal. Fire-and-forget; non-fatal.
        if (sessionStartedRef.current) {
          fetch(`${BACKEND_URL}/api/mira/visitor/session-save`, {
            method: "POST",
            headers: _authHeaders(),
            body: JSON.stringify({
              visitor_id: visitorId,
              session_id: sessionId,
              messages: next.map((mm) => ({
                role: mm.role === "user" ? "user" : "mira",
                text: mm.text,
              })),
              subject: next.find((mm) => mm.role === "user")?.text?.slice(0, 80) || null,
            }),
          }).catch(() => {});
        }
        return next;
      });
      setThinking(false);

      // v3.6 — On handoff, scroll the right pane into view on mobile so the
      // member can see the assembled brief without having to hunt for it.
      if (brain.handoff_moment) {
        setTimeout(() => {
          try {
            const pane = document.querySelector('[data-testid="mira-right-pane"]');
            if (pane && window.matchMedia("(max-width: 1023px)").matches) {
              pane.scrollIntoView({ behavior: "smooth", block: "start" });
            }
          } catch { /* non-fatal */ }
        }, 300);
      }

      // v3.2 — flip the once-per-session flags after their first appearance
      if (!governingUsed) setGoverningUsed(true);
      const newMeta = brain._meta || brain.meta || {};
      if (!inspirationsUsed && newMeta.phase === "curating") {
        setInspirationsUsed(true);
      }

      if (onResponseMeta) {
        const meta = newMeta;
        onResponseMeta({
          phase: meta.phase || "gathering",
          pattern: meta.pattern || null,
          pillar: brain.pillar,
          pillar_name: meta.pillar_name || null,
          subcategory: meta.subcategory || null,
          contains_imagined: meta.contains_imagined || false,
          gathered_details: meta.gathered_details || [],
          needs_details: meta.needs_details || [],
          venue_hints: brain.venue_hints || [],
          product_hints: brain.product_hints || [],
          brief_items: brain.brief_items || [],
          procedural_steps: brain.procedural_steps || [],
          adjacent_capabilities: brain.adjacent_capabilities || [],
          sourcing_hint: brain.sourcing_hint || null,
          mentioned_places: brain.mentioned_places || [],
          primary_city: meta.primary_city || brain.primary_city || null,
          handoff_moment: brain.handoff_moment || false,
          // v3.46.22 · founder watching-day batch P0:
          // Thread the brain's `intent` field through so the right pane
          // can honour decline/out-of-scope turns (don't show generic
          // city-services fallback content when Mira just declined).
          intent: brain.intent || null,
          itinerary_plan: brain.itinerary_plan || null,
          trip_state: (brain._meta && brain._meta.trip_state) || null,
          contextual_upsell: brain.contextual_upsell || null,
          // v3.32 — Latest transcript snapshot (post-this-turn) so the
          // basket-send handler can attach the full chat to the brief.
          // The concierge desk opens the ticket and has everything in one
          // place: items + transcript + summary.
          transcript_snapshot: ((messagesRef.current || []).concat([
            { role: "mira", text: brain.reply_text },
          ])).map((m) => ({ role: m.role, text: m.text || "" })),
        });
      }
    } catch (err) {
      // Loud-failure-logging, quiet-success-fallback
      // eslint-disable-next-line no-console
      console.error("[mira] /api/mira/understand failed:", err);
      setMessages((m) => [
        ...m,
        {
          role: "mira",
          text:
            "Let me have our team verify and come back — there's a momentary pause on my end. Tap the phone number below to reach a live Concierge® now.",
          followUps: ["try again", "WhatsApp me directly"],
        },
      ]);
      setThinking(false);
      // v3.46.3 — ensure the right-pane pulse stops on failure too.
      if (onComposingChange) onComposingChange(false);
    } finally {
      // v3.19 — always release the re-entry guard so the next send can fire
      sendingRef.current = false;
    }
  }

  // Phase 5 — Second-YES (awaiting_handoff_yes → handed_off).
  // Instead of routing through /api/mira/understand, fire the dedicated
  // /api/lc/itinerary-handoff endpoint which:
  //   1. Composes a TripDataset from the full conversation
  //   2. Renders The Edit (client-facing), Ops Docket (concierge-only),
  //      and WhatsApp Pack (single copy-paste block)
  //   3. Persists all three inline + writes a concierge ticket
  //   4. Returns the ref + URLs the member can open
  // The bubble that follows shows the package card (View The Edit · Copy
  // WhatsApp Pack), NOT the legacy Send-to-Concierge basket flow.
  async function handleHandoffYes() {
    if (sendingRef.current) return;
    sendingRef.current = true;
    const userText = "Yes, please proceed.";
    setMessages((m) => [...m, { role: "user", text: userText }]);
    setThinking(true);

    // Build the FULL history (not capped at 8 like /understand) so the
    // composer has every confirmation, accepted upsell, and itinerary_plan
    // to draw from.
    const fullHistory = messages
      .filter((m) => m.role === "user" || m.role === "mira")
      .map((m) => {
        const base = { role: m.role === "mira" ? "mira" : "user", text: m.text };
        if (m.role === "mira" && m.itineraryPlan) base.itinerary_plan = m.itineraryPlan;
        if (m.role === "mira" && m.tripState) base.trip_state = m.tripState;
        if (m.role === "mira" && m.contextualUpsell) base.contextual_upsell = m.contextualUpsell;
        // v3.23 — ship verified venue_hints so the composer can pull
        // live Google Places ratings + review counts onto matching
        // itinerary slots. Never trust LLM-asserted "(4.9★)" strings.
        if (m.role === "mira" && m.venueHints && m.venueHints.length) {
          base.venue_hints = m.venueHints;
        }
        return base;
      });
    fullHistory.push({ role: "user", text: userText });

    const memberName = localStorage.getItem("lc_visitor_name") || null;
    const memberEmail = localStorage.getItem("lc_visitor_email") || null;
    const memberPhone = localStorage.getItem("lc_visitor_phone") || null;

    try {
      const res = await fetch(`${BACKEND_URL}/api/lc/itinerary-handoff`, {
        method: "POST",
        headers: _authHeaders(),
        body: JSON.stringify({
          visitor_id: visitorId,
          session_id: sessionId,
          member: (memberName || memberEmail || memberPhone)
            ? { name: memberName, email: memberEmail, phone: memberPhone }
            : null,
          messages: fullHistory,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      setMessages((m) => [
        ...m,
        {
          role: "mira",
          text: `Your brief is with our team. Reference \`${data.ticket_id}\`. A Concierge® will be in touch shortly — meanwhile, your trip pack is ready to view and share.`,
          itineraryPackage: data,
          tripState: "handed_off",
        },
      ]);

      // Surface the handoff to the page layer so the right-pane can swap
      // to a confirmation state if needed.
      if (onResponseMeta) {
        onResponseMeta({
          phase: "handed_off",
          handoff_moment: true,
          trip_state: "handed_off",
          itinerary_package: data,
        });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[mira] /api/lc/itinerary-handoff failed:", err);
      setMessages((m) => [
        ...m,
        {
          role: "mira",
          text: "Sorry — couldn't reach the team. Tap the phone number below to reach a live Concierge® now.",
        },
      ]);
    } finally {
      setThinking(false);
      sendingRef.current = false;
    }
  }

  // v3.31 — Empty-state flag: nothing said yet, just Mira's static intro.
  // Used to tighten vertical rhythm — drop the minHeight so the chat
  // pane sizes to (intro + input + chips) rather than reserving a
  // 70vh editorial canvas before any conversation has begun.
  const emptyConversation = messages.length === 1;

  // v3.35 — Mobile shake fix (C1). The previous logic flipped `minHeight`
  // from "auto" (empty state) to "min(560px, 70vh)" the moment the first
  // user message landed — a hard layout jump that read as "shake" on
  // mobile. Solution: on phone widths (≤640px) we never set a minHeight
  // floor; the content (intro + bubbles + input) sizes the card naturally.
  // On desktop we keep the editorial-canvas floor but lower it so the
  // transition isn't a visible lurch.
  const isPhone = typeof window !== "undefined" && window.matchMedia
    ? window.matchMedia("(max-width: 640px)").matches
    : false;
  let chatMinHeight = "auto";
  if (!isPhone && !emptyConversation) {
    // Desktop, with content — keep a soft editorial floor (lower than
    // the v3.27 560px so the post-first-message lurch is gentler).
    chatMinHeight = "min(440px, 56vh)";
  }

  return (
    <div
      className="lc-card-elevated flex flex-col"
      style={{
        // v3.35 — see note above; mobile = always auto, desktop = soft
        // editorial floor only after the first turn.
        minHeight: chatMinHeight,
        maxHeight: "78vh",
      }}
      data-testid="mira-chat-pane"
    >
      {/* Welcome back ribbon — v3.6 */}
      {visitor && visitor.is_returning && visitor.display_name && (
        <div
          className="px-5 py-2"
          style={{
            borderBottom: "1px solid var(--lc-border-soft)",
            background: "rgba(201, 169, 106, 0.06)",
            fontSize: "0.78rem",
            color: "var(--lc-text-muted)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
          data-testid="mira-welcome-back"
        >
          <span>
            Welcome back, <em style={{ color: "var(--lc-text-secondary)" }}>{visitor.display_name}</em>. Mira remembers you.
          </span>
          <button
            onClick={() => {
              localStorage.removeItem("lc_visitor_id");
              localStorage.removeItem("lc_visitor_name");
              window.location.reload();
            }}
            style={{
              background: "transparent",
              border: "none",
              color: "var(--lc-text-muted)",
              fontSize: "0.7rem",
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              cursor: "pointer",
            }}
            data-testid="mira-not-you"
          >
            Not you? Start fresh
          </button>
        </div>
      )}

      {/* Header */}
      <div
        className="flex items-center gap-3 px-5 py-4"
        style={{
          borderBottom: "1px solid var(--lc-border-soft)",
          background:
            "linear-gradient(135deg, rgba(27,45,82,0.04) 0%, rgba(184,154,78,0.04) 100%)",
          borderTopLeftRadius: "var(--lc-radius-md)",
          borderTopRightRadius: "var(--lc-radius-md)",
        }}
      >
        <div
          className="w-10 h-10 flex items-center justify-center"
          style={{
            background:
              "linear-gradient(135deg, var(--lc-accent) 0%, var(--lc-highlight) 100%)",
            color: "var(--lc-cream)",
            borderRadius: "50%",
            fontFamily: "var(--lc-font-display)",
            fontSize: "1.125rem",
            fontWeight: 500,
          }}
        >
          M
        </div>
        <div className="flex-1">
          <div
            className="lc-text-display"
            style={{ fontSize: "1.0625rem", color: "var(--lc-text-primary)" }}
          >
            Mira
            {/* v3.29 — Mobile-only "● online" dot beside Mira's name.
                Desktop shows the longer "online" pill on the right;
                this gives mobile the same trust signal in compact form. */}
            <span
              className="md:hidden inline-flex items-center"
              style={{
                marginLeft: 8,
                gap: 5,
                fontSize: "0.7rem",
                color: "var(--lc-text-muted)",
                fontWeight: 400,
                fontFamily: "var(--lc-font-body)",
              }}
            >
              <span
                style={{
                  width: 6, height: 6, borderRadius: "50%",
                  background: "var(--lc-success)",
                  display: "inline-block",
                  marginRight: 4,
                }}
              />
              online
            </span>
          </div>
          <div className="lc-text-body lc-text-muted" style={{ fontSize: "0.75rem" }}>
            <span className="hidden sm:inline">The Les Concierges® AI · listens · gathers · brings to our team</span>
            {/* v3.29 — Tighter tagline on phones to honour the spec
                ("Mira · listens · gathers"). Stays warm but lighter. */}
            <span className="sm:hidden">listens · gathers · brings to our team</span>
          </div>
        </div>
        <span
          className="hidden md:inline-flex items-center gap-1.5 lc-text-body"
          style={{ fontSize: "0.7rem", color: "var(--lc-text-muted)" }}
        >
          <span
            className="lc-status-dot"
            style={{
              width: 6, height: 6, borderRadius: "50%",
              background: "var(--lc-success)",
              display: "inline-block",
            }}
          />
          online
        </span>

        {/* v3.6 — Three-dot menu (Start / End / Recent) */}
        <div style={{ position: "relative" }}>
          <button
            onClick={() => setMenuOpen((o) => !o)}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: "6px 8px",
              borderRadius: 4,
              color: "var(--lc-text-muted)",
              fontSize: 18,
              lineHeight: 1,
            }}
            aria-label="Chat options"
            data-testid="mira-menu-toggle"
          >
            <span style={{ letterSpacing: "1.5px" }}>•••</span>
          </button>
          {menuOpen && (
            <div
              style={{
                position: "absolute", top: "100%", right: 0, marginTop: 6,
                background: "var(--lc-cream)",
                border: "1px solid var(--lc-border-soft)",
                borderRadius: 6,
                boxShadow: "0 10px 30px rgba(11, 16, 32, 0.12)",
                minWidth: 220,
                zIndex: 30,
              }}
              data-testid="mira-menu-popover"
            >
              <MenuItem onClick={startNewChat} label="Start a new chat" icon="✎" testid="mira-menu-new" />
              <MenuItem onClick={endChat} label="End chat" icon="✕" testid="mira-menu-end" />
              <MenuItem onClick={openRecent} label="View recent chats" icon="↻" testid="mira-menu-recent" />
            </div>
          )}
        </div>
      </div>

      {/* v3.6 — Recent chats modal */}
      {recentOpen && (
        <RecentChatsModal
          sessions={recentSessions}
          onClose={() => setRecentOpen(false)}
          onResume={async (s) => {
            // v3.9 — Fetch full transcript and hydrate the chat surface
            try {
              const res = await fetch(
                `${BACKEND_URL}/api/mira/visitor/${encodeURIComponent(visitorId)}/session/${encodeURIComponent(s.session_id)}`
              );
              if (!res.ok) throw new Error("Session not found");
              const data = await res.json();
              const msgs = (data.messages || []).map((m) => ({
                role: m.role === "user" ? "user" : "mira",
                text: m.text,
              }));
              if (msgs.length === 0) {
                // Nothing to restore — just close
                setRecentOpen(false);
                return;
              }
              setMessages([{ role: "mira", text: theme.mira_intro }, ...msgs]);
              setSessionId(s.session_id);
              setGoverningUsed(true);  // governing sentence already used in the original thread
              setInspirationsUsed(true);
              sessionStartedRef.current = true;  // do not re-start
              setRecentOpen(false);
              setTimeout(() => {
                try {
                  transcriptRef.current?.scrollTo({ top: transcriptRef.current.scrollHeight, behavior: "smooth" });
                } catch { /* non-fatal */ }
              }, 200);
            } catch {
              setRecentOpen(false);
            }
          }}
        />
      )}

      {/* v3.30 — Starter chips moved BELOW the input per the welcome
          tightening brief. Reads as a soft prompt-suggestion near the
          input ("not sure where to start? try one"), not a banner over
          her greeting. Visible only on the very first turn. */}

      {/* v3.40 — Identity capture moved OUT of the empty state. The
          standalone banner above the transcript blocked the welcome
          ("Mira intro · input · chips") and asked for contact before any
          value was shown. Per founder soft-progressive doctrine (PRD
          v3.37): chat stays anonymous; identity is asked at the
          brief send-moment, inside the brief panel above the Send
          button. See <BriefIdentityFields /> inside <BasketHeader />. */}

      {/* Transcript */}
      <div
        ref={transcriptRef}
        className="flex-1 overflow-y-auto px-5 py-5 space-y-4"
        style={{ background: "var(--lc-bg-card)" }}
      >
        {messages.map((m, i) => (
          <MessageBubble
            key={i}
            message={m}
            messageIndex={i}
            onFollowUp={send}
            onHandoffYes={handleHandoffYes}
            onHandoffSend={onHandoffSend}
            submitState={submitState}
            backendUrl={BACKEND_URL}
          />
        ))}
        {thinking && <ThinkingBubble />}
      </div>

      {/* Input */}
      <div
        className="flex items-center gap-2 px-5 py-4"
        style={{
          borderTop: "1px solid var(--lc-border-soft)",
          background: "var(--lc-bg-elevated)",
          borderBottomLeftRadius: "var(--lc-radius-md)",
          borderBottomRightRadius: "var(--lc-radius-md)",
        }}
      >
        <input
          className="flex-1 outline-none lc-text-body"
          style={{
            background: "transparent",
            border: "none",
            color: "var(--lc-text-primary)",
            fontSize: "0.9375rem",
            padding: "0.5rem 0",
          }}
          placeholder={voice.listening ? "Listening…" : theme.placeholder_text}
          value={voice.listening && voice.interim ? `${input}${input ? " " : ""}${voice.interim}` : input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          data-testid="mira-input"
          disabled={voice.listening}
        />
        {voice.supported && (
          <button
            type="button"
            onClick={voice.listening ? voice.stop : voice.start}
            aria-label={voice.listening ? "Stop listening" : "Start voice input"}
            title={voice.listening ? "Stop listening" : "Speak to Mira"}
            data-testid="mira-mic-btn"
            data-listening={voice.listening ? "true" : "false"}
            className="lc-btn-secondary"
            style={{
              width: 38,
              height: 38,
              borderRadius: "var(--radius-full, 9999px)",
              padding: 0,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              background: voice.listening
                ? "var(--color-error, #8B2635)"
                : "transparent",
              color: voice.listening ? "#fff" : "var(--lc-text-secondary)",
              border: voice.listening
                ? "1px solid var(--color-error, #8B2635)"
                : "1px solid var(--lc-border)",
              cursor: "pointer",
              transition: "all 160ms ease",
              animation: voice.listening ? "lc-pulse 1.2s ease-in-out infinite" : "none",
              marginRight: 6,
              flex: "0 0 auto",
            }}
          >
            {/* Inline SVG mic icon — keeps zero-deps, scales crisply */}
            <svg
              width="16" height="16" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 1 0 6 0V5a3 3 0 0 0-3-3z" />
              <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
              <line x1="12" y1="19" x2="12" y2="22" />
              <line x1="8" y1="22" x2="16" y2="22" />
            </svg>
          </button>
        )}
        <button
          className="lc-btn-primary"
          style={{ padding: "0.55rem 1.1rem", fontSize: "0.8125rem" }}
          onClick={() => send()}
          data-testid="mira-send-btn"
          disabled={!input.trim() || thinking || voice.listening}
        >
          Send
        </button>
      </div>

      {/* v3.30 — Starter chips, now BELOW the input. Visible only on
          the very first turn; quiet "not sure where to start?" line +
          2 chips on mobile, 4 on desktop. Reads as a gentle nudge, not
          a banner above Mira's greeting. */}
      {messages.length === 1 && (
        <div
          className="px-5 pt-3 pb-4"
          style={{
            borderTop: "1px solid var(--lc-border-soft)",
            background: "var(--lc-bg-card)",
          }}
          data-testid="starter-chips-row"
        >
          <div
            className="lc-text-display lc-text-muted"
            style={{
              fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif",
              fontStyle: "italic",
              fontSize: "0.78rem",
              marginBottom: "0.6rem",
              letterSpacing: "0.005em",
            }}
          >
            Not sure where to start? Try one.
          </div>
          {/* Mobile: 2 chips, 1 column */}
          <div className="grid grid-cols-1 gap-2 lg:hidden">
            {STARTER_CHIPS.map((c) => (
              <button
                key={c.id}
                className="lc-chip text-left"
                style={{
                  justifyContent: "flex-start",
                  padding: "0.5rem 0.95rem",
                  fontSize: "0.82rem",
                }}
                onClick={() => send(c.text)}
                data-testid={`starter-chip-${c.id}`}
              >
                {c.text}
              </button>
            ))}
          </div>
          {/* Desktop: 4 chips, 2 columns */}
          <div className="hidden lg:grid grid-cols-2 gap-2">
            {STARTER_CHIPS_DESKTOP.map((c) => (
              <button
                key={c.id}
                className="lc-chip text-left"
                style={{
                  justifyContent: "flex-start",
                  padding: "0.48rem 0.85rem",
                  fontSize: "0.78rem",
                }}
                onClick={() => send(c.text)}
                data-testid={`starter-chip-${c.id}`}
              >
                {c.text}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// v3.40 / Identity capture — Soft-progressive, INSIDE THE BRIEF PANEL.
//
// Doctrine (founder, 25 May 2026 — PRD v3.37):
//   "Lowest friction at the front door, identity captured exactly when
//    the member wants to give it. Soft-progressive: chat anonymous;
//    identity asked at the brief send-moment, optional but encouraged."
//
// v3.39 placed the banner above the empty-state transcript — but the
// founder flagged it was sitting in the way of the welcome (Mira intro ·
// input · chips). v3.40 moves it INSIDE the brief panel (right pane),
// rendered just above the Send button. The empty state stays clean;
// identity surfaces exactly when the member is about to hand off.
//
// Persists captured values to localStorage so subsequent sessions know
// the member. Auto-persist on blur — no separate Save button (the Send
// brief button is the action). Phase 2 (Memory / Soul) will replace
// this localStorage shim with a proper backend-persisted member record
// keyed by (tenant, phone|email).
function BriefIdentityFields() {
  const initialName = (() => {
    if (typeof window === "undefined") return "";
    try { return localStorage.getItem("lc_visitor_name") || ""; } catch (e) { return ""; }
  })();
  const initialEmail = (() => {
    if (typeof window === "undefined") return "";
    try { return localStorage.getItem("lc_visitor_email") || ""; } catch (e) { return ""; }
  })();
  const initialPhone = (() => {
    if (typeof window === "undefined") return "";
    try { return localStorage.getItem("lc_visitor_phone") || ""; } catch (e) { return ""; }
  })();
  const [name, setName] = useState(initialName);
  const [email, setEmail] = useState(initialEmail);
  const [phone, setPhone] = useState(initialPhone);

  function persistKey(key, value) {
    if (typeof window === "undefined") return;
    try {
      if (value && value.trim()) localStorage.setItem(key, value.trim());
      else localStorage.removeItem(key);
    } catch (e) { /* private mode etc — silent */ }
  }

  const inputStyle = {
    padding: "0.5rem 0.65rem",
    border: "1px solid var(--lc-border-soft)",
    borderRadius: "var(--lc-radius-sm)",
    background: "var(--lc-bg-elevated, #FAF8F5)",
    fontFamily: "var(--lc-font-body)",
    fontSize: "0.78rem",
    color: "var(--lc-text-primary)",
    outline: "none",
    width: "100%",
  };

  return (
    <div
      data-testid="brief-identity-fields"
      style={{
        marginBottom: 10,
        padding: "0.7rem 0.85rem",
        background: "rgba(201,151,58,0.04)",
        border: "1px dashed rgba(201,151,58,0.35)",
        borderRadius: "var(--lc-radius-sm)",
      }}
    >
      <div
        className="lc-eyebrow"
        style={{ color: "var(--lc-text-secondary)", marginBottom: 2 }}
      >
        So we can call you back
      </div>
      <div
        style={{
          fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif",
          fontSize: "0.78rem",
          fontStyle: "italic",
          color: "var(--lc-text-muted, #6E7B72)",
          lineHeight: 1.4,
          marginBottom: 8,
          letterSpacing: "0.005em",
        }}
      >
        Optional — we&apos;ll only use this to reach you the moment our team is ready.
      </div>
      <div
        className="lc-identity-fields-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: 6,
        }}
      >
        <input
          type="text"
          placeholder="Name"
          data-testid="brief-identity-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={(e) => persistKey("lc_visitor_name", e.target.value)}
          style={inputStyle}
        />
        <input
          type="email"
          placeholder="Email"
          data-testid="brief-identity-email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={(e) => persistKey("lc_visitor_email", e.target.value)}
          style={inputStyle}
        />
        <input
          type="tel"
          placeholder="WhatsApp / phone"
          data-testid="brief-identity-phone"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          onBlur={(e) => persistKey("lc_visitor_phone", e.target.value)}
          style={inputStyle}
        />
      </div>
      <style>{`
        @media (min-width: 600px) {
          .lc-identity-fields-grid {
            grid-template-columns: 1fr 1fr 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}


function MessageBubble({ message, messageIndex, onFollowUp, onHandoffYes, onHandoffSend, submitState, backendUrl }) {
  const isUser = message.role === "user";

  // USER: keeps the navy filled bubble — they're confirming what they said
  if (isUser) {
    return (
      <div className="flex justify-end">
        <div style={{ maxWidth: "85%" }}>
          <div
            className="lc-text-body"
            style={{
              background: "var(--lc-accent)",
              color: "var(--lc-cream)",
              padding: "0.85rem 1.1rem",
              borderRadius: "var(--lc-radius-lg)",
              borderTopRightRadius: "0.25rem",
              fontSize: "0.9375rem",
              lineHeight: 1.55,
            }}
            data-testid="message-user"
          >
            {message.text}
          </div>
        </div>
      </div>
    );
  }

  // MIRA: NO bubble. Editorial register.
  // v3.19 — Italics are NEVER applied globally to the first sentence. The
  // model now drives ALL emphasis via inline *italic* and **bold** markers
  // (per R24 in the prompt) and renderInlineMarkdown turns those into <em>
  // and <strong>. Previous "first sentence is italic" CSS was creating long
  // italic walls when the model emitted multi-line opening sentences — and
  // making asterisks render as literal `*Word*` text. Both gone.
  const text = message.text || "";
  const firstSentenceMatch = text.match(/^([^.!?]+[.!?])(\s+)([\s\S]*)$/);
  const opening = firstSentenceMatch ? firstSentenceMatch[1] : text;
  const rest = firstSentenceMatch ? firstSentenceMatch[3] : "";

  return (
    <div className="flex justify-start">
      <div style={{ maxWidth: "92%" }} data-testid="message-mira">
        {/* v3.24 — Honest curating-status. When the bubble is still
            streaming and no tokens have arrived yet, render the rotating
            status copy in place of the prose. As tokens flow, this
            disappears and the prose takes over. */}
        {message.streaming && !text && message.curatingStatus && (
          <div
            data-testid="curating-status"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.55rem",
              padding: "0.55rem 0.95rem",
              borderRadius: 999,
              background: "rgba(15, 61, 46, 0.04)",
              border: "1px solid rgba(15, 61, 46, 0.10)",
              fontFamily:
                "'Fraunces', 'Cormorant Garamond', Georgia, serif",
              fontStyle: "italic",
              color: "var(--lc-text-secondary, #5C6863)",
              fontSize: "0.95rem",
              letterSpacing: "0.005em",
              animation: "lc-fade-in 220ms ease-out",
            }}
          >
            <span
              aria-hidden
              style={{
                width: 8, height: 8, borderRadius: "50%",
                background: "var(--lc-accent, #C9973A)",
                boxShadow: "0 0 0 0 rgba(201,151,58,0.55)",
                animation: "lc-pulse-dot 1.4s ease-in-out infinite",
                flexShrink: 0,
              }}
            />
            <span>{message.curatingStatus}</span>
          </div>
        )}
        {text && (<>
        <div
          className="lc-text-display"
          style={{
            /* v3.26 — Unified body type for Mira's prose. The first
               paragraph was 1.0625rem (display) and the rest 0.9375rem
               (body), creating an inconsistent visual on desktop AND
               mobile. Now BOTH render at the same body size (0.9375rem)
               with the same line-height; hierarchy comes from spacing
               and the italic intro convention, not font scale. */
            fontSize: "0.9375rem",
            color: "var(--lc-text-primary)",
            lineHeight: 1.65,
            fontWeight: 400,
            letterSpacing: "-0.003em",
          }}
        >
          {renderInlineMarkdown(opening)}
          {message.streaming && (
            <span
              aria-hidden
              style={{
                display: "inline-block",
                width: 2, height: "1em",
                marginLeft: 2,
                verticalAlign: "text-bottom",
                background: "var(--lc-accent, #C9973A)",
                animation: "lc-caret-blink 1s steps(2) infinite",
              }}
            />
          )}
        </div>
        {rest && (
          <div
            className="lc-text-body"
            style={{
              marginTop: "0.65rem",
              fontSize: "0.9375rem",   // v3.26 — matches opening above
              color: "var(--lc-text-secondary)",
              lineHeight: 1.65,
              whiteSpace: "pre-wrap",
            }}
          >
            {renderInlineMarkdown(rest)}
          </div>
        )}
        </>)}

        {/* v3.21 — Contextual upsell card. When Mira offers a transfer /
            private chef / VIP table / etc, the chips become accept/decline
            framed inside a brass-bordered card with the visible REASON. */}
        {message.contextualUpsell && (
          <div
            className="tdc-card"
            data-testid={`contextual-upsell-${messageIndex}`}
            data-kind={message.contextualUpsell.kind || "other"}
            style={{
              marginTop: "0.85rem",
              padding: "0.85rem 1.05rem",
              borderLeft: "3px solid var(--lc-highlight)",
              background: "linear-gradient(180deg, rgba(201,151,58,0.06), transparent 70%)",
            }}
          >
            <div
              style={{
                fontFamily: "var(--font-sans)",
                fontSize: "0.65rem",
                fontWeight: 700,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "var(--lc-highlight)",
                marginBottom: "0.35rem",
              }}
            >
              In-context offer · Mira
            </div>
            <div
              style={{
                fontFamily: "var(--font-serif)",
                fontSize: "1rem",
                color: "var(--lc-text-primary)",
                marginBottom: "0.35rem",
                lineHeight: 1.35,
                fontWeight: 500,
              }}
            >
              {message.contextualUpsell.title}
            </div>
            <div
              style={{
                fontSize: "0.85rem",
                color: "var(--lc-text-secondary)",
                lineHeight: 1.55,
                marginBottom: message.contextualUpsell.indicative_price ? "0.4rem" : "0.7rem",
                fontStyle: "italic",
              }}
            >
              {message.contextualUpsell.reason}
            </div>
            {message.contextualUpsell.indicative_price && (
              <div
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: "0.7rem",
                  color: "var(--color-text-faint)",
                  marginBottom: "0.7rem",
                  letterSpacing: "0.04em",
                }}
              >
                {message.contextualUpsell.indicative_price} · nothing charged until you approve
              </div>
            )}
            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => onFollowUp(message.contextualUpsell.accept_label || "Yes, arrange it")}
                data-testid={`upsell-accept-${messageIndex}`}
                style={{
                  /* v3.27 — §4 Brass-discipline: outline-only. */
                  background: "var(--lc-cream, #FAF8F5)",
                  color: "var(--lc-text-primary, #0F3D2E)",
                  border: "1px solid var(--lc-accent, #C9973A)",
                  borderRadius: 999,
                  padding: "0.42rem 0.95rem",
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  letterSpacing: "0.03em",
                  cursor: "pointer",
                }}
              >
                {message.contextualUpsell.accept_label || "Yes, arrange it"}
              </button>
              <button
                type="button"
                onClick={() => onFollowUp(message.contextualUpsell.decline_label || "Skip this one")}
                data-testid={`upsell-decline-${messageIndex}`}
                style={{
                  background: "transparent",
                  color: "var(--lc-text-secondary)",
                  border: "1px solid var(--lc-border)",
                  borderRadius: 999,
                  padding: "0.42rem 0.95rem",
                  fontSize: "0.78rem",
                  fontWeight: 400,
                  fontStyle: "italic",
                  cursor: "pointer",
                }}
              >
                {message.contextualUpsell.decline_label || "Skip this one"}
              </button>
            </div>
          </div>
        )}

        {/* v3.43 / Phase C — Programme-benefit gate chips.
            Rendered when /api/lc/benefits/gate-check returned
            gate_required=true. Yes → persist localStorage, retry the
            original turn via onFollowUp (which is the send() function).
            No → enrolment enquiry ticket + append a Mira ack bubble. */}
        {message.gatePrompt && (
          <div className="flex flex-wrap gap-2 mt-3" data-testid={`benefit-gate-${messageIndex}`}>
            <button
              type="button"
              data-testid={`benefit-gate-yes-${messageIndex}`}
              onClick={() => {
                try {
                  if (typeof window !== "undefined" && message.gatePrompt.localstorage_key) {
                    localStorage.setItem(message.gatePrompt.localstorage_key, "yes");
                  }
                } catch (_) {}
                if (message.originalUserText && typeof onFollowUp === "function") {
                  onFollowUp(message.originalUserText);
                }
              }}
              style={{
                background: "var(--lc-forest, #0F3D2E)",
                color: "var(--lc-cream, #FAF8F5)",
                border: "none",
                borderRadius: 999,
                padding: "0.55rem 1.1rem",
                fontSize: "0.82rem", fontWeight: 600,
                letterSpacing: "0.02em", cursor: "pointer",
              }}
            >
              {message.gatePrompt.chip_yes_label || "Yes"}
            </button>
            <button
              type="button"
              data-testid={`benefit-gate-no-${messageIndex}`}
              onClick={async () => {
                try {
                  if (typeof window !== "undefined" && message.gatePrompt.localstorage_key) {
                    localStorage.setItem(message.gatePrompt.localstorage_key, "no");
                  }
                } catch (_) {}
                let name = null, email = null, phone = null;
                try {
                  if (typeof window !== "undefined") {
                    name = localStorage.getItem("lc_visitor_name");
                    email = localStorage.getItem("lc_visitor_email");
                    phone = localStorage.getItem("lc_visitor_phone");
                  }
                } catch (_) {}
                try {
                  await fetch(`${backendUrl}/api/lc/benefits/enrolment-enquiry`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      benefit_id: message.gatePrompt.benefit_id,
                      member_name: name,
                      member_email: email,
                      member_phone: phone,
                      note: message.originalUserText || "",
                    }),
                  });
                } catch (_) {}
                if (typeof onFollowUp === "function") {
                  // Send a synthetic continuation message so the chat
                  // shows Mira's warm acknowledgement without forcing
                  // a brain call. We piggyback on the existing send()
                  // by passing a phrase that the brain handles as a
                  // graceful "tell me more" continuation.
                  onFollowUp("I'd like to know more about joining Club Social");
                }
              }}
              style={{
                background: "transparent",
                color: "var(--lc-text-secondary)",
                border: "1px solid var(--lc-border)",
                borderRadius: 999,
                padding: "0.55rem 1.1rem",
                fontSize: "0.78rem", fontStyle: "italic", cursor: "pointer",
              }}
            >
              {message.gatePrompt.chip_no_label || "Not yet"}
            </button>
          </div>
        )}

        {/* v3.21 — Two-stage handoff buttons.
            trip_state="summary" → first yes ("Yes, that's perfect" / Refine)
            trip_state="awaiting_handoff_yes" → second yes ("Yes, please proceed" / Hold on)
            handoff=true → final Send/Refine pair (existing logic). */}
        {!message.contextualUpsell && message.tripState === "summary" && (
          <div className="flex flex-wrap gap-2 mt-3" data-testid={`summary-stage-${messageIndex}`}>
            <button
              type="button"
              onClick={() => onFollowUp("Yes, that's perfect")}
              data-testid={`summary-yes-${messageIndex}`}
              style={{
                /* v3.27 — §4 Brass-discipline: outline + small dot. */
                background: "var(--lc-cream, #FAF8F5)",
                color: "var(--lc-text-primary, #0F3D2E)",
                border: "1px solid var(--lc-accent, #C9973A)",
                borderRadius: 999,
                padding: "0.55rem 1.1rem",
                fontSize: "0.82rem", fontWeight: 600,
                letterSpacing: "0.03em", cursor: "pointer",
                boxShadow: "0 1px 0 var(--lc-accent, #C9973A)",
              }}
            >
              Yes, that's perfect
            </button>
            <button
              type="button"
              onClick={() => onFollowUp("Refine a detail first")}
              data-testid={`summary-refine-${messageIndex}`}
              style={{
                background: "transparent",
                color: "var(--lc-text-secondary)",
                border: "1px solid var(--lc-border)",
                borderRadius: 999,
                padding: "0.55rem 1.1rem",
                fontSize: "0.78rem", fontStyle: "italic", cursor: "pointer",
              }}
            >
              Refine a detail first
            </button>
          </div>
        )}
        {!message.contextualUpsell && message.tripState === "awaiting_handoff_yes" && (
          <div className="flex flex-wrap gap-2 mt-3" data-testid={`handoff-stage-${messageIndex}`}>
            <button
              type="button"
              onClick={() => (onHandoffYes ? onHandoffYes() : onFollowUp("Yes, please proceed"))}
              data-testid={`handoff-yes-${messageIndex}`}
              style={{
                /* v3.27 — §4 Brass-discipline: outline-with-cream-fill.
                   This is the SIGNATURE moment (second YES) — the brass
                   border + brass underline + the soft pulse-dot carry
                   the wax-seal feel without flooding the button. */
                background: "var(--lc-cream, #FAF8F5)",
                color: "var(--lc-text-primary, #0F3D2E)",
                border: "1.5px solid var(--lc-accent, #C9973A)",
                borderRadius: 999,
                padding: "0.6rem 1.2rem",
                fontSize: "0.85rem", fontWeight: 600,
                letterSpacing: "0.04em", cursor: "pointer",
                boxShadow: "0 1px 0 var(--lc-accent, #C9973A)",
                animation: "lc-pulse 2s ease-in-out infinite",
              }}
            >
              Yes, please proceed
            </button>
            <button
              type="button"
              onClick={() => onFollowUp("Hold on — I'll come back")}
              data-testid={`handoff-hold-${messageIndex}`}
              style={{
                background: "transparent",
                color: "var(--lc-text-secondary)",
                border: "1px solid var(--lc-border)",
                borderRadius: 999,
                padding: "0.6rem 1.2rem",
                fontSize: "0.78rem", fontStyle: "italic", cursor: "pointer",
              }}
            >
              Hold on — I'll come back
            </button>
          </div>
        )}

        {/* Phase 5 — Post-handoff itinerary package card.
            Renders the three deliverables: The Edit (HTML, member-facing)
            opens in a new tab; the WhatsApp pack copies to clipboard for
            sharing in the family group; the Ops Docket is concierge-only
            and is mentioned as held by the team (no link surfaced). */}
        {message.itineraryPackage && (
          <ItineraryPackageCard
            pkg={message.itineraryPackage}
            backendUrl={backendUrl}
            messageIndex={messageIndex}
          />
        )}

        {(!message.contextualUpsell &&
          message.tripState !== "summary" &&
          message.tripState !== "awaiting_handoff_yes" &&
          ((message.followUps && message.followUps.length > 0) || message.handoff)) ? (
          <div className="flex flex-wrap gap-1.5 mt-3" data-testid={`followup-row-${messageIndex}`}>
            {(() => {
              // v3.46.14 — Single-send doctrine. The brass "Send to Concierge® →"
              // chip surface is GONE. There is exactly ONE send action in the
              // product: the green "Send brief to Concierge® team →" button at
              // the bottom of the right brief panel. We:
              //   1. drop any "send to concierge" follow-up the brain still
              //      emits (defense-in-depth — server also strips this);
              //   2. ensure "Refine a detail first" is present on handoff turns.
              const list = (message.followUps || []).filter(
                (fu) => !/send to concierge/i.test(fu || "")
              );
              if (message.handoff) {
                const hasRefine = list.some((fu) => /refine/i.test(fu || ""));
                if (!hasRefine) list.unshift("Refine a detail first");
              }
              return list;
            })().map((fu, idx) => {
              // v3.46.14 — All chips now render as quiet refinement options.
              // The brass-pulsing send-CTA chip branch is removed (the green
              // brief-panel button is the only send surface).
              return (
                <button
                  key={idx}
                  className="lc-chip"
                  style={{ fontSize: "0.75rem", padding: "0.3rem 0.7rem", fontStyle: "italic" }}
                  onClick={() => onFollowUp(stripChipMarkdown(fu))}
                  data-testid={`followup-${messageIndex}-${idx}`}
                >
                  {stripChipMarkdown(fu)}
                </button>
              );
            })}
          </div>
        ) : null}
        {/* Compact reference confirmation chip — appears under any
            handoff message AFTER the brief is sent. v3.46.18 — Suppress
            when the message text ITSELF already carries the ticket id
            (the post-Send Mira acknowledgement), so the same reference
            isn't shown twice on the same bubble. */}
        {(() => {
          if (!message.handoff) return null;
          if (submitState?.status !== "sent") return null;
          if (!submitState?.ticketId) return null;
          try {
            if (typeof message.text === "string" &&
                message.text.indexOf(submitState.ticketId) >= 0) {
              return null;
            }
          } catch (_) {}
          return (
            <div
              className="mt-3 px-3 py-2 lc-text-body"
              style={{
                background: "rgba(74, 222, 128, 0.08)",
                border: "1px solid rgba(74, 222, 128, 0.28)",
                borderRadius: 6,
                fontSize: "0.78rem",
                color: "var(--lc-text-secondary)",
              }}
              data-testid={`handoff-confirmation-${messageIndex}`}
            >
              Reference <code style={{ fontFamily: "monospace" }}>{submitState.ticketId}</code> — your Concierge® will be in touch.
            </div>
          );
        })()}
      </div>
    </div>
  );
}

// Phase 5 / v3.28 §5 — ItineraryPackageCard.
// Renders the three deliverables produced by /api/lc/itinerary-handoff:
//   1. The Edit — opens the magazine-style HTML in a new tab (member)
//   2. WhatsApp Pack — copies the single-block share text to clipboard
//   3. Ops Docket — concierge-only, NEVER linked here
// v3.28: Adds a quiet "Share The Edit" action that opens a tiny inline
// form (WhatsApp deep-link OR email) routed through
// /api/lc/itinerary/{ref}/share. The Ops Docket is never shared.
function ItineraryPackageCard({ pkg, backendUrl, messageIndex }) {
  const [copied, setCopied] = React.useState(false);
  const [shareOpen, setShareOpen] = React.useState(false);
  const [shareMode, setShareMode] = React.useState(null); // "whatsapp" | "email"
  const [shareNote, setShareNote] = React.useState("");
  const [shareEmail, setShareEmail] = React.useState("");
  const [shareStatus, setShareStatus] = React.useState({ state: "idle", message: "" });
  const editHref = pkg?.edit_url ? `${backendUrl}${pkg.edit_url}` : null;
  const whatsAppText = pkg?.whatsapp_text || "";

  async function copyWhatsapp() {
    try {
      if (navigator.clipboard && whatsAppText) {
        await navigator.clipboard.writeText(whatsAppText);
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      } else {
        // Fallback for non-HTTPS / older browsers
        const ta = document.createElement("textarea");
        ta.value = whatsAppText;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      }
    } catch {
      /* non-fatal */
    }
  }

  async function doShareWhatsapp() {
    if (!pkg?.ref) return;
    setShareStatus({ state: "working", message: "Composing your share…" });
    try {
      const res = await fetch(`${backendUrl}/api/lc/itinerary/${pkg.ref}/share`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          channel: "whatsapp_link",
          sender_name: localStorage.getItem("lc_visitor_name") || "",
          note: shareNote,
          public_base_url: backendUrl,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.share_url) {
        // Open the member's WhatsApp composer in a new tab.
        window.open(data.share_url, "_blank", "noopener,noreferrer");
        setShareStatus({ state: "done", message: "Opened WhatsApp — choose who receives it." });
      } else {
        throw new Error("no share_url");
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[share] WhatsApp failed", e);
      setShareStatus({ state: "error", message: "Couldn't compose the share. Tap Copy WhatsApp pack instead." });
    }
  }

  async function doShareEmail() {
    if (!pkg?.ref || !shareEmail) return;
    setShareStatus({ state: "working", message: "Sending The Edit…" });
    try {
      const res = await fetch(`${backendUrl}/api/lc/itinerary/${pkg.ref}/share`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          channel: "email",
          to: [shareEmail],
          sender_name: localStorage.getItem("lc_visitor_name") || "",
          note: shareNote,
          public_base_url: backendUrl,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data?.result?.sent) {
        setShareStatus({
          state: "done",
          message: `The Edit is on its way to ${shareEmail}.`,
        });
        setShareEmail("");
      } else {
        const why = data?.result?.error || data?.result?.skipped_reason || "couldn't send";
        setShareStatus({ state: "error", message: `Couldn't send: ${why}.` });
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[share] email failed", e);
      setShareStatus({ state: "error", message: "Couldn't reach the team. Try again or use WhatsApp." });
    }
  }

  return (
    <div
      className="mt-4"
      data-testid={`itinerary-package-${messageIndex}`}
      data-ref={pkg?.ref}
      style={{
        background: "var(--lc-bg-elevated, #FFFFFF)",
        border: "1px solid var(--lc-accent, #C9973A)",
        borderRadius: "var(--lc-radius-lg, 14px)",
        padding: "1rem 1.1rem 1.05rem",
        boxShadow: "0 6px 20px rgba(15,61,46,0.07)",
      }}
    >
      <div
        style={{
          fontFamily:
            "'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif",
          fontSize: "0.68rem",
          fontWeight: 700,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: "var(--lc-accent, #C9973A)",
          marginBottom: "0.35rem",
        }}
      >
        Your trip pack
      </div>
      <div
        style={{
          fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif",
          fontSize: "1.15rem",
          fontWeight: 400,
          color: "var(--lc-text-primary, #1B1F1D)",
          lineHeight: 1.25,
        }}
      >
        {pkg?.destination ? `The ${pkg.destination} Edit` : "Your Edit is ready"}
      </div>
      {pkg?.date_range_label && (
        <div
          style={{
            fontFamily: "'Fraunces', Georgia, serif",
            fontStyle: "italic",
            fontSize: "0.85rem",
            color: "var(--lc-text-secondary, #5C6863)",
            marginTop: "0.15rem",
          }}
        >
          {pkg.date_range_label}
          {pkg.party_size ? ` · ${pkg.party_size} guests` : ""}
        </div>
      )}

      <div
        style={{
          marginTop: "0.85rem",
          display: "flex",
          flexWrap: "wrap",
          gap: "0.5rem",
        }}
      >
        {editHref && (
          <a
            href={editHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`itinerary-view-edit-${messageIndex}`}
            style={{
              /* v3.27 — §4 Brass-discipline: outline-only — the wax-seal
                 feel comes from the brass border + arrow ornament, not
                 from flooding the button with brass. */
              background: "var(--lc-cream, #FAF8F5)",
              color: "var(--lc-text-primary, #0F3D2E)",
              border: "1px solid var(--lc-accent, #C9973A)",
              borderRadius: 999,
              padding: "0.55rem 1.05rem",
              fontSize: "0.82rem",
              fontWeight: 600,
              letterSpacing: "0.03em",
              cursor: "pointer",
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: "0.35rem",
              transition: "background-color 180ms ease, border-color 180ms ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--lc-cream-soft, #F0EDE8)";
              e.currentTarget.style.borderColor = "var(--lc-text-primary, #0F3D2E)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "var(--lc-cream, #FAF8F5)";
              e.currentTarget.style.borderColor = "var(--lc-accent, #C9973A)";
            }}
          >
            View The Edit <span style={{ color: "var(--lc-accent, #C9973A)" }}>↗</span>
          </a>
        )}
        <button
          type="button"
          onClick={copyWhatsapp}
          disabled={!whatsAppText}
          data-testid={`itinerary-copy-whatsapp-${messageIndex}`}
          style={{
            background: copied
              ? "rgba(15,61,46,0.10)"
              : "transparent",
            color: "var(--lc-text-primary, #1B1F1D)",
            border: "1px solid var(--lc-border, rgba(15,61,46,0.18))",
            borderRadius: 999,
            padding: "0.55rem 1.05rem",
            fontSize: "0.82rem",
            fontWeight: 600,
            letterSpacing: "0.03em",
            cursor: whatsAppText ? "pointer" : "not-allowed",
            opacity: whatsAppText ? 1 : 0.5,
          }}
        >
          {copied ? "✓ Copied — paste into WhatsApp" : "Copy WhatsApp pack"}
        </button>
        {/* v3.28 §5 — Share The Edit. Quiet outline button — opens an
            inline form. WhatsApp deep-link OR email (server-side via
            Resend). The Ops Docket is NEVER shared on this path. */}
        <button
          type="button"
          onClick={() => {
            setShareOpen((v) => !v);
            setShareMode(null);
            setShareStatus({ state: "idle", message: "" });
          }}
          data-testid={`itinerary-share-edit-${messageIndex}`}
          style={{
            background: "transparent",
            color: "var(--lc-text-primary, #1B1F1D)",
            border: "1px solid var(--lc-border, rgba(15,61,46,0.18))",
            borderRadius: 999,
            padding: "0.55rem 1.05rem",
            fontSize: "0.82rem",
            fontWeight: 600,
            letterSpacing: "0.03em",
            cursor: "pointer",
          }}
        >
          {shareOpen ? "Close" : "Share The Edit"}
        </button>
      </div>

      {/* v3.28 §5 — Inline Share form, expands beneath the actions row */}
      {shareOpen && (
        <div
          data-testid={`itinerary-share-form-${messageIndex}`}
          style={{
            marginTop: "0.95rem",
            padding: "0.85rem 0.95rem 0.95rem",
            background: "var(--lc-cream-soft, #F0EDE8)",
            border: "1px solid var(--lc-border-soft, rgba(15,61,46,0.10))",
            borderRadius: "var(--lc-radius-md, 10px)",
          }}
        >
          <div
            style={{
              fontFamily: "'Inter', system-ui, sans-serif",
              fontSize: "0.66rem",
              fontWeight: 700,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "var(--lc-text-muted, #94A099)",
              marginBottom: "0.6rem",
            }}
          >
            Share with someone
          </div>
          {!shareMode && (
            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => setShareMode("whatsapp")}
                data-testid={`share-mode-whatsapp-${messageIndex}`}
                style={{
                  background: "var(--lc-cream, #FAF8F5)",
                  color: "var(--lc-text-primary, #0F3D2E)",
                  border: "1px solid var(--lc-accent, #C9973A)",
                  borderRadius: 999,
                  padding: "0.45rem 0.95rem",
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  letterSpacing: "0.02em",
                  cursor: "pointer",
                }}
              >
                via WhatsApp
              </button>
              <button
                type="button"
                onClick={() => setShareMode("email")}
                data-testid={`share-mode-email-${messageIndex}`}
                style={{
                  background: "var(--lc-cream, #FAF8F5)",
                  color: "var(--lc-text-primary, #0F3D2E)",
                  border: "1px solid var(--lc-accent, #C9973A)",
                  borderRadius: 999,
                  padding: "0.45rem 0.95rem",
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  letterSpacing: "0.02em",
                  cursor: "pointer",
                }}
              >
                via Email
              </button>
            </div>
          )}
          {shareMode && (
            <>
              {shareMode === "email" && (
                <input
                  type="email"
                  value={shareEmail}
                  onChange={(e) => setShareEmail(e.target.value)}
                  placeholder="Their email address"
                  data-testid={`share-email-input-${messageIndex}`}
                  style={{
                    width: "100%",
                    padding: "0.55rem 0.75rem",
                    background: "var(--lc-cream, #FAF8F5)",
                    color: "var(--lc-text-primary, #0F3D2E)",
                    border: "1px solid var(--lc-border, rgba(15,61,46,0.18))",
                    borderRadius: "var(--lc-radius-sm, 6px)",
                    fontSize: "0.85rem",
                    fontFamily: "inherit",
                    outline: "none",
                    marginBottom: "0.55rem",
                  }}
                />
              )}
              <textarea
                value={shareNote}
                onChange={(e) => setShareNote(e.target.value)}
                placeholder="Add a short note (optional) — 'look at this plan'…"
                rows={2}
                data-testid={`share-note-input-${messageIndex}`}
                style={{
                  width: "100%",
                  padding: "0.55rem 0.75rem",
                  background: "var(--lc-cream, #FAF8F5)",
                  color: "var(--lc-text-primary, #0F3D2E)",
                  border: "1px solid var(--lc-border, rgba(15,61,46,0.18))",
                  borderRadius: "var(--lc-radius-sm, 6px)",
                  fontSize: "0.85rem",
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontStyle: "italic",
                  outline: "none",
                  resize: "vertical",
                  marginBottom: "0.6rem",
                }}
              />
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={shareMode === "email" ? doShareEmail : doShareWhatsapp}
                  disabled={shareMode === "email" && !shareEmail}
                  data-testid={`share-submit-${messageIndex}`}
                  style={{
                    background: "var(--lc-cream, #FAF8F5)",
                    color: "var(--lc-text-primary, #0F3D2E)",
                    border: "1.5px solid var(--lc-accent, #C9973A)",
                    borderRadius: 999,
                    padding: "0.5rem 1.15rem",
                    fontSize: "0.82rem",
                    fontWeight: 600,
                    letterSpacing: "0.03em",
                    cursor: shareMode === "email" && !shareEmail ? "not-allowed" : "pointer",
                    opacity: shareMode === "email" && !shareEmail ? 0.5 : 1,
                    boxShadow: "0 1px 0 var(--lc-accent, #C9973A)",
                  }}
                >
                  {shareMode === "email" ? "Send The Edit" : "Open WhatsApp"}
                </button>
                <button
                  type="button"
                  onClick={() => { setShareMode(null); setShareStatus({ state: "idle", message: "" }); }}
                  data-testid={`share-back-${messageIndex}`}
                  style={{
                    background: "transparent",
                    color: "var(--lc-text-muted, #94A099)",
                    border: "1px solid var(--lc-border, rgba(15,61,46,0.12))",
                    borderRadius: 999,
                    padding: "0.5rem 1rem",
                    fontSize: "0.78rem",
                    cursor: "pointer",
                  }}
                >
                  Back
                </button>
              </div>
              {shareStatus.message && (
                <div
                  data-testid={`share-status-${messageIndex}`}
                  style={{
                    marginTop: "0.6rem",
                    fontSize: "0.78rem",
                    fontFamily: "'Fraunces', Georgia, serif",
                    fontStyle: "italic",
                    color:
                      shareStatus.state === "error"
                        ? "#8B2635"
                        : shareStatus.state === "done"
                        ? "var(--lc-accent, #C9973A)"
                        : "var(--lc-text-muted, #94A099)",
                  }}
                >
                  {shareStatus.message}
                </div>
              )}
              <div
                style={{
                  marginTop: "0.7rem",
                  fontSize: "0.7rem",
                  fontStyle: "italic",
                  color: "var(--lc-text-muted, #94A099)",
                  fontFamily: "'Fraunces', Georgia, serif",
                  lineHeight: 1.45,
                }}
              >
                Only The Edit is shared — never the Concierge® team's operational brief.
              </div>
            </>
          )}
        </div>
      )}

      <div
        style={{
          marginTop: "0.85rem",
          fontFamily: "'Fraunces', Georgia, serif",
          fontStyle: "italic",
          fontSize: "0.78rem",
          color: "var(--lc-text-muted, #94A099)",
          lineHeight: 1.5,
        }}
      >
        Your Concierge® team has the full operational brief on their desk and
        will be in touch shortly. Nothing is charged until you approve.
      </div>
      {pkg?.ticket_id && (
        <div
          style={{
            marginTop: "0.5rem",
            fontFamily:
              "'JetBrains Mono', 'SF Mono', Menlo, monospace",
            fontSize: "0.7rem",
            color: "var(--lc-text-faint, #94A099)",
            letterSpacing: "0.05em",
          }}
          data-testid={`itinerary-ref-${messageIndex}`}
        >
          Reference {pkg.ticket_id}
        </div>
      )}
    </div>
  );
}


function ThinkingBubble() {
  return (
    <div className="flex justify-start">
      <div
        className="lc-text-body lc-text-muted"
        style={{
          background: "var(--lc-bg-elevated)",
          padding: "0.85rem 1.1rem",
          borderRadius: "var(--lc-radius-lg)",
          borderTopLeftRadius: "0.25rem",
          fontSize: "0.875rem",
          fontStyle: "italic",
          border: "1px solid var(--lc-border-soft)",
        }}
        data-testid="mira-thinking"
      >
        <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
          <Dot delay={0} />
          <Dot delay={200} />
          <Dot delay={400} />
        </span>
      </div>
    </div>
  );
}

function Dot({ delay }) {
  return (
    <span
      style={{
        width: 6, height: 6, borderRadius: "50%",
        background: "var(--lc-highlight)",
        opacity: 0.5,
        animation: `lcPulse 1.2s ${delay}ms ease-in-out infinite`,
      }}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RIGHT PANE
// ─────────────────────────────────────────────────────────────────────────────

function RightPane({
  activeContext,
  isComposing,
  onArrangeVenue,
  basket,
  isInBasket,
  addToBasket,
  removeFromBasket,
  cityCapabilities,
  onSendBasket,
  submitState,
  // Pattern C state
  procStepsSelection,
  toggleStep,
  onSendProcedural,
  // v3.1 — pillar tap-to-ask
  onPillarTap,
}) {
  const [tab, setTab] = useState("suggested"); // legacy state — tabs removed in v3.27 but state kept to avoid breaking downstream refs
  const phase = activeContext?.phase || "gathering";
  const pattern = activeContext?.pattern || null;
  const isGathering = phase === "gathering";
  // v3.21 — R26 trip state machine drives the quiet-during-gathering rule
  const tripState = activeContext?.trip_state || null;

  // v3.26 — Pillar-aware results tab label. "Venues" was wrong for any
  // non-dining request — a medical companion is not a venue. The label
  // adapts to the actual pillar the member is in:
  //   Dine/Enjoy/Fit/Club    → "Picks"
  //   Stay/Travel/Escape/Move → "Options"
  //   Care/Advisory/Work     → "Next steps"
  //   Pattern B (search)      → "Sourcing"
  //   Pattern C (procedural)  → "Steps"
  // Default (no pillar yet)   → "Brief"
  const _resultsLabel = (() => {
    if (pattern === "C") return "Steps";
    if (pattern === "B") return "Sourcing";
    const pillarLabel = activeContext?.pillar_name || "";
    const pillarMap = {
      "Dine": "Picks",
      "Enjoy": "Picks",
      "Fit": "Picks",
      "Club": "Picks",
      "Stay": "Options",
      "Travel": "Options",
      "Escape": "Options",
      "Move": "Options",
      "Transport": "Options",
      "Care": "Next steps",
      "Advisory": "Next steps",
      "Work": "Next steps",
      "Documentation": "Next steps",
      "Family Care": "Next steps",
    };
    return pillarMap[pillarLabel] || "Brief";
  })();

  const TABS = [
    { id: "suggested", label: "Suggested" },
    { id: "venues",    label: _resultsLabel },
    { id: "human",     label: "Concierge®" },
  ];

  const hasPatternContent =
    (pattern === "A" && (activeContext?.venue_hints || []).length > 0) ||
    (pattern === "B" && (activeContext?.product_hints || []).length > 0) ||
    (pattern === "C" && (activeContext?.procedural_steps || []).length > 0);

  // v3.27 — "Pillars are context, not action" (per brief §2).
  // The FULL pillar list is a welcome-mat: shown ONLY while the output
  // panel is empty (before the member speaks). The moment Mira produces
  // ANY output — prose with inspirations OR structured cards — the
  // pillars give way to the output, and a thin name-only strip at the
  // bottom of the pane carries the breadth-reminder.
  //
  // The "December in Italy" test case was the proof: inspirations were
  // NAMED in the prose (Piedmont / Tuscany / Sicily) but no venue_hints
  // had been emitted yet — so the old logic still showed the full
  // pillar list. Now: any activeContext at all means output exists.
  const hasAnyOutput = !!activeContext;
  const showWelcomeMatPillars = !hasAnyOutput;
  const showThinPillarStrip = hasAnyOutput;

  return (
    <div
      className={showWelcomeMatPillars ? "flex flex-col gap-2.5" : "flex flex-col gap-3"}
      data-testid="mira-right-pane"
      data-pattern={pattern || "none"}
      data-phase={phase}
    >
      {/* v3.46.18 — Option C state-based hybrid. The BasketHeader rises
          to the top of the right pane only once Mira has work to show
          (≥1 brief_item, ≥1 basket item, handoff_moment, OR sent). On a
          cold open the cards lead — see the SuggestedPanel + alternatives
          stack below — and the basket header stays hidden, so the panel
          doesn't open with an empty "Your brief" box.

          When the first brief_items payload arrives, the BasketHeader
          mounts with a quiet 240ms ease-in. From that moment the layout
          is stable: basket on top, alternatives below — same shape ever
          after, no further re-shuffling.

          v3.46.18 — R32 / Phase 1b iv. Phase 1b brief_items are the
          line-item structure the BasketHeader renders. handoff_moment
          gating mirrors the server-side multi-ask gate visually.

          BC-003 · 9 Feb 2026 PM · doctrine fix · "the brief composer
          must never disappear mid-conversation". The previous gate
          required brief_items / basket / sent / handoff_moment to be
          truthy — fine for restaurant queries where venue_hints come
          back verified, but a Sardinia / Tokyo / "tell me about Capri"
          turn produces rich PROSE with no structured venue_hints
          (beaches / districts aren't verified Anchor rows). The brain
          would NEVER populate brief_items, so the BasketHeader would
          NEVER mount, and the right pane reverted to welcome-mat —
          the founder's "right panel disappeared!" P0 breach.
          New rule: once Mira has produced ANY conversational reply
          (`hasAnyOutput`), the brief composer mounts. The Send-to-
          Concierge® CTA is the doctrine-mandated handoff surface; it
          cannot evaporate because the brain didn't tag a structured
          line-item. The cold-open path is preserved by gating on
          `hasAnyOutput` rather than always-on. */}
      {pattern !== "C" && (
        hasAnyOutput ||
        (activeContext?.brief_items?.length || 0) > 0 ||
        basket.length > 0 ||
        submitState.status === "sent" ||
        activeContext?.handoff_moment
      ) && (
        <BasketHeader
          basket={basket}
          briefItems={activeContext?.brief_items || []}
          handoffMoment={!!activeContext?.handoff_moment}
          removeFromBasket={removeFromBasket}
          city={cityCapabilities?.city || activeContext?.primary_city}
          itineraryPlan={activeContext?.itinerary_plan || null}
          onSend={onSendBasket}
          submitState={submitState}
        />
      )}

      {/* v3.27 — Mobile tabs REMOVED entirely (per brief §3.3 — tab names
          weren't parallel: "Suggested / Picks / Concierge®" mixed a state,
          a content type, and a brand term). Replaced with the natural
          sequential stack: input → output cards → thin pillar strip →
          Concierge® handoff at end. The mobile experience now flows like
          a single column conversation. */}

      {/* Welcome-mat pillar list — ONLY when no output yet */}
      {showWelcomeMatPillars && (
        <SuggestedPanel
          activePillar={activeContext?.pillar}
          activePillarName={activeContext?.pillar_name}
          onPillarTap={onPillarTap}
        />
      )}

      {/* v3.21 — Right pane stays QUIET during the 4-exchange intake.
          No venue cards, no city tiles, no premature options — just the
          brief skeleton on the left, the conversation. */}
      {tripState && /^gathering_intake_/.test(tripState) ? (
        <div className="block">
          <BriefBuilder
            phase="gathering"
            primaryCity={activeContext?.primary_city}
            pillarName={activeContext?.pillar_name}
            subcategory={activeContext?.subcategory}
            gatheredDetails={activeContext?.gathered_details || []}
            needsDetails={activeContext?.needs_details || []}
            basket={basket || []}
            removeFromBasket={removeFromBasket}
            itineraryPlan={activeContext?.itinerary_plan || null}
          />
        </div>
      ) : (
      <>
      {/* v3.2 — Brief Builder always renders during Beat 2 (gathering),
            above the venues/sourcing/procedural panels. Tells the member
            "I have you" in real time. v3.19 — also surfaces the basket
            count so members understand when a single brief becomes a
            multi-leg itinerary.
            v3.31 — Gated on `hasAnyOutput` so the empty "Tell me what's
            on your mind — I'm listening." placeholder never appears
            before Mira has spoken once. The 5-cluster welcome-mat
            already carries the "I'm listening" signal; a second card
            saying the same thing was noise. */}
      {isGathering && hasAnyOutput && (
        <div className="block">
          <BriefBuilder
            phase={phase}
            primaryCity={activeContext?.primary_city}
            pillarName={activeContext?.pillar_name}
            subcategory={activeContext?.subcategory}
            gatheredDetails={activeContext?.gathered_details || []}
            needsDetails={activeContext?.needs_details || []}
            basket={basket || []}
            removeFromBasket={removeFromBasket}
            itineraryPlan={activeContext?.itinerary_plan || null}
          />
        </div>
      )}

      {/* v3.20 — ITINERARY PANEL — when Mira has composed a day-by-day
            multi-day plan, this REPLACES the flat venue grid. The plan
            renders as Day cards with morning/afternoon/evening slots. */}
      {activeContext?.itinerary_plan && (
        <div className="block">
          <ItineraryPlanPanel
            plan={activeContext.itinerary_plan}
            onRefine={(text) => onArrangeVenue({ name: text, city: null, _refine: true })}
            onSendItinerary={() => {
              // Use parent's send wiring via onArrangeVenue with a sentinel
              onArrangeVenue({ _sendItinerary: true });
            }}
          />
        </div>
      )}

      {/* Pattern dispatcher — A/B/C/D in Phase 2; empty-state Venues in Phase 1.
          v3.30: Hide ENTIRELY when there's no conversation yet (no
          activeContext). The welcome-mat alone fills the empty state —
          the "Picks will appear here once we've shaped what you're
          looking for" placeholder leaked the same kind of internal
          mechanic as the "0/1 captured" message. */}
      <div className="block">
        {hasAnyOutput && !activeContext?.itinerary_plan && (pattern === "A" || isGathering || !pattern) && (
          <VenuesPanel
            phase={phase}
            venueHints={activeContext?.venue_hints || []}
            mentionedPlaces={activeContext?.mentioned_places || []}
            onArrange={onArrangeVenue}
            addToBasket={addToBasket}
            removeFromBasket={removeFromBasket}
            isInBasket={isInBasket}
            hasContent={hasPatternContent}
            pillarName={activeContext?.pillar_name}
            isComposing={isComposing}
          />
        )}
        {pattern === "B" && (
          <SourcingPanel
            productHints={activeContext?.product_hints || []}
            sourcingHint={activeContext?.sourcing_hint}
            primaryCity={activeContext?.primary_city}
            onArrange={onArrangeVenue}
            addToBasket={addToBasket}
            removeFromBasket={removeFromBasket}
            isInBasket={isInBasket}
          />
        )}
        {pattern === "C" && (
          <ProceduralStepSelector
            steps={activeContext?.procedural_steps || []}
            adjacentCapabilities={activeContext?.adjacent_capabilities || []}
            selection={procStepsSelection}
            toggleStep={toggleStep}
            onSend={onSendProcedural}
            submitState={submitState}
            primaryCity={activeContext?.primary_city}
          />
        )}
      </div>

      {/* City capabilities — Phase 2 Pattern A only.
          v3.15 — Hide whenever Mira has surfaced specific venue_hints for
          this request. The city-capabilities tile is for open-ended "tell me
          what you can do in <city>" turns ONLY. On a specific request
          (Americano · Wasabi · etc.) the panel must stay focused on the
          named venue + relevant alternatives — no generic city filler.
          v3.46.22 (founder watching-day batch P0) — also hide on
          decline / out-of-scope / chitchat / farewell turns. The brain
          emits `intent` on these; dumping generic "Concierge® can:"
          services after Mira declined a dry-state whiskey or marijuana
          ask reads as broken. Founder pattern: "the panel must reflect
          the conversation; never dump generic content over a decline." */}
      {pattern === "A"
        && !isGathering
        && !["out_of_scope", "decline", "chitchat", "ambiguous", "farewell", "greeting"]
            .includes((activeContext?.intent || "").toLowerCase())
        && (activeContext?.venue_hints || []).length === 0
        && (activeContext?.product_hints || []).length === 0
        && cityCapabilities?.items?.length > 0 && (
        <div className="block">
          <CityCapabilitiesPanel
            city={cityCapabilities.city}
            source={cityCapabilities.source}
            items={cityCapabilities.items}
            activePillar={activeContext?.pillar_name}
            addToBasket={addToBasket}
            removeFromBasket={removeFromBasket}
            isInBasket={isInBasket}
          />
        </div>
      )}

      {/* Talk to a Concierge® */}
      <div className="block">
        <TalkToConciergePanel />
      </div>
      </>
      )}

      {/* v3.27 — Thin pillar name-strip — the "permanent reminder of
          breadth" the brief specifies for §2.1. Renders only when
          output exists (i.e. the welcome-mat full pillar list has
          already given way). NAME-ONLY, no schema sub-fields, no
          captures, no taps. Pure context. */}
      {showThinPillarStrip && (
        <ThinPillarStrip
          activePillarName={activeContext?.pillar_name}
          onPillarTap={onPillarTap}
        />
      )}

      {/* Promise band — protected zone, always visible on desktop */}
      <div className="hidden lg:block">
        <PromiseBand />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BASKET HEADER — sticky-feeling promise of "tell me many things, we deliver as one outcome"
// ─────────────────────────────────────────────────────────────────────────────

function BasketHeader({ basket, briefItems = [], handoffMoment = false, removeFromBasket, city, itineraryPlan, onSend, submitState }) {
  if (submitState.status === "sent") {
    return (
      <div
        className="lc-card-elevated lc-basket-header-rise"
        style={{
          /* v3.27 — §4 Brass-discipline: the "brief received" card was
             a brass-floodfill. Restored to cream with a brass hairline
             border + brass eyebrow + dark forest body. */
          padding: "1.1rem 1.25rem",
          background: "var(--lc-cream, #FAF8F5)",
          color: "var(--lc-text-primary, #0F3D2E)",
          border: "1px solid var(--lc-accent, #C9973A)",
        }}
        data-testid="basket-sent"
      >
        <div className="lc-eyebrow" style={{ color: "var(--lc-accent, #C9973A)" }}>Brief received</div>
        <div className="lc-text-display" style={{ fontStyle: "italic", fontSize: "0.95rem", marginTop: 6, lineHeight: 1.4, color: "var(--lc-text-primary, #0F3D2E)" }}>
          {submitState.message}
        </div>
        {submitState.ticketId && (
          <div className="lc-text-mono" style={{ fontSize: "0.65rem", opacity: 0.75, marginTop: 8, letterSpacing: "0.08em", color: "var(--lc-text-muted)" }}>
            REF · {submitState.ticketId}
          </div>
        )}
      </div>
    );
  }

  const items = Array.isArray(basket) ? basket : [];
  const lineItems = Array.isArray(briefItems) ? briefItems : [];
  const hasLineItems = lineItems.length > 0;
  const itineraryDays = itineraryPlan?.days?.length || 0;

  // ── 9 Feb 2026 · Doctrine fix · "Send to Concierge® must work" ────────
  // Founder ruling: Mira PROMISES the handoff throughout the
  // conversation ("your brief is composed", "your brief is with them
  // now"). When the gate kept the button disabled, the promise was
  // words without a mechanism — a small untruth, the exact thing the
  // doctrine forbids.
  //
  // New contract:
  //   1. The button is NEVER gated by missing line-item slots. Real
  //      concierge calls START as partial briefs ("a table tonight,
  //      somewhere nice"). The desk picks them up gracefully.
  //   2. The button STAYS gated by missing contact info (F13 doctrine
  //      preserved · the desk needs a callback channel to deliver).
  //   3. When line items are unsatisfied, a small honest hedge chip
  //      renders BELOW the button instead of blocking it · "the desk
  //      will ask before sending". Non-blocking. Trust contract.
  const unsatItems = lineItems.filter((li) => !li?.is_satisfied);
  const allLineItemsSatisfied = unsatItems.length === 0;
  // Kept for hedge-chip rendering only (no longer the gate).
  const hasUnsatisfiedSlots = hasLineItems && !allLineItemsSatisfied && !handoffMoment;
  const sendDisabled = (
    submitState.status === "sending"
    // F13 contact-only gate stays · evaluated further down once
    // _missingContactOnly is computed.
  );

  // ── F13 (FIR-20260602-007) · Contact-only + named-field surfacing ─────
  // Founder doctrine (2 Jun 2026 PM · binding):
  //   "A Concierge® handoff must never be hidden behind an unclear
  //    button. If Mira needs one more detail, she must ask for that
  //    detail — not leave the member or team stuck."
  //
  // The previous label only showed a numeric count ("One detail still
  // to capture →") which left the member guessing. F13 surfaces:
  //   (a) the field name itself when one item is short of satisfaction
  //   (b) a contact-specific CTA when the ONLY missing surface is
  //       contact (email / phone / WhatsApp), focusing the contact inputs
  //       instead of the chat input on click
  const _CONTACT_FIELDS = new Set([
    "email", "phone", "whatsapp", "contact", "phone_number",
    "contact_email", "contact_phone", "mobile",
  ]);
  const _humaniseField = (f) => {
    const map = {
      occasion: "occasion",
      party_size: "party size",
      guest_mix: "guest count",
      date: "date",
      date_hint: "date",
      time: "time",
      cuisine: "cuisine",
      city: "city",
      pickup: "pickup point",
      pickup_location: "pickup point",
      drop: "drop point",
      drop_location: "destination",
      vehicle: "vehicle type",
      vehicle_type: "vehicle type",
      flight_number: "flight number",
      nights: "number of nights",
      property_name: "property",
      destination: "destination",
      issue: "what the consult is for",
      preference: "preference",
      health_goal: "health goal",
      availability: "your availability",
      trainer_preference: "trainer preference",
      email: "email",
      phone: "phone",
      whatsapp: "WhatsApp",
      contact: "WhatsApp / phone",
      phone_number: "phone",
      contact_email: "email",
      contact_phone: "phone",
      mobile: "mobile",
    };
    return map[f] || String(f || "").replace(/_/g, " ");
  };
  // Flatten every unsatisfied missing field across all unsat items.
  const _allMissing = unsatItems.flatMap((li) => li.missing_fields || []);
  const _missingContactOnly = (
    _allMissing.length > 0
    && _allMissing.every((f) => _CONTACT_FIELDS.has(f))
  );
  // 9 Feb 2026 · the F13 contact-only gate is the ONLY thing that
  // can still block send. Doctrine: the desk needs a callback
  // channel to deliver. Everything else (missing slots etc.) is
  // surfaced as a non-blocking hedge chip below the button.
  const sendBlockedByMissingContact = _missingContactOnly;
  const sendDisabledFinal = sendDisabled || sendBlockedByMissingContact;
  const _firstNamedMissing = (() => {
    for (const li of unsatItems) {
      const missing = li.missing_fields || [];
      if (missing.length > 0) return missing[0];
    }
    return null;
  })();

  const sendButtonLabel = (() => {
    if (submitState.status === "sending") return "Sending…";
    // 9 Feb 2026 doctrine fix · the ONLY thing that re-labels the
    // button is the contact-only gate (F13). When slots are missing,
    // the button STILL says "Send brief to Concierge® team →" and a
    // small honest hedge chip below it names the missing fields. The
    // desk picks up partial briefs · the member is never told "wait,
    // I need more from you" by a button label.
    if (sendBlockedByMissingContact) {
      return "Add WhatsApp / phone to send this brief →";
    }
    return "Send brief to Concierge® team →";
  })();

  // v3.46.18 — Founder-found: when the Send button is gated, tapping it
  // gave the no-allowed cursor and SILENCE — the member is left wondering
  // why nothing happens. A concierge wouldn't refuse silently; they'd say
  // "I need a couple more things first, then I'll send it."
  //
  // The click handler now:
  //   1. If genuinely sending → no-op (prevent double-send).
  //   2. If gated → surface a humanised "I still need" toast that lists
  //      the missing fields PER LINE-ITEM, then focus the chat input so
  //      the member knows exactly where to type. Mira's brain naturally
  //      asks for the next missing field on the next user turn.
  //   3. If ready → fire the real send path.
  // ── 9 Feb 2026 · Doctrine-fix click handler ─────────────────────────
  // The button is no longer gated by missing slots. The only thing
  // that still blocks send is missing contact info (F13). When the
  // member taps Send with slots unfilled we fire the real send · the
  // desk picks up the partial brief and asks the rest. When the
  // member taps Send with contact missing we still surface the
  // focused contact-input nudge (the desk cannot reach back without
  // a channel).
  const handleSendClick = () => {
    if (submitState.status === "sending") return;
    if (!sendDisabledFinal) {
      return onSend();
    }
    // F13 (FIR-20260602-007): contact-only soft path — focus the
    // contact inputs (Email / WhatsApp / phone) with the founder-
    // verbatim "I have the brief ready" line already enforced
    // server-side by the F12 truth guard.
    if (sendBlockedByMissingContact) {
      try {
        toast.info("Add WhatsApp or email", {
          description: "I have the brief ready. Please share the best WhatsApp number or email so our Concierge® team can take this forward.",
          duration: 8000,
          id: "send-gated-contact-only",
        });
      } catch (_) { /* sonner not mounted */ }
      try {
        const inp = (typeof document !== "undefined")
          && (
            document.querySelector('[data-testid="brief-identity-phone"]')
            || document.querySelector('input[placeholder*="WhatsApp" i], input[placeholder*="phone" i]')
            || document.querySelector('[data-testid="brief-identity-email"]')
            || document.querySelector('input[type="email"], input[placeholder*="email" i]')
          );
        if (inp) {
          if (typeof inp.scrollIntoView === "function") {
            inp.scrollIntoView({ behavior: "smooth", block: "center" });
          }
          setTimeout(() => { try { inp.focus(); } catch (_) {} }, 300);
        }
      } catch (_) {}
      return;
    }
    // Defensive fallback · should not be reachable now that
    // sendDisabledFinal only covers sending + contact-only.
    onSend();
  };

  // v3.43 / Phase C — Club Access conditions band.
  // v3.46.7 (25 May 2026) — Founder-found bug: the band was sticky for
  // the entire session once a member answered "Yes" to the Club Social
  // gate. A subsequent restaurant brief (e.g. Nuna al Sole) still
  // showed the club rules — wrong information on the brief panel.
  // Fix: gate now ANDs the member-status flag with "current brief
  // actually contains a Club-pillar item". The band must DISAPPEAR
  // the moment the brief becomes a non-club request — it's only
  // relevant when there's a club in play.
  const _isClubItem = (it) => {
    if (!it) return false;
    if (it.pillar_name === "Club") return true;
    if (it.pillar === "Club") return true;
    // Defensive: source tag from lc_clubs / club_intent paths.
    const src = (it.source || "").toLowerCase();
    if (src === "lc_clubs" || src === "club_intent") return true;
    // Last-ditch: subcategory text says club. Conservative — only
    // exact phrase matches the spec uses; avoids "country" mismatches.
    const sub = (it.subcategory || "").toLowerCase();
    if (sub.includes("private club") || sub.includes("members' club") ||
        sub.includes("country club") || sub.includes("reciprocal club")) return true;
    return false;
  };
  const _isClubSocialMember = (() => {
    try {
      if (typeof window === "undefined") return false;
      return localStorage.getItem("lc_clubsocial_member") === "yes";
    } catch (_) { return false; }
  })();
  const isClubAccessBasket = _isClubSocialMember && (Array.isArray(basket) ? basket.some(_isClubItem) : false);

  return (
    <div
      className="lc-card-elevated lc-basket-header-rise"
      style={{ padding: "1.05rem 1.15rem", border: "1px solid var(--lc-highlight)" }}
      data-testid="basket-header"
      data-line-items={lineItems.length}
      data-all-satisfied={allLineItemsSatisfied ? "true" : "false"}
    >
      <div className="flex items-baseline justify-between mb-2">
        <div className="lc-eyebrow" style={{ color: "var(--lc-highlight)" }}>
          {itineraryDays >= 1 ? `Your ${itineraryDays}-day itinerary` : "Your Concierge® brief"}
        </div>
        <span className="lc-text-mono lc-text-muted" style={{ fontSize: "0.65rem" }}>
          {hasLineItems
            ? `${lineItems.length} ${lineItems.length === 1 ? "ask" : "asks"}`
            : `${items.length} ${items.length === 1 ? "item" : "items"}`}
        </span>
      </div>
      {city && (
        <div className="lc-text-display" style={{ fontSize: "0.85rem", fontStyle: "italic", color: "var(--lc-text-secondary)", marginBottom: 12, lineHeight: 1.4 }}>
          {city}
        </div>
      )}

      {/* v3.43 / Phase C — Club access conditions band. Renders only
          when the member is a Club Social member (gate answered Yes
          this session). Carries the 5 verification rules verbatim so
          the member knows what the concierge will check before issuing
          the introduction letter. */}
      {isClubAccessBasket && items.length > 0 && (
        <div
          data-testid="club-access-conditions-band"
          style={{
            marginBottom: 12,
            padding: "0.85rem 1rem",
            background: "rgba(15,61,46,0.04)",
            border: "1px dashed rgba(15,61,46,0.35)",
            borderRadius: "var(--lc-radius-sm)",
          }}
        >
          <div
            className="lc-eyebrow"
            style={{ color: "var(--lc-forest, #0F3D2E)", marginBottom: 4 }}
          >
            Club access · our team will verify
          </div>
          <div
            style={{
              fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif",
              fontSize: "0.8rem",
              fontStyle: "italic",
              color: "var(--lc-text-muted, #6E7B72)",
              lineHeight: 1.45,
              marginBottom: 8,
            }}
          >
            We&apos;ll walk these through with you before issuing the introduction letter.
          </div>
          <ul
            style={{
              listStyle: "none",
              padding: 0, margin: 0,
              fontSize: "0.74rem",
              color: "var(--lc-text-primary)",
              lineHeight: 1.55,
            }}
          >
            <li><strong>Eligible persons:</strong> the member, their spouse, and up to two dependent children only — no guests, no friends</li>
            <li>· 90 km domicile (Aadhaar verification)</li>
            <li>· Physical Club Social card on the day</li>
            <li>· Once-per-month per-club cap</li>
            <li>· The club&apos;s own house rules (dress code, hours, etc.)</li>
          </ul>
        </div>
      )}

      {/* v3.32 — Itinerary plan card (when Mira composed days). */}
      {itineraryDays > 0 && (
        <div
          data-testid="basket-itinerary-summary"
          style={{
            padding: "0.7rem 0.85rem",
            marginBottom: (items.length > 0 || hasLineItems) ? "0.85rem" : "0.4rem",
            borderRadius: "var(--lc-radius-sm)",
            background: "rgba(15,61,46,0.03)",
            border: "1px solid rgba(15,61,46,0.12)",
          }}
        >
          <div
            className="lc-text-display"
            style={{
              fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif",
              fontSize: "0.85rem",
              fontWeight: 500,
              color: "var(--lc-text-primary)",
              lineHeight: 1.4,
              marginBottom: 4,
            }}
          >
            {itineraryPlan?.title || `${itineraryDays}-day itinerary`}
          </div>
          {itineraryPlan?.date_range_label && (
            <div
              className="lc-text-body"
              style={{ fontSize: "0.72rem", color: "var(--lc-text-secondary)", fontStyle: "italic" }}
            >
              {itineraryPlan.date_range_label}
            </div>
          )}
        </div>
      )}

      {/* v3.46.18 — Phase 1b line-items list (preferred grouping).
          Each row = one ask (one BriefItem). When line-items are
          available, they're the primary structure of the brief — venues
          from the flat basket are grouped under their matching line-item.
          Falls back to the flat-list render path below when no line-items
          have been computed yet (pre-Phase-1b cached responses). */}
      {hasLineItems && (
        <>
          <div
            data-testid="basket-selections-eyebrow"
            style={{
              fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif",
              fontStyle: "italic",
              fontSize: "0.74rem",
              color: "var(--lc-accent, #C9973A)",
              letterSpacing: "0.02em",
              marginBottom: 6,
              lineHeight: 1.35,
            }}
          >
            What we&apos;re arranging for you
          </div>
          <div
            data-testid="basket-line-items-list"
            style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}
          >
            {lineItems.map((li, idx) => (
              <BasketLineItemRow
                key={li.item_id || `${li.pillar}-${idx}`}
                lineItem={li}
                basket={items}
                idx={idx}
                removeFromBasket={removeFromBasket}
              />
            ))}
          </div>
        </>
      )}

      {/* Back-compat — flat basket list when no brief_items present
          (legacy turns / cached responses). Phase 1b line-items above
          are the preferred render; this branch keeps older sessions
          working without a hot reload. */}
      {!hasLineItems && items.length > 0 && (
        <>
          <div
            data-testid="basket-selections-eyebrow"
            style={{
              fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif",
              fontStyle: "italic",
              fontSize: "0.74rem",
              color: "var(--lc-accent, #C9973A)",
              letterSpacing: "0.02em",
              marginBottom: 6,
              lineHeight: 1.35,
            }}
          >
            Items you have chosen
          </div>
        <div
          data-testid="basket-items-list"
          style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}
        >
          {items.map((it, idx) => {
            const locationLine = [it.area, it.city].filter(Boolean).join(" · ");
            return (
              <div
                key={`${it.kind}-${it.name}-${idx}`}
                data-testid={`basket-item-${idx}`}
                data-kind={it.kind}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                  padding: "0.55rem 0.7rem",
                  borderRadius: "var(--lc-radius-sm)",
                  background: "var(--lc-bg-elevated)",
                  border: "1px solid var(--lc-border-soft)",
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    className="lc-text-body"
                    style={{
                      fontSize: "0.83rem",
                      fontWeight: 500,
                      color: "var(--lc-text-primary)",
                      lineHeight: 1.3,
                    }}
                  >
                    {it.name}
                  </div>
                  {locationLine && (
                    <div
                      className="lc-text-body"
                      style={{
                        fontSize: "0.7rem",
                        color: "var(--lc-text-muted)",
                        marginTop: 1,
                        lineHeight: 1.35,
                      }}
                    >
                      {locationLine}
                    </div>
                  )}
                  <div style={{ marginTop: 5 }}>
                    <ProvenanceTag item={it} compact />
                  </div>
                </div>
                {removeFromBasket && (
                  <button
                    onClick={() => removeFromBasket(it)}
                    data-testid={`basket-item-remove-${idx}`}
                    aria-label={`Remove ${it.name} from your brief`}
                    title="Remove from brief"
                    style={{
                      background: "transparent",
                      border: "1px solid var(--lc-border-soft)",
                      color: "var(--lc-text-muted)",
                      fontFamily: "var(--lc-font-body)",
                      fontSize: "0.68rem",
                      letterSpacing: "0.04em",
                      padding: "3px 8px",
                      borderRadius: 999,
                      cursor: "pointer",
                      flex: "0 0 auto",
                      transition: "all 160ms ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = "var(--lc-error, #8B2635)";
                      e.currentTarget.style.borderColor = "var(--lc-error, #8B2635)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = "var(--lc-text-muted)";
                      e.currentTarget.style.borderColor = "var(--lc-border-soft)";
                    }}
                  >
                    Remove
                  </button>
                )}
              </div>
            );
          })}
        </div>
        </>
      )}

      {/* v3.40 — Identity capture moved into the brief panel. Optional
          name/email/WhatsApp surfaced exactly when the member is about
          to send the brief — replaces the dismissed empty-state banner
          (v3.39). Soft-progressive doctrine per founder PRD v3.37.

          F18 (3 Jun 2026 PM late · FIR-20260603-008 · binding): the
          contact-capture block MUST NOT appear during gathering. It
          surfaces only when:
            (a) the brief is ready to hand off (allLineItemsSatisfied
                OR handoff_moment), OR
            (b) the message context has urgent/human-required signals
                (handled at the brain layer via captured_fields), OR
            (c) the member has typed contact info already (a soft
                "I'm here" signal — preserve their input).
          Otherwise the block is hidden during gathering so Mira's
          discovery feels like a real Concierge®, not a form. */}
      {(() => {
        const _handoffReady = allLineItemsSatisfied || handoffMoment;
        const _contactProvided = Boolean(
          (window.localStorage?.getItem("lc_visitor_name") || "").trim()
          || (window.localStorage?.getItem("lc_visitor_email") || "").trim()
          || (window.localStorage?.getItem("lc_visitor_phone") || "").trim()
        );
        const _urgentBrief = Boolean(
          lineItems.find((li) => {
            const cf = li?.captured_fields || {};
            return (
              (cf.urgency && String(cf.urgency).toLowerCase().includes("urgent"))
              || (li?.pillar === "Emergency")
              || (li?.pillar === "Care" && cf.urgency)
            );
          })
        );
        const _showContactCapture = _handoffReady || _contactProvided || _urgentBrief;
        return _showContactCapture ? <BriefIdentityFields /> : null;
      })()}

      <button
        className="lc-btn-primary lc-btn-primary--forest"
        style={{
          width: "100%",
          justifyContent: "center",
          padding: "0.85rem 1rem",
          fontSize: "0.875rem",
          opacity: sendDisabledFinal && submitState.status !== "sending" ? 0.6 : 1,
          cursor: sendDisabledFinal ? "not-allowed" : "pointer",
        }}
        onClick={handleSendClick}
        // 9 Feb 2026 doctrine fix · the button is no longer gated by
        // missing slot fields. Only "sending" and "contact missing"
        // block it. Missing-slot state surfaces as a non-blocking
        // hedge chip below the button.
        disabled={submitState.status === "sending"}
        aria-disabled={sendDisabledFinal}
        data-testid="basket-send-btn"
        data-send-gated={sendDisabledFinal && submitState.status !== "sending" ? "true" : "false"}
        title={
          sendBlockedByMissingContact
            ? "Add a WhatsApp number or email so the Concierge® team can reach you back."
            : "Send the assembled brief to the Concierge® team"
        }
      >
        {sendButtonLabel}
      </button>

      {/* 9 Feb 2026 honest hedge chip · founder doctrine fix.
         When the conversation has open slots, we DON'T block the
         send · we say so honestly under the button instead. The
         desk picks up partial briefs and asks the rest. */}
      {hasUnsatisfiedSlots && submitState.status !== "sending" && !sendBlockedByMissingContact ? (
        <div
          data-testid="basket-send-hedge"
          className="lc-text-body"
          style={{
            fontSize: "0.72rem",
            color: "var(--lc-ink-3, #686868)",
            marginTop: 8,
            lineHeight: 1.45,
            fontStyle: "italic",
          }}
        >
          The Concierge® will confirm
          {_firstNamedMissing ? (
            <> · {_humaniseField(_firstNamedMissing)}</>
          ) : null}
          {unsatItems.length > 1 && _firstNamedMissing ? (
            <> + {unsatItems.length - 1} more detail{unsatItems.length - 1 === 1 ? "" : "s"}</>
          ) : null}
          {" "}before sending.
        </div>
      ) : null}
      {submitState.status === "error" && (
        <div className="lc-text-body" style={{ fontSize: "0.72rem", color: "var(--lc-error)", marginTop: 8, fontStyle: "italic" }}>
          {submitState.message}
        </div>
      )}
    </div>
  );
}


// ── v3.46.18 · Phase 1b line-item row ──────────────────────────────────
// One BriefItem renders here. Shape:
//
//   ┌────────────────────────────────────────────────────────────┐
//   │ Dinner for 4 in Mumbai                       Remove ✕      │
//   │ Dine · Friday                                              │
//   │ ✓ city  ✓ date  ✓ party_size  ◯ time  ◯ dietary           │
//   │ ┌── Attached venues ─────────────────────────────────────┐│
//   │ │ Bombay Canteen · Mumbai · verified ✓                   ││
//   │ │ The Bombay Canteen · Mumbai · verified ✓               ││
//   │ └────────────────────────────────────────────────────────┘│
//   └────────────────────────────────────────────────────────────┘
//
// When the line-item has no attached venue AND is_satisfied = false:
//   ┌────────────────────────────────────────────────────────────┐
//   │ Car for arrival                              Remove ✕      │
//   │ Move                                                       │
//   │ ✓ pickup  ◯ drop  ◯ time                                  │
//   │ "Mira is arranging the details"  (italic, muted)           │
//   └────────────────────────────────────────────────────────────┘
function BasketLineItemRow({ lineItem, basket, idx, removeFromBasket }) {
  const li = lineItem || {};
  const captured = li.captured_fields || {};
  const missing = Array.isArray(li.missing_fields) ? li.missing_fields : [];
  // v3.46.22 · founder watching-day batch P0:
  //   Optional fields (e.g. Dine.dietary) appear in the row for
  //   transparency but render with a dash glyph — never the ◯ that
  //   reads as a missing blocker. Silence on these is a valid answer.
  const optionalMissing = Array.isArray(li.optional_missing_fields)
    ? li.optional_missing_fields
    : [];
  const allFields = [
    ...Object.keys(captured),
    ...missing,
    ...optionalMissing,
  ];
  const isSat = !!li.is_satisfied;

  // Attach venues from the flat basket whose name matches a venue
  // candidate's name, OR whose pillar matches the line-item's pillar.
  // Best-effort match — basket items don't always carry pillar tags.
  const venueCandidateName = (li.venue_candidate && (li.venue_candidate.name || li.venue_candidate.title)) || null;
  const attachedVenues = (basket || []).filter((bi) => {
    if (!bi) return false;
    if (venueCandidateName && bi.name === venueCandidateName) return true;
    if (li.pillar && bi.pillar_name && bi.pillar_name === li.pillar) return true;
    return false;
  });

  const fieldPill = (name, state) => {
    // v3.46.22: state ∈ "captured" | "missing" | "optional".
    // Optional fields show a dash glyph and dimmed text — they appear
    // for transparency but NEVER read as a blocker.
    const captured = state === "captured";
    const optional = state === "optional";
    const glyph = captured ? "✓" : (optional ? "–" : "◯");
    const color = captured
      ? "var(--lc-text-primary, #0F3D2E)"
      : "var(--lc-text-muted)";
    const opacity = captured ? 0.92 : (optional ? 0.4 : 0.55);
    return (
      <span
        key={`${name}-${state}`}
        data-field={name}
        data-captured={captured ? "true" : "false"}
        data-optional={optional ? "true" : "false"}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 3,
          fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif",
          fontStyle: "italic",
          fontSize: "0.66rem",
          letterSpacing: "0.01em",
          color,
          opacity,
        }}
      >
        <span aria-hidden="true">{glyph}</span>
        <span>{name.replace(/_/g, " ")}{optional ? " (if any)" : ""}</span>
      </span>
    );
  };

  return (
    <div
      data-testid={`basket-line-item-${idx}`}
      data-pillar={li.pillar}
      data-satisfied={isSat ? "true" : "false"}
      style={{
        padding: "0.7rem 0.85rem",
        borderRadius: "var(--lc-radius-sm)",
        background: "var(--lc-bg-elevated)",
        border: `1px solid ${isSat ? "var(--lc-border-soft)" : "rgba(201,151,58,0.45)"}`,
        opacity: isSat ? 1 : 0.95,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            className="lc-text-body"
            style={{
              fontSize: "0.85rem",
              fontWeight: 500,
              color: "var(--lc-text-primary)",
              lineHeight: 1.3,
            }}
            data-testid={`line-item-display-name-${idx}`}
          >
            {li.display_name || li.pillar}
          </div>
          {li.pillar && (
            <div
              className="lc-text-body"
              style={{
                fontSize: "0.68rem",
                color: "var(--lc-text-muted)",
                letterSpacing: "0.04em",
                marginTop: 1,
                textTransform: "uppercase",
              }}
            >
              {li.pillar}
            </div>
          )}
        </div>
        {removeFromBasket && attachedVenues.length > 0 && (
          <button
            onClick={() => attachedVenues.forEach((v) => removeFromBasket(v))}
            data-testid={`basket-line-item-remove-${idx}`}
            aria-label={`Remove ${li.display_name || li.pillar} from your brief`}
            title="Remove this ask from the brief"
            style={{
              background: "transparent",
              border: "1px solid var(--lc-border-soft)",
              color: "var(--lc-text-muted)",
              fontFamily: "var(--lc-font-body)",
              fontSize: "0.66rem",
              letterSpacing: "0.04em",
              padding: "3px 8px",
              borderRadius: 999,
              cursor: "pointer",
              flex: "0 0 auto",
              transition: "all 160ms ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = "var(--lc-error, #8B2635)";
              e.currentTarget.style.borderColor = "var(--lc-error, #8B2635)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = "var(--lc-text-muted)";
              e.currentTarget.style.borderColor = "var(--lc-border-soft)";
            }}
          >
            Remove
          </button>
        )}
      </div>

      {/* Phase 2.C.2 · Tier-1B whisper inline · the founder's "Mira
          speaks with the desk's voice" doctrine, on screen. When
          the brain's brief_item is backed by a render-eligible
          row in `table_archive_overlays`, we surface the venue
          name + the curated whisper italic, with a "Confirmed by
          Concierge®" mini-badge. Honest absence by design: when
          the overlay isn't there, this block doesn't render.
          The right pane and the in-conversation card BOTH read
          this same data (no parallel surface drift). */}
      {li.venue_candidate && li.venue_candidate.concierge_whisper && (
        <div
          data-testid={`brief-line-archive-whisper-${idx}`}
          data-archive-status={li.archive_status}
          style={{
            marginTop: 7,
            paddingTop: 7,
            borderTop: "1px dashed rgba(15,61,46,0.12)",
          }}
        >
          {li.venue_candidate.name && (
            <div
              style={{
                fontSize: "0.78rem",
                fontWeight: 500,
                color: "var(--lc-text-primary, #0F3D2E)",
                lineHeight: 1.3,
              }}
              data-testid={`brief-line-venue-name-${idx}`}
            >
              {li.venue_candidate.name}
            </div>
          )}
          <div
            style={{
              fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif",
              fontStyle: "italic",
              fontSize: "0.78rem",
              color: "rgba(15,61,46,0.78)",
              lineHeight: 1.45,
              marginTop: 3,
            }}
            data-testid={`brief-line-concierge-whisper-${idx}`}
          >
            {li.venue_candidate.concierge_whisper}
          </div>
          <div
            style={{
              marginTop: 5,
              fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif",
              fontSize: "0.62rem",
              fontStyle: "italic",
              letterSpacing: "0.05em",
              color: "rgba(15,61,46,0.55)",
            }}
            data-testid={`brief-line-concierge-confirmed-badge-${idx}`}
          >
            · confirmed by Concierge®
          </div>
        </div>
      )}

      {/* Field pills — captured (✓ filled) and missing (◯ outline) */}
      {allFields.length > 0 && (
        <div
          data-testid={`line-item-fields-${idx}`}
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            marginTop: 6,
            paddingTop: 6,
            borderTop: "1px dashed var(--lc-border-soft)",
          }}
        >
          {Object.keys(captured).map((k) => fieldPill(k, "captured"))}
          {missing.map((k) => fieldPill(k, "missing"))}
          {optionalMissing.map((k) => fieldPill(k, "optional"))}
        </div>
      )}

      {/* Attached venues OR muted placeholder when the line-item carries
          no venue candidate. The muted line is the honest "in progress"
          register — the brief shows the ask without pretending it's
          already chosen. */}
      {attachedVenues.length > 0 ? (
        <div
          data-testid={`line-item-venues-${idx}`}
          style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}
        >
          {attachedVenues.map((v, vi) => {
            const locationLine = [v.area, v.city].filter(Boolean).join(" · ");
            return (
              <div
                key={`${v.name}-${vi}`}
                data-testid={`line-item-venue-${idx}-${vi}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "4px 8px",
                  borderRadius: "var(--lc-radius-sm)",
                  background: "rgba(15,61,46,0.03)",
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: "0.76rem",
                      color: "var(--lc-text-primary)",
                      lineHeight: 1.3,
                    }}
                  >
                    {v.name}
                  </div>
                  {locationLine && (
                    <div
                      style={{
                        fontSize: "0.66rem",
                        color: "var(--lc-text-muted)",
                        lineHeight: 1.3,
                      }}
                    >
                      {locationLine}
                    </div>
                  )}
                </div>
                <ProvenanceTag item={v} compact />
              </div>
            );
          })}
        </div>
      ) : (
        !isSat && (
          <div
            data-testid={`line-item-placeholder-${idx}`}
            style={{
              marginTop: 8,
              fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif",
              fontStyle: "italic",
              fontSize: "0.74rem",
              color: "var(--lc-text-muted, #6E7B72)",
              lineHeight: 1.4,
            }}
          >
            {/* M5 fix · 9 Feb 2026 PM · founder doctrine.
                Previous label: "Mira is arranging the details" — literally
                false. Between turns Mira is WAITING, not arranging. The
                member is the one with the missing information. New label
                is honest about the state: a few more details from the
                member close the gap, then the brief is ready. */}
            A few details still to come
          </div>
        )
      )}
    </div>
  );
}

// v3.30 — SuggestedPanel rewritten to share the same 5-cluster layout
// as <ThinPillarStrip>. Members should never see the flat 19-name list
// anywhere — opening mat AND post-output strip both speak the editorial
// cluster register. Only the eyebrow copy differs (welcome-mat invites;
// thin-strip captions).
function SuggestedPanel({ activePillar, activePillarName, onPillarTap }) {
  const resolved = activePillarName || LEGACY_PILLAR_MAP[activePillar] || null;
  return (
    <div
      className="lc-card"
      style={{ padding: "1rem 1.15rem 1.1rem" }}
      data-testid="suggested-panel"
    >
      {/* v3.32 — D1 framing: replace bounded "Our Concierge® can help you
          with" with a limitless invitation. The 5 clusters become the
          visible tip of something much bigger, not a finite menu. */}
      <div className="lc-eyebrow mb-2" data-testid="welcome-mat-eyebrow">A few of the ways we begin</div>
      <div
        className="lc-text-display"
        data-testid="welcome-mat-framing"
        style={{
          fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif",
          fontStyle: "italic",
          fontSize: "0.92rem",
          color: "var(--lc-text-secondary, #4A5651)",
          lineHeight: 1.5,
          marginBottom: "1rem",
          letterSpacing: "0.005em",
        }}
      >
        There is little we cannot arrange — anywhere in the world, at any hour.
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
        {PILLAR_CLUSTERS.map((cluster) => {
          const clusterContainsActive = cluster.pillars.includes(resolved);
          return (
            <div
              key={cluster.key}
              data-testid={`welcome-cluster-${cluster.key}`}
              data-active={clusterContainsActive ? "true" : "false"}
            >
              <div
                style={{
                  fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif",
                  fontSize: "0.96rem",
                  fontWeight: clusterContainsActive ? 600 : 500,
                  color: clusterContainsActive
                    ? "var(--lc-accent, #C9973A)"
                    : "var(--lc-text-primary, #0F3D2E)",
                  letterSpacing: "0.005em",
                  lineHeight: 1.25,
                  marginBottom: "0.2rem",
                }}
              >
                {cluster.name}
              </div>
              <div
                style={{
                  fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif",
                  fontStyle: "italic",
                  // v3.32 — D1 font nudge: was 0.78rem → 0.85rem so breadth
                  // reads with confidence, not fine print.
                  fontSize: "0.85rem",
                  color: "var(--lc-text-muted, #94A099)",
                  lineHeight: 1.45,
                  letterSpacing: "0.01em",
                }}
              >
                {cluster.pillars.map((pillarLabel, i) => {
                  const isActive = resolved === pillarLabel;
                  return (
                    <React.Fragment key={pillarLabel}>
                      {i > 0 && (
                        <span style={{ color: "var(--lc-border, rgba(15,61,46,0.20))", margin: "0 0.32rem" }}>·</span>
                      )}
                      <button
                        type="button"
                        onClick={() => onPillarTap && onPillarTap(pillarLabel, pillarLabel)}
                        data-testid={`welcome-sub-${pillarLabel.toLowerCase().replace(/\s+/g, "-")}`}
                        data-active={isActive ? "true" : "false"}
                        style={{
                          background: "transparent",
                          border: "none",
                          padding: 0,
                          font: "inherit",
                          fontStyle: "italic",
                          color: isActive
                            ? "var(--lc-accent, #C9973A)"
                            : "var(--lc-text-muted, #94A099)",
                          fontWeight: isActive ? 600 : 400,
                          cursor: "pointer",
                          letterSpacing: "0.01em",
                          transition: "color 180ms ease",
                        }}
                        onMouseEnter={(e) => {
                          if (!isActive) e.currentTarget.style.color = "var(--lc-text-primary, #0F3D2E)";
                        }}
                        onMouseLeave={(e) => {
                          if (!isActive) e.currentTarget.style.color = "var(--lc-text-muted, #94A099)";
                        }}
                      >
                        {pillarLabel}
                      </button>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      {/* v3.32 — D1: open-door line after the clusters. The "things that
          fit no list at all" line tells the member the 5 clusters are an
          invitation, not a fence. */}
      <div
        className="lc-text-display"
        data-testid="welcome-mat-openend"
        style={{
          fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif",
          fontStyle: "italic",
          fontSize: "0.88rem",
          color: "var(--lc-text-secondary, #4A5651)",
          lineHeight: 1.5,
          marginTop: "1rem",
          paddingTop: "0.85rem",
          borderTop: "1px solid var(--lc-border-soft, rgba(15,61,46,0.10))",
          letterSpacing: "0.005em",
        }}
      >
        …and the things that fit no list at all. Just ask.
      </div>
    </div>
  );
}

// ─── v3.27 / v3.28 — ThinPillarStrip ────────────────────────────────────────
// "Pillars are context, not action" (brief §2.1). Once Mira has produced
// any output, the full pillar list retreats — and this thin strip carries
// the permanent reminder of breadth.
//
// v3.28: Now organised into 5 editorial clusters (Journeys · The Table ·
// Looking After · The Extraordinary · Counsel). Each cluster carries an
// always-visible faint italic sub-line of its pillar names — the breadth
// signal lands "spread before the eye" without requiring a tap. Each
// pillar name in the sub-line remains tappable so the member can pivot.
// Per founder direction (24 May 2026): the 4 internal pillars (Mira AI,
// Support, Engagement, Feedback) are kept in the brain but NEVER appear
// in any member-facing cluster.
//
// F18 (3 Jun 2026 PM late · FIR-20260603-008 · binding): when a pillar
// context is active, the full pillar directory is REPLACED with a small,
// contextual add-ons list tied to that pillar. The full directory reads
// like a site map, not a Concierge®. For a STAY flow: airport transfers ·
// child-friendly itinerary · dining reservations · celebration setup ·
// local experiences. For a DINE flow: flowers · cake · car · pre-dinner
// drinks · post-dinner experience. Unrelated pillars stay suppressed.
const F18_CONTEXTUAL_ADDONS = {
  Stay: [
    { label: "Airport transfers",       pillar: "Airport" },
    { label: "Child-friendly itinerary", pillar: "Enjoy" },
    { label: "Dining reservations",      pillar: "Dine" },
    { label: "Celebration setup",        pillar: "Unique" },
    { label: "Local experiences",        pillar: "Enjoy" },
  ],
  Dine: [
    { label: "Flowers on the table", pillar: "Unique" },
    { label: "Cake or dessert",      pillar: "Unique" },
    { label: "Car to the table",     pillar: "Move" },
    { label: "Pre-dinner drinks",    pillar: "Club" },
    { label: "Post-dinner experience", pillar: "Enjoy" },
  ],
  Travel: [
    { label: "Hotel for arrival",  pillar: "Stay" },
    { label: "Airport assistance", pillar: "Airport" },
    { label: "City driver",        pillar: "Move" },
    { label: "Dining reservations", pillar: "Dine" },
    { label: "Local experiences",   pillar: "Enjoy" },
  ],
  Move: [
    { label: "Airport assistance",   pillar: "Airport" },
    { label: "Hotel for the night",  pillar: "Stay" },
    { label: "Dining on the way",    pillar: "Dine" },
  ],
  Airport: [
    { label: "Onward driver",       pillar: "Move" },
    { label: "Hotel for the night", pillar: "Stay" },
    { label: "Lounge access",       pillar: "Club" },
  ],
  Enjoy: [
    { label: "Car to the venue",     pillar: "Move" },
    { label: "Pre-event dining",     pillar: "Dine" },
    { label: "Post-event drinks",    pillar: "Club" },
    { label: "Hotel for the night",  pillar: "Stay" },
  ],
  Care: [
    { label: "Care companion",      pillar: "Concierge" },
    { label: "Driver for the day",  pillar: "Move" },
    { label: "Specialist referral", pillar: "Referrals" },
    { label: "Paperwork support",   pillar: "Paperwork" },
  ],
  Fit: [
    { label: "Wellness retreat",  pillar: "Stay" },
    { label: "Healthy dining",    pillar: "Dine" },
    { label: "Recovery booking",  pillar: "Care" },
  ],
  Club: [
    { label: "Car to the venue",   pillar: "Move" },
    { label: "Pre-club dining",    pillar: "Dine" },
    { label: "Hotel for the night", pillar: "Stay" },
  ],
  Shop_Assist: [
    { label: "Delivery / courier", pillar: "Move" },
    { label: "Gift presentation",  pillar: "Unique" },
  ],
  Paperwork: [
    { label: "Notary appointment", pillar: "Concierge" },
    { label: "Translation",        pillar: "Referrals" },
    { label: "Specialist counsel", pillar: "Advisory" },
  ],
  Emergency: [
    { label: "Driver standing by",   pillar: "Move" },
    { label: "Hotel for tonight",    pillar: "Stay" },
    { label: "Specialist referral",  pillar: "Referrals" },
  ],
  Exclusive: [
    { label: "Private transfer",  pillar: "Move" },
    { label: "Sourced dining",    pillar: "Dine" },
    { label: "Quiet stay",        pillar: "Stay" },
  ],
  Unique: [
    { label: "Flowers / cake",      pillar: "Unique" },
    { label: "Memorable transport", pillar: "Move" },
    { label: "Photography",         pillar: "Referrals" },
  ],
  Advisory: [
    { label: "Counsel session",        pillar: "Concierge" },
    { label: "Discreet meeting space", pillar: "Stay" },
    { label: "Trusted referrals",      pillar: "Referrals" },
  ],
  Work: [
    { label: "Meeting space",   pillar: "Stay" },
    { label: "Business dining", pillar: "Dine" },
    { label: "Driver",          pillar: "Move" },
  ],
  Referrals: [
    { label: "Trusted specialists", pillar: "Concierge" },
    { label: "Paperwork support",   pillar: "Paperwork" },
  ],
};

function ThinPillarStrip({ activePillarName, onPillarTap }) {
  // F18 — contextual add-ons mode: only render when a pillar context is
  // active AND we have a curated add-on list for it. Otherwise fall
  // through to the full directory (idle state / no active pillar yet).
  const _addons = activePillarName && F18_CONTEXTUAL_ADDONS[activePillarName];
  if (_addons && _addons.length) {
    return (
      <div
        className="block"
        data-testid="thin-pillar-strip"
        data-mode="contextual-addons"
        data-active-pillar={activePillarName}
        style={{
          padding: "0.85rem 1rem 0.95rem",
          borderTop: "1px solid var(--lc-border-soft)",
          borderBottom: "1px solid var(--lc-border-soft)",
          background: "transparent",
        }}
      >
        <div
          style={{
            fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
            fontSize: "0.62rem",
            fontWeight: 700,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "var(--lc-text-muted, #94A099)",
            marginBottom: "0.55rem",
          }}
        >
          Also worth considering
        </div>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "0.45rem 0.65rem",
          }}
        >
          {_addons.map((addon, i) => (
            <button
              key={`${addon.pillar}-${i}`}
              type="button"
              data-testid={`f18-addon-${addon.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => onPillarTap && onPillarTap(addon.pillar, addon.label)}
              style={{
                background: "transparent",
                border: "1px solid var(--lc-border, rgba(15,61,46,0.20))",
                borderRadius: "999px",
                padding: "0.32rem 0.7rem",
                fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif",
                fontSize: "0.76rem",
                color: "var(--lc-text-secondary, #4A5853)",
                cursor: "pointer",
                letterSpacing: "0.01em",
              }}
            >
              {addon.label}
            </button>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div
      className="block"
      data-testid="thin-pillar-strip"
      data-mode="full-directory"
      style={{
        padding: "0.85rem 1rem 0.95rem",
        borderTop: "1px solid var(--lc-border-soft)",
        borderBottom: "1px solid var(--lc-border-soft)",
        background: "transparent",
      }}
    >
      <div
        style={{
          fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
          fontSize: "0.62rem",
          fontWeight: 700,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: "var(--lc-text-muted, #94A099)",
          marginBottom: "0.95rem",
        }}
      >
        We can also help with
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "0.85rem",
        }}
      >
        {PILLAR_CLUSTERS.map((cluster) => {
          const clusterContainsActive = cluster.pillars.includes(activePillarName);
          return (
            <div
              key={cluster.key}
              data-testid={`pillar-cluster-${cluster.key}`}
              data-active={clusterContainsActive ? "true" : "false"}
            >
              {/* Cluster name — Fraunces, upright, brass-accent only when active */}
              <div
                style={{
                  fontFamily:
                    "'Fraunces', 'Cormorant Garamond', Georgia, serif",
                  fontSize: "0.96rem",
                  fontWeight: clusterContainsActive ? 600 : 500,
                  color: clusterContainsActive
                    ? "var(--lc-accent, #C9973A)"
                    : "var(--lc-text-primary, #0F3D2E)",
                  letterSpacing: "0.005em",
                  lineHeight: 1.25,
                  marginBottom: "0.2rem",
                }}
              >
                {cluster.name}
              </div>
              {/* Sub-line: faint italic Fraunces, pillar names · separated.
                  Each name remains tappable to pivot the conversation. */}
              <div
                style={{
                  fontFamily:
                    "'Fraunces', 'Cormorant Garamond', Georgia, serif",
                  fontStyle: "italic",
                  fontSize: "0.78rem",
                  color: "var(--lc-text-muted, #94A099)",
                  lineHeight: 1.45,
                  letterSpacing: "0.01em",
                }}
              >
                {cluster.pillars.map((pillarLabel, i) => {
                  const isActive = activePillarName === pillarLabel;
                  return (
                    <React.Fragment key={pillarLabel}>
                      {i > 0 && (
                        <span style={{ color: "var(--lc-border, rgba(15,61,46,0.20))", margin: "0 0.32rem" }}>
                          ·
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => onPillarTap && onPillarTap(pillarLabel, pillarLabel)}
                        data-testid={`pillar-sub-${pillarLabel.toLowerCase().replace(/\s+/g, "-")}`}
                        data-active={isActive ? "true" : "false"}
                        style={{
                          background: "transparent",
                          border: "none",
                          padding: 0,
                          font: "inherit",
                          fontStyle: "italic",
                          color: isActive
                            ? "var(--lc-accent, #C9973A)"
                            : "var(--lc-text-muted, #94A099)",
                          fontWeight: isActive ? 600 : 400,
                          cursor: "pointer",
                          letterSpacing: "0.01em",
                          transition: "color 180ms ease",
                        }}
                        onMouseEnter={(e) => {
                          if (!isActive)
                            e.currentTarget.style.color =
                              "var(--lc-text-primary, #0F3D2E)";
                        }}
                        onMouseLeave={(e) => {
                          if (!isActive)
                            e.currentTarget.style.color =
                              "var(--lc-text-muted, #94A099)";
                        }}
                      >
                        {pillarLabel}
                      </button>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ─── v3.20 — ItineraryPlanPanel ────────────────────────────────────────────
// Renders a day-by-day plan as Day cards with morning / afternoon / evening
// slots. NEVER a flat shopping basket — this is the considered-arc surface.
// The Send-to-Concierge button on this panel packages the WHOLE plan as one
// coherent brief to the human team.
const TIME_OF_DAY_ORDER = ["morning", "afternoon", "evening", "night", "anytime"];
const TIME_OF_DAY_LABEL = {
  morning: "Morning",
  afternoon: "Afternoon",
  evening: "Evening",
  night: "Late night",
  anytime: "Any time",
};

function ItineraryPlanPanel({ plan, onRefine, onSendItinerary }) {
  if (!plan || !Array.isArray(plan.days) || plan.days.length === 0) return null;
  const days = plan.days;

  return (
    <div
      className="tdc-card lc-card"
      style={{
        padding: "var(--space-5)",
        background: "linear-gradient(180deg, rgba(15,61,46,0.04) 0%, transparent 60%)",
      }}
      data-testid="itinerary-plan-panel"
    >
      <div className="flex items-baseline justify-between" style={{ marginBottom: "var(--space-3)" }}>
        <div className="tdc-section-label tdc-section-label-gold" style={{ marginBottom: 0 }}>
          Your itinerary · {days.length} {days.length === 1 ? "day" : "days"}
        </div>
        <span
          style={{
            fontFamily: "var(--font-mono)", fontSize: 11,
            color: "var(--color-text-faint)", letterSpacing: "0.06em",
            textTransform: "uppercase",
          }}
        >
          composed by Mira®
        </span>
      </div>

      {plan.title && (
        <div
          className="lc-text-display"
          style={{
            fontFamily: "var(--font-serif)", fontSize: "1.25rem",
            color: "var(--lc-text-primary)", lineHeight: 1.3,
            marginBottom: plan.arc_note ? "var(--space-2)" : "var(--space-4)",
            fontWeight: 500,
          }}
          data-testid="itinerary-title"
        >
          {plan.title}
        </div>
      )}

      {plan.arc_note && (
        <p
          className="lc-text-body"
          style={{
            fontSize: "0.85rem", lineHeight: 1.55,
            color: "var(--lc-text-secondary)",
            marginBottom: "var(--space-5)", fontStyle: "italic",
          }}
          data-testid="itinerary-arc-note"
        >
          {plan.arc_note}
        </p>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-5)" }}>
        {days.map((day, i) => {
          const slots = [...(day.slots || [])].sort(
            (a, b) =>
              TIME_OF_DAY_ORDER.indexOf(a.time_of_day || "anytime") -
              TIME_OF_DAY_ORDER.indexOf(b.time_of_day || "anytime")
          );
          return (
            <div
              key={i}
              data-testid={`itinerary-day-${day.day_number}`}
              style={{
                paddingLeft: "var(--space-4)",
                borderLeft: "2px solid var(--lc-highlight)",
              }}
            >
              <div
                style={{
                  display: "flex", alignItems: "baseline",
                  gap: "var(--space-3)", marginBottom: "var(--space-3)",
                }}
              >
                <div
                  style={{
                    fontFamily: "var(--font-sans)", fontSize: "0.7rem",
                    fontWeight: 700, letterSpacing: "0.14em",
                    textTransform: "uppercase", color: "var(--lc-highlight)",
                  }}
                >
                  Day {day.day_number}
                </div>
                {day.date && (
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--color-text-faint)" }}>
                    {day.date}
                  </div>
                )}
                {day.label && (
                  <div
                    style={{
                      fontFamily: "var(--font-serif)", fontSize: "0.95rem",
                      color: "var(--lc-text-primary)", fontStyle: "italic",
                    }}
                  >
                    {day.label}
                  </div>
                )}
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
                {slots.length === 0 && (
                  <div style={{ fontSize: "0.8rem", color: "var(--color-text-faint)", fontStyle: "italic" }}>
                    Free day — at your own pace.
                  </div>
                )}
                {slots.map((slot, si) => (
                  <div
                    key={si}
                    data-testid={`itinerary-slot-${day.day_number}-${si}`}
                    style={{ display: "flex", gap: "var(--space-3)", alignItems: "flex-start" }}
                  >
                    <span
                      style={{
                        flex: "0 0 auto", minWidth: 76,
                        fontFamily: "var(--font-sans)", fontSize: 11,
                        fontWeight: 600, letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        color: "var(--color-text-muted)", paddingTop: 2,
                      }}
                    >
                      {TIME_OF_DAY_LABEL[slot.time_of_day] || "Any time"}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: "0.95rem", color: "var(--lc-text-primary)",
                          fontWeight: 500, lineHeight: 1.4,
                        }}
                      >
                        {slot.title}
                      </div>
                      {slot.venue_city && slot.venue_name && slot.venue_name !== slot.title && (
                        <div style={{ fontSize: "0.75rem", color: "var(--color-text-muted)", marginTop: 2 }}>
                          {slot.venue_name} · {slot.venue_city}
                        </div>
                      )}
                      {slot.note && (
                        <div
                          style={{
                            fontSize: "0.78rem", color: "var(--color-text-muted)",
                            marginTop: 4, lineHeight: 1.5, fontStyle: "italic",
                          }}
                        >
                          {slot.note}
                        </div>
                      )}
                      {slot.duration_hint && (
                        <div
                          style={{
                            fontSize: "0.7rem", color: "var(--color-text-faint)",
                            marginTop: 4, fontFamily: "var(--font-mono)",
                            letterSpacing: "0.04em",
                          }}
                        >
                          {slot.duration_hint}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div
        style={{
          marginTop: "var(--space-6)", display: "flex",
          flexWrap: "wrap", gap: "var(--space-2)",
        }}
        data-testid="itinerary-refine-row"
      >
        {[
          "Make day 2 quieter",
          "We're vegetarian",
          "Swap the cruise to day 3",
          "Add a wine tour",
          "Shorten the trip",
        ].map((label) => (
          <button
            key={label}
            type="button"
            className="lc-chip"
            style={{
              fontSize: "0.72rem", padding: "0.32rem 0.7rem",
              background: "transparent", border: "1px solid var(--lc-border)",
              color: "var(--lc-text-secondary)", borderRadius: 999,
              cursor: "pointer", fontStyle: "italic",
            }}
            onClick={() => onRefine && onRefine(label)}
          >
            {label}
          </button>
        ))}
      </div>

      <button
        type="button"
        data-testid="itinerary-send-btn"
        onClick={() => onSendItinerary && onSendItinerary()}
        className="lc-btn-primary lc-btn-primary--forest"
        style={{
          marginTop: "var(--space-5)", width: "100%",
          padding: "0.85rem 1.2rem", fontSize: "0.88rem",
          letterSpacing: "0.04em", textTransform: "uppercase", fontWeight: 600,
        }}
      >
        Send this itinerary to Concierge®
      </button>
    </div>
  );
}



function VenuesPanel({ venueHints, mentionedPlaces, onArrange, addToBasket, removeFromBasket, isInBasket, hasContent, phase, pillarName, isComposing }) {
  // v3.1 — venue_hints is the structured VenueHint list. archive_status drives badges.
  // mentionedPlaces remains for legacy back-compat (older Claude payloads).
  const cards = useMemo(() => {
    if (venueHints && venueHints.length > 0) {
      return venueHints.map((v) => ({
        name: v.name,
        city: v.city || "",
        area: v.area || "",
        blurb: v.blurb || "",
        category: v.category || "venue",
        seasonality: v.seasonality || null,
        pillar: v.pillar || null,
        // v3.14 — keep the full taxonomy fields so CardActionRow / resolver
        // downstream features can read them (subcategory drives flight vs
        // standard-ticket detection in mira_actions).
        pillar_name: v.pillar_name || v.pillar || null,
        subcategory: v.subcategory || null,
        state: v.state || null,
        country: v.country || null,
        in_cn_db: !!v.in_cn_db,
        archive_status: v.archive_status || (v.in_cn_db ? "verified_archive" : "unknown"),
        // v3.3 — Live Places enrichment passthrough
        places_verified: !!v.places_verified,
        place_id: v.place_id || null,
        formatted_address: v.formatted_address || null,
        rating: v.rating || null,
        user_ratings_total: v.user_ratings_total || null,
        image_url: v.image_url || null,
        photo_html_attribution: v.photo_html_attribution || null,
        website_url: v.website_url || null,
        // v3.14 — Category-aware action plan attached server-side by
        // mira_actions.resolve_card_actions. Without this, CardActionRow's
        // book-direct CTA never renders (directVisible gate stays false).
        actions: v.actions || null,
        _source: "venue_hint",
      }));
    }
    return (mentionedPlaces || []).map((p) => ({
      name: p.name,
      city: p.city || "",
      area: "",
      blurb: p.why_relevant || "",
      category: "venue",
      seasonality: null,
      pillar: p.pillar || null,
      pillar_name: p.pillar_name || p.pillar || null,
      subcategory: p.subcategory || null,
      state: p.state || null,
      country: p.country || null,
      in_cn_db: false,
      archive_status: "unknown",
      actions: p.actions || null,
      _source: "mentioned_place",
    }));
  }, [venueHints, mentionedPlaces]);

  const hasMentioned = cards.length > 0;
  const isGathering = phase === "gathering";

  // v3.12 — Choose card variant based on density + provenance.
  // Compact when 3+ archive cards (high-density Tier-1 browse). Full when
  // 1-2 cards OR mostly web/Places results (more detail to show per card).
  const archiveCount = cards.filter((c) => c.archive_status === "verified_archive").length;
  const useCompact = cards.length >= 3 && archiveCount >= 3;

  // v3.26 — pillar-aware copy. The right-pane label, eyebrow, and empty-
  // state placeholder all derive from the active pillar — a medical
  // companion is not a venue; an itinerary is not a pick.
  const _pillarCopy = (() => {
    const CARE = ["Care", "Advisory", "Work", "Family Care", "Documentation"];
    const TRAVEL = ["Stay", "Travel", "Escape", "Move", "Transport"];
    if (CARE.includes(pillarName)) {
      return {
        eyebrow: "Next steps",
        emptyState: "Your next steps will appear here once we've shaped the request.",
        whatMira: "What Mira recommends",
        miraCurated: "Mira's plan",
        noun: "step",
      };
    }
    if (TRAVEL.includes(pillarName)) {
      return {
        eyebrow: "Options",
        emptyState: "Options will appear here once we've shaped the journey.",
        whatMira: "What Mira would consider",
        miraCurated: "Mira's options",
        noun: "option",
      };
    }
    return {
      eyebrow: "Mira's picks",
      emptyState: "Picks will appear here once we've shaped what you're looking for.",
      whatMira: "What Mira would consider",
      miraCurated: "Mira curated",
      noun: "pick",
    };
  })();

  return (
    <div className="lc-card" style={{ padding: "1.25rem" }} data-testid="venues-panel" data-phase={phase || "curating"}>
      {/* Phase 1 — empty state: doctrinal "venues come after gathering" message */}
      {isGathering && !hasMentioned && (
        <div className="mb-5" data-testid="venues-empty-gathering">
          <div className="lc-eyebrow mb-3">{_pillarCopy.eyebrow}</div>
          {isComposing ? (
            // v3.46.3 — Brass pulse while Sonnet is still generating the
            // venue_hints JSON tail (the ~9s gap Phase 1.2 exposed once
            // prose streams faster than the rest of the JSON). Honest
            // status: "Mira is shaping your picks…" not an idle empty.
            <div
              data-testid="venues-pulse-status"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.6rem",
                padding: "1rem 0.25rem 1.25rem",
                borderTop: "1px solid var(--lc-border-soft)",
                borderBottom: "1px solid var(--lc-border-soft)",
                fontFamily: "var(--font-display, 'Fraunces', serif)",
                fontStyle: "italic",
                fontSize: "0.82rem",
                color: "var(--lc-text-secondary)",
                letterSpacing: "0.005em",
                animation: "lc-fade-in 220ms ease-out",
              }}
            >
              <span
                aria-hidden
                style={{
                  width: 8, height: 8, borderRadius: "50%",
                  background: "var(--lc-accent, #C9973A)",
                  boxShadow: "0 0 0 0 rgba(201,151,58,0.55)",
                  animation: "lc-pulse-dot 1.4s ease-in-out infinite",
                  flexShrink: 0,
                }}
              />
              <span>Mira is shaping your picks…</span>
            </div>
          ) : (
            <div
              className="lc-text-display"
              style={{
                fontSize: "0.82rem",
                fontStyle: "italic",
                color: "var(--lc-text-muted)",
                lineHeight: 1.6,
                padding: "1rem 0.25rem 1.25rem",
                borderTop: "1px solid var(--lc-border-soft)",
                borderBottom: "1px solid var(--lc-border-soft)",
              }}
            >
              {_pillarCopy.emptyState}
            </div>
          )}
        </div>
      )}

      {/* VENUE HINTS — surfaced live from Mira's reply (Phase 2 only) */}
      {hasMentioned && (
        <div className="mb-5">
          <div className="flex items-baseline justify-between mb-1">
            <div className="tdc-section-label tdc-section-label-gold" style={{ marginBottom: 0 }}>
              {archiveCount >= 3 ? _pillarCopy.miraCurated : _pillarCopy.whatMira}
            </div>
            <span
              className="tdc-section-label"
              style={{
                marginBottom: 0,
                fontSize: "11px",
                color: "var(--color-text-faint)",
                fontFamily: "var(--font-mono)",
              }}
            >
              {archiveCount > 0
                ? `${archiveCount} curated · ${cards.length - archiveCount} suggested`
                : "tap Choose to add to your brief"}
            </span>
          </div>
          {/* v3.46.14 + 4 Jun 2026 AM founder fix — eyebrow honours
              Mira's-Pick protected-language rule. Only say "Mira's picks"
              when at least one card is verified_archive. Otherwise say
              "Candidates the Concierge® team will verify" — same intent
              (these are the venues you'd add to your brief), honest about
              their state. */}
          <div
            data-testid="mira-picks-eyebrow"
            style={{
              fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif",
              fontStyle: "italic",
              fontSize: "0.74rem",
              color: "var(--lc-accent, #C9973A)",
              letterSpacing: "0.02em",
              marginBottom: 10,
              lineHeight: 1.4,
            }}
          >
            {archiveCount > 0 ? (
              <>Mira&apos;s picks for you — tap <strong style={{ fontStyle: "normal", fontWeight: 600 }}>Choose</strong> to add to your brief</>
            ) : (
              <>Candidates the Concierge&reg; team will verify — tap <strong style={{ fontStyle: "normal", fontWeight: 600 }}>Choose</strong> to add to your brief</>
            )}
          </div>
          <div className={useCompact ? "space-y-2" : "space-y-3"}>
            {cards.map((p, idx) => {
              // v3.29 — Cards fade/rise into place. Stagger by ~90ms
              // each so 3-5 cards land "one by one" not all at once.
              const riseClass = `lc-card-rise-${Math.min(5, idx + 1)}`;
              return useCompact ? (
                <div key={`${p.name}-${idx}`} className={riseClass}>
                  <VenueHintCardCompact
                    place={p}
                    onArrange={onArrange}
                    idx={idx}
                    addToBasket={addToBasket}
                    removeFromBasket={removeFromBasket}
                    isInBasket={isInBasket}
                  />
                </div>
              ) : (
                <div key={`${p.name}-${idx}`} className={riseClass}>
                  <VenueHintCard
                    place={p}
                    onArrange={onArrange}
                    idx={idx}
                    addToBasket={addToBasket}
                    removeFromBasket={removeFromBasket}
                    isInBasket={isInBasket}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* v3.10 — SEED_VENUES block removed. The right panel never displays
          hard-coded placeholder venues. Real options come exclusively from
          venue_hints / product_hints in the brain response. During Phase 1
          gathering this region stays empty; the Brief Builder above carries
          the conversational weight. Real Tier-1 archive matches will surface
          here via the broadcast-query endpoint once city + pillar are known. */}
    </div>
  );
}

/**
 * VenueHintCardCompact — v3.12 high-density variant for archive (Tier-1) results.
 * Used in VenuesPanel when ≥ 3 verified_archive cards stack together. Single-row
 * layout: 60×60 hero · name + address · badge · basket toggle. Tap "ask Mira"
 * to deep-dive into the full PlaceCard pattern.
 */
function VenueHintCardCompact({ place, onArrange, idx, addToBasket, removeFromBasket, isInBasket }) {
  const firstLetter = (place.name || "?").trim().charAt(0).toUpperCase();
  const status = place.archive_status || (place.in_cn_db ? "verified_archive" : "unknown");
  const cardOpacity = status === "mira_imagined" ? 0.78 : status === "web_found" ? 0.95 : 1;
  // v3.32 — Rich basket item (all provenance, address, URLs, ratings, etc.)
  const basketItem = buildBasketItemFromCard(place, "venue");
  const added = isInBasket && isInBasket(basketItem);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.85rem",
        padding: "0.6rem 0.75rem",
        borderRadius: "var(--lc-radius-sm)",
        background: "var(--lc-bg-elevated)",
        border: added ? "1.5px solid var(--lc-highlight)" : "1px solid var(--lc-border-soft)",
        opacity: cardOpacity,
        transition: "all var(--lc-dur) var(--lc-ease)",
      }}
      data-testid={`venue-hint-compact-${idx}`}
    >
      {/* Square hero, 56×56 */}
      <div
        style={{
          flexShrink: 0,
          width: 56,
          height: 56,
          borderRadius: "var(--lc-radius-sm)",
          background: place.image_url
            ? `center / cover no-repeat url("${place.image_url}"), var(--lc-cream)`
            : "linear-gradient(135deg, rgba(184,154,78,0.18), rgba(247,242,232,1))",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          border: "1px solid var(--lc-border-soft)",
        }}
      >
        {!place.image_url && (
          <span
            className="lc-text-display"
            style={{ fontSize: "1.5rem", color: "var(--lc-highlight)", opacity: 0.7, fontStyle: "italic" }}
          >
            {firstLetter}
          </span>
        )}
      </div>

      {/* Body — title + meta + badge */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          className="lc-text-body"
          style={{
            fontSize: "0.84rem",
            fontWeight: 500,
            color: "var(--lc-text-primary)",
            lineHeight: 1.25,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {place.name}
        </div>
        <div
          className="lc-text-mono lc-text-muted"
          style={{
            fontSize: "0.62rem",
            marginTop: 2,
            letterSpacing: "0.04em",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {[place.area, place.city].filter(Boolean).join(" · ")}
        </div>
        <div style={{ marginTop: 5 }}>
          <ArchiveStatusBadge status={status} />
        </div>
        {/* v3.46.18 — R28.7 temporarily-closed honest note (compact card). */}
        {place.operational_note && place.op_status === "closed_temporarily" && (
          <div
            data-testid={`operational-note-${idx}`}
            style={{
              marginTop: 5,
              fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif",
              fontStyle: "italic",
              fontSize: "0.7rem",
              color: "var(--lc-accent, #C9973A)",
              lineHeight: 1.35,
            }}
          >
            {place.operational_note}
          </div>
        )}
      </div>

      {/* Right rail — basket + ask Concierge® (CardActionRow renders the
          R32-clean layout: Choose / Visit Website / Ask Concierge®). */}
      <div style={{ flexShrink: 0, display: "flex", flexDirection: "column", gap: 6, alignItems: "stretch", minWidth: 140 }}>
        <CardActionRow
          place={place}
          idx={`compact-${idx}`}
          basketItem={basketItem}
          added={added}
          addToBasket={addToBasket}
          removeFromBasket={removeFromBasket}
          onArrange={onArrange}
          brief={null}
          compact={true}
        />
      </div>
    </div>
  );
}

/**
 * VenueHintCard — Pattern A card. One per place Mira named in her reply.
 * v3 schema: place = { name, city, area, blurb, category, seasonality, pillar, in_cn_db }
 * (back-compat: also accepts legacy why_relevant via blurb in VenuesPanel mapper)
 */
function VenueHintCard({ place, onArrange, idx, addToBasket, removeFromBasket, isInBasket }) {
  const firstLetter = (place.name || "?").trim().charAt(0).toUpperCase();
  const angle = 100 + (idx * 37) % 60;
  const locationLine = [place.area, place.city].filter(Boolean).join(" · ");
  // v3.12 — Tier-3 (mira_imagined) cards render slightly faded with a
  // "Concierge® will source" stamp; Tier-2 web_found cards 0.95; Tier-1 + 2
  // verified routes stay full opacity. Honest provenance signal at a glance.
  const status = place.archive_status || (place.in_cn_db ? "verified_archive" : "unknown");
  const cardOpacity = status === "mira_imagined" ? 0.78 : status === "web_found" ? 0.95 : 1;
  const showSourceStamp = status === "mira_imagined";
  // v3.32 — Rich basket item (provenance, place_id, ratings, actions, etc.)
  const basketItem = buildBasketItemFromCard(place, "venue");
  const added = isInBasket && isInBasket(basketItem);

  return (
    <div
      style={{
        borderRadius: "var(--lc-radius-md)",
        overflow: "hidden",
        background: "var(--lc-bg-elevated)",
        border: added ? "1.5px solid var(--lc-highlight)" : "1px solid var(--lc-highlight)",
        boxShadow: "var(--lc-shadow-soft)",
        transition: "transform var(--lc-dur) var(--lc-ease), box-shadow var(--lc-dur) var(--lc-ease), opacity var(--lc-dur) var(--lc-ease)",
        opacity: cardOpacity,
      }}
      data-testid={`venue-hint-${idx}`}
    >
      {/* Hero — real Google Places photo (Cloudinary cached) when verified,
           otherwise editorial cream/brass gradient with first-letter cap */}
      <div
        style={{
          height: 132,
          background: place.image_url
            ? `center / cover no-repeat url("${place.image_url}"), var(--lc-cream)`
            : `linear-gradient(${angle}deg, rgba(184,154,78,0.18) 0%, rgba(27,45,82,0.10) 70%, rgba(247,242,232,0) 100%), var(--lc-cream)`,
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {!place.image_url && (
          <div
            className="lc-text-display"
            style={{
              fontSize: "3rem",
              color: "var(--lc-highlight)",
              fontWeight: 400,
              letterSpacing: "-0.02em",
              fontStyle: "italic",
              opacity: 0.7,
              lineHeight: 1,
            }}
          >
            {firstLetter}
          </div>
        )}
        {/* v3.12 — Tier-3 stamp: corner-bottom "Concierge® will source" overlay,
            shown only for mira_imagined cards. Member sees at a glance that
            we'll source rather than confirm an existing record. */}
        {showSourceStamp && (
          <div
            style={{
              position: "absolute",
              bottom: 8,
              left: 10,
              fontSize: "0.6rem",
              color: "var(--lc-accent)",
              background: "rgba(27,45,82,0.88)",
              padding: "3px 8px",
              borderRadius: 999,
              fontFamily: "var(--lc-font-body)",
              letterSpacing: "0.06em",
              fontStyle: "italic",
              fontWeight: 500,
            }}
            data-testid="tier3-source-stamp"
          >
            Concierge® will source
          </div>
        )}
        {place.seasonality && (
          <div
            style={{
              position: "absolute",
              top: 8,
              left: 10,
              fontSize: "0.6rem",
              color: "var(--lc-accent)",
              background: "rgba(184,154,78,0.92)",
              padding: "2px 7px",
              borderRadius: 999,
              fontFamily: "var(--lc-font-body)",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              fontWeight: 500,
            }}
          >
            {place.seasonality}
          </div>
        )}
        {/* Mira's Pick badge — verified_archive ONLY (founder doctrine,
            4 Jun 2026 AM · binding). Mira's Pick and Concierge® to verify
            are mutually exclusive. For all non-verified_archive cards
            (verified_places / web_found / mira_imagined / unknown) the
            badge is suppressed and only the "Concierge® to verify" chip
            renders. */}
        {place.archive_status === "verified_archive" && (
          <div
            data-testid="mira-pick-badge"
            style={{
              position: "absolute",
              top: 8,
              right: 10,
              fontSize: "0.6rem",
              color: "var(--lc-text-muted)",
              background: "rgba(247,242,232,0.92)",
              padding: "2px 6px",
              borderRadius: 999,
              fontFamily: "var(--lc-font-body)",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            Mira's pick
          </div>
        )}
      </div>

      {/* Card body */}
      <div style={{ padding: "0.85rem 1rem 0.95rem" }}>
        <div className="lc-text-body" style={{ fontSize: "0.9rem", fontWeight: 500, color: "var(--lc-text-primary)", lineHeight: 1.3 }}>
          {place.name}
        </div>
        {locationLine && (
          <div className="lc-text-mono lc-text-muted" style={{ fontSize: "0.65rem", marginTop: 3, letterSpacing: "0.04em" }}>
            {locationLine}
          </div>
        )}
        {place.rating && (
          <div
            className="lc-text-mono"
            style={{
              fontSize: "0.62rem",
              marginTop: 4,
              letterSpacing: "0.04em",
              color: "var(--lc-text-secondary)",
            }}
            data-testid="venue-rating"
          >
            ★ {place.rating.toFixed(1)}
            {place.user_ratings_total ? ` · ${place.user_ratings_total.toLocaleString()} reviews` : ""}
          </div>
        )}
        {/* v3.1 — provenance badge: tells the member what we know about this place */}
        <div style={{ marginTop: 6 }}>
          <ArchiveStatusBadge status={place.archive_status || (place.in_cn_db ? "verified_archive" : "unknown")} />
        </div>
        {/* v3.4 — mini-map thumbnail (only when Google Places gave us lat/lng) */}
        {place.map_url && (
          <div
            style={{
              marginTop: 10,
              borderRadius: "var(--lc-radius-sm)",
              overflow: "hidden",
              border: "1px solid var(--lc-border-soft)",
              height: 88,
              background: `center / cover no-repeat url("${place.map_url}"), var(--lc-cream)`,
            }}
            data-testid={`venue-mini-map-${idx}`}
            aria-label={`Map of ${place.name}`}
            title={place.formatted_address || place.name}
          />
        )}
        {place.blurb && (
          <div
            className="lc-text-display"
            style={{
              fontSize: "0.78rem",
              fontStyle: "italic",
              color: "var(--lc-text-secondary)",
              marginTop: 8,
              lineHeight: 1.5,
            }}
          >
            {place.blurb}
          </div>
        )}
        {/* v3.46.18 — R28.7 temporarily-closed honest note (full card).
            Quiet luxury register: amber italic Fraunces, never red, never
            "⚠️ CLOSED" — woven gently into the voice. The desk verifies
            reopening before anything is set. Doctrine in
            /app/memory/R28_7_OPERATIONAL_STATUS_DOCTRINE.md. */}
        {place.operational_note && place.op_status === "closed_temporarily" && (
          <div
            data-testid={`operational-note-${idx}`}
            style={{
              marginTop: 10,
              padding: "0.5rem 0.75rem",
              fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif",
              fontStyle: "italic",
              fontSize: "0.78rem",
              color: "var(--lc-accent, #C9973A)",
              background: "rgba(201,151,58,0.06)",
              border: "1px solid rgba(201,151,58,0.25)",
              borderRadius: "var(--lc-radius-sm)",
              lineHeight: 1.45,
            }}
          >
            {place.operational_note}
          </div>
        )}
        <div style={{ marginTop: 12 }}>
          <CardActionRow
            place={place}
            idx={`place-${idx}`}
            basketItem={basketItem}
            added={added}
            addToBasket={addToBasket}
            removeFromBasket={removeFromBasket}
            onArrange={onArrange}
            brief={null}
            compact={false}
          />
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// v3.2 — BRIEF BUILDER (Beat 2 right pane)
// Shows what Mira has captured vs what she's still asking. Real-time fill.
// ─────────────────────────────────────────────────────────────────────────────

// Friendly labels for the gathered_details / needs_details keys the brain returns.
// v3.12 — expanded to cover every field across the 23-pillar capture registry.
const BRIEF_FIELD_LABELS = {
  // shared
  city: "City",
  date: "Date & time",
  guest_count: "Guests",
  guests: "Guests",
  party_size: "Guests",
  occasion: "Occasion",
  occasion_tone: "Occasion",
  dietary: "Dietary",
  dietary_restrictions: "Dietary",
  accessibility: "Accessibility",
  budget: "Budget",
  jurisdiction: "Jurisdiction",
  age: "Age band",
  age_band: "Age band",
  destination: "Destination",
  origin: "From",
  window: "Travel window",
  travellers: "Travellers",
  companions: "Companions",
  pace: "Pace",
  interests: "Interests",
  clarify_intent: "Request type",
  preferred_venue: "Preferred venue",
  preferred_location: "Preferred area",
  delivery_address: "Delivery address",
  delivery_address_area: "Delivery area",
  time: "Time",
  time_of_day: "Time of day",
  note: "Note",
  recipient: "Recipient",
  category: "Category",
  size: "Size",
  leather: "Leather",
  hardware: "Hardware",
  colour: "Colour",
  timeline: "Timeline",
  // v3.12 — 23-pillar fields
  intent: "Intent",
  urgency: "Urgency",
  outcome: "Outcome wanted",
  context: "Context",
  cuisine: "Cuisine",
  purpose: "Purpose",
  nights: "Nights",
  guest_mix: "Guest mix",
  flight_number: "Flight number",
  terminal: "Terminal",
  direction: "Arrival / departure",
  assistance_type: "Assistance",
  pickup: "Pickup",
  drop: "Drop",
  vehicle_type: "Vehicle",
  luggage: "Luggage",
  event_type: "Event",
  seats: "Seats",
  tone: "Tone",
  club_name: "Club",
  access_type: "Access",
  issue: "What's happening",
  home_location: "Home location",
  item_type: "Item",
  objective: "Objective",
  stakeholders: "Stakeholders",
  output_format: "Output format",
  health_goal: "Goal",
  availability: "Availability",
  trainer_preference: "Trainer preference",
  geography: "Geography",
  time_sensitivity: "Time sensitivity",
  crisis_nature: "Crisis nature",
  location: "Location",
  immediate_risk: "Immediate risk",
  emotional_goal: "Emotional goal",
  constraints: "Constraints",
  symbolism: "Symbolism",
  milestone: "Milestone",
  time_horizon: "Time horizon",
  personal_meaning: "Personal meaning",
  topic: "Topic",
  decision_stage: "Decision stage",
  sensitivity: "Sensitivity",
  document_type: "Document",
  authority: "Authority",
  deadline: "Deadline",
  service_needed: "Service",
  trust_level: "Trust level",
  request: "Request",
  issue_type: "Issue",
  device: "Device",
  severity: "Severity",
  trigger_event: "Trigger",
  member_state: "Member state",
  experience_type: "Experience",
  sentiment: "Sentiment",
};

function _briefFieldLabel(key) {
  if (!key) return null;
  return (
    BRIEF_FIELD_LABELS[key] ||
    key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

function BriefBuilder({ phase, primaryCity, pillarName, subcategory, gatheredDetails, needsDetails, basket = [], removeFromBasket, itineraryPlan = null }) {
  // v3.32 — Brief panel is now the visible proof of "the AI listens."
  // Renders Mira's auto-assembled brief as the conversation builds:
  //   - Each basket item as a row with name + provenance tag + Remove button
  //   - The itinerary plan (if Mira composed one) summarised as N days
  //   - Static eyebrow + pillar header above
  // The member's role is editor (Remove what they don't want) not curator.
  // The 6-field strip from v3.14 has been undone — every row now carries
  // the full address/phone/URL/ratings/actions payload that flows through
  // to the concierge Ops Docket.
  const items = Array.isArray(basket) ? basket : [];
  const itemCount = items.length;
  const itineraryDays = itineraryPlan?.days?.length || 0;

  // When nothing has been composed yet, fall back to the warm listening
  // state (post-first-turn but pre-cards).
  const hasMaterialContent = itemCount > 0 || itineraryDays > 0;
  const showListeningState = !hasMaterialContent;

  // Eyebrow copy adapts to the size of the brief
  const eyebrow = itineraryDays >= 1
    ? `Your ${itineraryDays}-day itinerary`
    : itemCount >= 2
    ? "Your itinerary"
    : "Your brief";

  return (
    <div className="tdc-card lc-card" style={{ padding: "var(--space-5)" }} data-testid="brief-builder">
      <div className="flex items-baseline justify-between mb-3">
        <div className="tdc-section-label tdc-section-label-gold" style={{ marginBottom: 0 }}>
          {eyebrow}
          {hasMaterialContent && itemCount > 0 && (
            <>
              {" · "}
              {`${itemCount} ${itemCount === 1 ? "item" : "items"}`}
            </>
          )}
        </div>
        {pillarName && (
          <span
            className="tdc-section-label"
            style={{
              marginBottom: 0,
              fontSize: "11px",
              color: "var(--color-text-faint)",
              fontFamily: "var(--font-mono)",
            }}
          >
            {pillarName}
          </span>
        )}
      </div>

      {/* v3.32 — Itinerary plan summary band (when Mira composed days). */}
      {itineraryDays > 0 && (
        <div
          data-testid="brief-itinerary-summary"
          style={{
            padding: "0.7rem 0.85rem",
            marginBottom: itemCount > 0 ? "0.85rem" : "0.4rem",
            borderRadius: "var(--lc-radius-sm)",
            background: "rgba(15,61,46,0.03)",
            border: "1px solid rgba(15,61,46,0.12)",
          }}
        >
          <div
            className="lc-text-display"
            style={{
              fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif",
              fontSize: "0.85rem",
              fontWeight: 500,
              color: "var(--lc-text-primary)",
              lineHeight: 1.4,
              marginBottom: 4,
            }}
          >
            {itineraryPlan?.title || `${itineraryDays}-day itinerary`}
          </div>
          {itineraryPlan?.date_range_label && (
            <div
              className="lc-text-body"
              style={{ fontSize: "0.72rem", color: "var(--lc-text-secondary)", fontStyle: "italic" }}
            >
              {itineraryPlan.date_range_label}
            </div>
          )}
        </div>
      )}

      {/* v3.32 — Brief items rendered with provenance + Remove. */}
      {items.length > 0 && (
        <div data-testid="brief-items" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {items.map((it, idx) => {
            const locationLine = [it.area, it.city].filter(Boolean).join(" · ");
            return (
              <div
                key={`${it.kind}-${it.name}-${idx}`}
                data-testid={`brief-item-${idx}`}
                data-kind={it.kind}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                  padding: "0.65rem 0.75rem",
                  borderRadius: "var(--lc-radius-sm)",
                  background: "var(--lc-bg-elevated)",
                  border: "1px solid var(--lc-border-soft)",
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    className="lc-text-body"
                    style={{
                      fontSize: "0.85rem",
                      fontWeight: 500,
                      color: "var(--lc-text-primary)",
                      lineHeight: 1.35,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {it.name}
                  </div>
                  {locationLine && (
                    <div
                      className="lc-text-body"
                      style={{
                        fontSize: "0.72rem",
                        color: "var(--lc-text-muted)",
                        marginTop: 2,
                        lineHeight: 1.4,
                      }}
                    >
                      {locationLine}
                    </div>
                  )}
                  <div style={{ marginTop: 6 }}>
                    <ProvenanceTag item={it} compact />
                  </div>
                </div>
                {removeFromBasket && (
                  <button
                    onClick={() => removeFromBasket(it)}
                    data-testid={`brief-item-remove-${idx}`}
                    aria-label={`Remove ${it.name} from your brief`}
                    title="Remove from brief"
                    style={{
                      background: "transparent",
                      border: "1px solid var(--lc-border-soft)",
                      color: "var(--lc-text-muted)",
                      fontFamily: "var(--lc-font-body)",
                      fontSize: "0.7rem",
                      letterSpacing: "0.04em",
                      padding: "4px 9px",
                      borderRadius: 999,
                      cursor: "pointer",
                      flex: "0 0 auto",
                      transition: "all 160ms ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = "var(--lc-error, #8B2635)";
                      e.currentTarget.style.borderColor = "var(--lc-error, #8B2635)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = "var(--lc-text-muted)";
                      e.currentTarget.style.borderColor = "var(--lc-border-soft)";
                    }}
                  >
                    Remove
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showListeningState && (
        <div
          className="lc-text-display"
          data-testid="brief-listening-placeholder"
          style={{
            fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif",
            fontStyle: "italic",
            fontSize: "0.92rem",
            lineHeight: 1.55,
            color: "var(--lc-text-muted)",
            padding: "0.75rem 0",
          }}
        >
          {phase === "gathering"
            ? "Tell me what's on your mind — I'm listening."
            : "Your brief is taking shape on the right."}
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Removed in v3.26: the internal "schema rows" renderer for
// BriefBuilder. The captured / asking / pending field tracker, the
// progress bar, and the "0/1 captured 0%" line all leaked internal
// gathering mechanics to the member. The build logic still uses the
// underlying state — it's just no longer painted on screen.
// ─────────────────────────────────────────────────────────────────────
function _unused_BriefBuilderLegacy({ phase, primaryCity, pillarName, subcategory, gatheredDetails, needsDetails, basketCount = 0 }) {
  // Kept only as a reference of what was. Not exported, not rendered.
  return null;
}

// ─────────────────────────────────────────────────────────────────────
function _legacy_BriefBuilderRowsKeptInRepo() {
  // (preserved as comment in source — the field-tracker UI was here)
}


// ─────────────────────────────────────────────────────────────────────────────
// PATTERN B — Sourcing & Products (supplier-pays model)
// ─────────────────────────────────────────────────────────────────────────────

function SourcingPanel({ productHints, sourcingHint, primaryCity, onArrange, addToBasket, removeFromBasket, isInBasket }) {
  if (!productHints || productHints.length === 0) {
    return (
      <div className="lc-card" style={{ padding: "1.25rem" }} data-testid="sourcing-panel-empty">
        <div className="lc-eyebrow mb-3">Things your Concierge® can source</div>
        <div className="lc-text-body lc-text-muted" style={{ fontSize: "0.85rem", fontStyle: "italic", lineHeight: 1.5 }}>
          Tell Mira what you'd like sourced — flowers, gifts, groceries, bespoke items.
          The Concierge® team coordinates with the supplier; payment goes directly to them.
        </div>
      </div>
    );
  }

  return (
    <div className="lc-card" style={{ padding: "1.25rem" }} data-testid="sourcing-panel">
      <div className="flex items-baseline justify-between mb-3">
        <div className="lc-eyebrow" style={{ color: "var(--lc-highlight)" }}>
          Mira will source
        </div>
        {primaryCity && (
          <span className="lc-text-mono lc-text-muted" style={{ fontSize: "0.65rem" }}>
            {primaryCity}
          </span>
        )}
      </div>
      <div className="space-y-3">
        {productHints.map((p, idx) => (
          <ProductHintCard
            key={`${p.name}-${idx}`}
            product={p}
            onArrange={onArrange}
            idx={idx}
            addToBasket={addToBasket}
            removeFromBasket={removeFromBasket}
            isInBasket={isInBasket}
          />
        ))}
      </div>
      {sourcingHint && (
        <div
          className="lc-text-body"
          style={{
            marginTop: "1rem",
            paddingTop: "0.85rem",
            borderTop: "1px solid var(--lc-border-soft)",
            fontSize: "0.7rem",
            color: "var(--lc-text-muted)",
            fontStyle: "italic",
            lineHeight: 1.5,
          }}
          data-testid="sourcing-hint"
        >
          Brief to the team: {sourcingHint}
        </div>
      )}
    </div>
  );
}

function ProductHintCard({ product, onArrange, idx, addToBasket, removeFromBasket, isInBasket }) {
  const firstLetter = (product.name || "?").trim().charAt(0).toUpperCase();
  const angle = 70 + (idx * 43) % 80;
  // v3.32 — Rich basket item (full sourcing context for the Ops Docket).
  const basketItem = buildBasketItemFromCard(product, "product");
  const added = isInBasket && isInBasket(basketItem);

  return (
    <div
      style={{
        borderRadius: "var(--lc-radius-md)",
        overflow: "hidden",
        background: "var(--lc-bg-elevated)",
        border: added ? "1.5px solid var(--lc-highlight)" : "1px solid var(--lc-highlight)",
        boxShadow: "var(--lc-shadow-soft)",
      }}
      data-testid={`product-hint-${idx}`}
    >
      {/* Hero — real photo (Cloudinary) when product venue verified;
           else softer cream/sage for "sourced" register */}
      <div
        style={{
          height: 132,
          background: product.image_url
            ? `center / cover no-repeat url("${product.image_url}"), var(--lc-cream)`
            : `linear-gradient(${angle}deg, rgba(184,154,78,0.14) 0%, rgba(72,93,73,0.10) 60%, rgba(247,242,232,0) 100%), var(--lc-cream)`,
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {!product.image_url && (
          <div
            className="lc-text-display"
            style={{
              fontSize: "3rem",
              color: "var(--lc-highlight)",
              fontWeight: 400,
              fontStyle: "italic",
              opacity: 0.65,
              lineHeight: 1,
            }}
          >
            {firstLetter}
          </div>
        )}
        <div
          style={{
            position: "absolute",
            top: 8,
            right: 10,
            fontSize: "0.6rem",
            color: "var(--lc-text-muted)",
            background: "rgba(247,242,232,0.92)",
            padding: "2px 6px",
            borderRadius: 999,
            fontFamily: "var(--lc-font-body)",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
          }}
        >
          {product.category || "sourced"}
        </div>
      </div>

      {/* Card body */}
      <div style={{ padding: "0.85rem 1rem 0.95rem" }}>
        <div className="lc-text-body" style={{ fontSize: "0.9rem", fontWeight: 500, color: "var(--lc-text-primary)", lineHeight: 1.3 }}>
          {product.name}
        </div>
        {product.location && (
          <div className="lc-text-mono lc-text-muted" style={{ fontSize: "0.65rem", marginTop: 3, letterSpacing: "0.04em" }}>
            {product.location}{product.supplier_class ? ` · ${product.supplier_class.replace(/_/g, " ")}` : ""}
          </div>
        )}
        {/* v3.1 — provenance badge */}
        <div style={{ marginTop: 6 }}>
          <ArchiveStatusBadge status={product.archive_status || "unknown"} />
        </div>
        {product.blurb && (
          <div
            className="lc-text-display"
            style={{
              fontSize: "0.78rem",
              fontStyle: "italic",
              color: "var(--lc-text-secondary)",
              marginTop: 8,
              lineHeight: 1.5,
            }}
          >
            {product.blurb}
          </div>
        )}
        {/* Supplier-pays micro-copy — single most important UX-to-business-model line */}
        <div
          className="lc-text-body"
          style={{
            marginTop: 10,
            paddingTop: 8,
            borderTop: "1px dashed var(--lc-border-soft)",
            fontSize: "0.68rem",
            color: "var(--lc-text-muted)",
            lineHeight: 1.55,
            fontStyle: "italic",
          }}
          data-testid={`supplier-pays-microcopy-${idx}`}
        >
          Sourced via our partner. Payment to the supplier directly — your Concierge® arranges everything else.
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12, gap: 8 }}>
          <BasketToggle
            item={basketItem}
            added={added}
            onAdd={() => addToBasket(basketItem)}
            onRemove={() => removeFromBasket(basketItem)}
            testid={`basket-toggle-product-${idx}`}
            variant="primary"
          />
          <button
            className="lc-text-body"
            style={{
              background: "transparent",
              border: "none",
              color: "var(--lc-text-muted)",
              fontSize: "0.7rem",
              padding: 0,
              cursor: "pointer",
              textDecoration: "underline",
              textUnderlineOffset: "2px",
            }}
            onClick={() => onArrange && onArrange({ name: product.name, city: product.location })}
            data-testid={`arrange-product-${idx}`}
          >
            ask Mira
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PATTERN C — Procedural & Documentation step-selector
// ─────────────────────────────────────────────────────────────────────────────

function ProceduralStepSelector({ steps, adjacentCapabilities, selection, toggleStep, onSend, submitState, primaryCity }) {
  if (!steps || steps.length === 0) return null;
  const ticked = steps.filter((s) => selection.has(s.number)).length;
  const total = steps.length;

  if (submitState.status === "sent") {
    return (
      <div
        className="lc-card-elevated"
        style={{
          padding: "1.1rem 1.25rem",
          background: "var(--lc-cream, #FAF8F5)",
          color: "var(--lc-text-primary, #0F3D2E)",
          border: "1px solid var(--lc-accent, #C9973A)",
        }}
        data-testid="procedural-sent"
      >
        <div className="lc-eyebrow" style={{ color: "var(--lc-accent, #C9973A)" }}>Delegated</div>
        <div className="lc-text-display" style={{ fontStyle: "italic", fontSize: "0.95rem", marginTop: 6, lineHeight: 1.4, color: "var(--lc-text-primary, #0F3D2E)" }}>
          {submitState.message}
        </div>
        {submitState.ticketId && (
          <div className="lc-text-mono" style={{ fontSize: "0.65rem", opacity: 0.75, marginTop: 8, letterSpacing: "0.08em", color: "var(--lc-text-muted)" }}>
            REF · {submitState.ticketId}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="lc-card" style={{ padding: "1.25rem" }} data-testid="procedural-panel">
      <div className="flex items-baseline justify-between mb-3">
        <div className="lc-eyebrow" style={{ color: "var(--lc-highlight)" }}>
          Your Concierge® can help with
        </div>
        {primaryCity && (
          <span className="lc-text-mono lc-text-muted" style={{ fontSize: "0.65rem" }}>
            {primaryCity}
          </span>
        )}
      </div>
      <div className="space-y-2.5">
        {steps.map((s) => {
          const isOn = selection.has(s.number);
          return (
            <button
              key={s.number}
              onClick={() => toggleStep(s.number)}
              data-testid={`step-toggle-${s.number}`}
              style={{
                width: "100%",
                textAlign: "left",
                padding: "0.85rem 1rem",
                borderRadius: "var(--lc-radius-sm)",
                background: isOn ? "rgba(184,154,78,0.10)" : "var(--lc-bg-elevated)",
                border: isOn ? "1px solid var(--lc-highlight)" : "1px solid var(--lc-border-soft)",
                cursor: "pointer",
                transition: "all var(--lc-dur) var(--lc-ease)",
                display: "flex",
                gap: 12,
                alignItems: "flex-start",
              }}
            >
              {/* Custom checkbox */}
              <span
                style={{
                  flexShrink: 0,
                  marginTop: 2,
                  width: 18,
                  height: 18,
                  borderRadius: 4,
                  border: `1.5px solid ${isOn ? "var(--lc-highlight)" : "var(--lc-border)"}`,
                  background: isOn ? "var(--lc-highlight)" : "transparent",
                  color: "var(--lc-cream)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 13,
                  fontWeight: 700,
                  lineHeight: 1,
                }}
              >
                {isOn ? "✓" : ""}
              </span>
              <div style={{ flex: 1 }}>
                <div className="lc-text-body" style={{ fontSize: "0.85rem", fontWeight: 500, color: "var(--lc-text-primary)", lineHeight: 1.35 }}>
                  <span className="lc-text-mono" style={{ color: "var(--lc-text-muted)", marginRight: 6, fontSize: "0.72rem" }}>
                    {s.number}.
                  </span>
                  {s.title}
                </div>
                {s.detail && (
                  <div
                    className="lc-text-display"
                    style={{
                      fontSize: "0.72rem",
                      fontStyle: "italic",
                      color: "var(--lc-text-secondary)",
                      marginTop: 5,
                      lineHeight: 1.5,
                    }}
                  >
                    {s.detail}
                  </div>
                )}
                {!s.concierge_can_handle && (
                  <div className="lc-text-mono lc-text-muted" style={{ fontSize: "0.6rem", marginTop: 4, letterSpacing: "0.06em" }}>
                    Member-only step
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      <div className="lc-text-body" style={{ fontSize: "0.75rem", color: "var(--lc-text-muted)", marginTop: 14, marginBottom: 12, fontStyle: "italic" }}>
        {ticked} of {total} steps selected
      </div>

      {/* v3.40 — Identity capture mirrors the basket flow (Pattern C). */}
      <BriefIdentityFields />

      <button
        className="lc-btn-primary lc-btn-primary--forest"
        style={{ width: "100%", justifyContent: "center", padding: "0.85rem 1rem", fontSize: "0.875rem" }}
        onClick={onSend}
        disabled={submitState.status === "sending" || ticked === 0}
        data-testid="procedural-send-btn"
      >
        {submitState.status === "sending" ? "Sending…" : `Send brief to Concierge® team →`}
      </button>
      {submitState.status === "error" && (
        <div className="lc-text-body" style={{ fontSize: "0.72rem", color: "var(--lc-error)", marginTop: 8, fontStyle: "italic" }}>
          {submitState.message}
        </div>
      )}

      {/* Adjacent capabilities */}
      {adjacentCapabilities && adjacentCapabilities.length > 0 && (
        <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid var(--lc-border-soft)" }}>
          <div className="lc-eyebrow mb-2.5">Your Concierge® can also</div>
          <div className="flex flex-wrap gap-2">
            {adjacentCapabilities.map((cap, idx) => (
              <span
                key={idx}
                className="lc-chip"
                style={{
                  fontSize: "0.72rem",
                  padding: "0.35rem 0.7rem",
                  fontStyle: "italic",
                  background: "transparent",
                  border: "1px solid var(--lc-border-soft)",
                  color: "var(--lc-text-secondary)",
                  cursor: "default",
                }}
                data-testid={`adjacent-cap-${idx}`}
              >
                {cap}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BasketToggle — small reusable add/remove button
// ─────────────────────────────────────────────────────────────────────────────

function BasketToggle({ added, onAdd, onRemove, testid, variant = "secondary", compact = false }) {
  const padding = compact ? "0.2rem 0.55rem" : "0.35rem 0.75rem";
  const fontSize = compact ? "0.62rem" : "0.72rem";
  if (added) {
    return (
      <button
        className="lc-text-body"
        style={{
          background: "rgba(47,110,79,0.08)",
          color: "var(--lc-success)",
          border: "1px solid var(--lc-success)",
          borderRadius: "var(--lc-radius-sm)",
          padding: padding,
          fontSize: fontSize,
          fontWeight: 500,
          cursor: "pointer",
          letterSpacing: "0.02em",
        }}
        onClick={onRemove}
        data-testid={testid}
      >
        {compact ? "✓ Added" : "✓ Added · undo"}
      </button>
    );
  }
  const isPrimary = variant === "primary";
  return (
    <button
      className="lc-text-body"
      style={{
        background: isPrimary ? "var(--lc-highlight)" : "transparent",
        color: isPrimary ? "var(--lc-cream)" : "var(--lc-accent)",
        border: isPrimary ? "none" : "1px solid var(--lc-border)",
        borderRadius: "var(--lc-radius-sm)",
        padding: padding,
        fontSize: fontSize,
        fontWeight: 500,
        cursor: "pointer",
        letterSpacing: "0.02em",
      }}
      onClick={onAdd}
      data-testid={testid}
    >
      {compact ? "+ Add" : "Add to brief +"}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CityCapabilitiesPanel — "Things your Concierge® can do in [city]"
// ─────────────────────────────────────────────────────────────────────────────

// F13 (FIR-20260602-007) — pillar-filter map.
// When the active conversation has a pillar (Dine / Stay / Move / etc),
// the right-pane rail must show only items whose `lc_pillar` matches —
// or items that share a co-relevant pillar pair (e.g. Stay flow can
// still show Travel-class items because they sit naturally together).
// Founder rule (binding): "For a DINE / dinner flow, do not show
// medicines, cardiology consults, unrelated health services, unrelated
// city services."
const _LC_PILLAR_RAIL_MATCH = {
  Dine: ["Dine"],
  Stay: ["Stay", "Travel"],
  Move: ["Move", "Travel", "Airport"],
  Care: ["Care"],
  Paperwork: ["Paperwork"],
  Travel: ["Travel", "Stay", "Move", "Airport"],
  Enjoy: ["Enjoy"],
  "Shop Assist": ["Shop Assist"],
  Fit: ["Fit", "Care"],
  Club: ["Club"],
  Airport: ["Airport", "Travel", "Move"],
};

// Legacy `pillar` string → canonical lc_pillar fallback (mirrors
// /app/backend/city_capabilities.py::_LEGACY_PILLAR_TO_LC_PILLAR so
// older payloads or Claude-fallback items without `lc_pillar` still
// classify correctly on the rail).
const _LEGACY_PILLAR_TO_LC_PILLAR_JS = {
  everyday_life: "Care",
  family_care: "Care",
  wellness: "Care",
  documentation: "Paperwork",
  moments_of_life: "Dine",
  travel: "Travel",
};

function _resolveItemLcPillar(item) {
  if (!item || typeof item !== "object") return null;
  if (item.lc_pillar) return item.lc_pillar;
  const legacy = (item.pillar || "").toString().trim();
  if (legacy && _LEGACY_PILLAR_TO_LC_PILLAR_JS[legacy]) {
    return _LEGACY_PILLAR_TO_LC_PILLAR_JS[legacy];
  }
  return null;
}

function CityCapabilitiesPanel({ city, source, items, activePillar, addToBasket, removeFromBasket, isInBasket }) {
  // F13 (FIR-20260602-007) — pillar filter.
  // If activePillar is set AND we know which co-relevant pillars to
  // accept, restrict items to those whose resolved lc_pillar is in the
  // allow-list. If activePillar is null (cold-open / no pillar yet)
  // OR the active pillar is not in the rail-match map, show everything
  // (preserves the "things your Concierge® can do here" pattern on the
  // welcome mat).
  let renderItems = items;
  const allow = activePillar ? _LC_PILLAR_RAIL_MATCH[activePillar] : null;
  if (allow && Array.isArray(allow) && allow.length > 0) {
    const filtered = (items || []).filter((it) => {
      const lcp = _resolveItemLcPillar(it);
      if (!lcp) return false;
      return allow.includes(lcp);
    });
    renderItems = filtered;
  }
  // If the filter wipes the rail entirely (e.g. Bangalore-Dine when no
  // Dine-tagged cards exist), HIDE the rail rather than showing an
  // empty card frame.
  if (!renderItems || renderItems.length === 0) {
    return null;
  }
  return (
    <div className="lc-card" style={{ padding: "1.25rem" }} data-testid="city-capabilities-panel">
      <div className="flex items-baseline justify-between mb-3">
        <div className="lc-eyebrow">
          {city ? `In ${city}, your Concierge® can` : "Your Concierge® can"}
        </div>
        <span className="lc-text-muted" style={{ fontSize: "0.65rem", letterSpacing: "0.06em" }}>
          {source === "static" ? "Mira curated" : source === "claude" ? "curated for you" : "default capabilities"}
        </span>
      </div>
      <div className="space-y-2.5">
        {renderItems.map((item, idx) => {
          const basketItem = {
            kind: "city_capability",
            name: item.title,
            city: city || "",
            why: item.why_relevant,
            pillar: item.pillar || null,
            source: "concierge_capability",
          };
          const added = isInBasket && isInBasket(basketItem);
          return (
            <div
              key={`${item.title}-${idx}`}
              style={{
                padding: "0.75rem 0.9rem",
                borderRadius: "var(--lc-radius-sm)",
                background: added ? "rgba(47,110,79,0.05)" : "var(--lc-bg-elevated)",
                border: added ? "1px solid var(--lc-success)" : "1px solid var(--lc-border-soft)",
                transition: "all var(--lc-dur) var(--lc-ease)",
              }}
              data-testid={`city-cap-${idx}`}
            >
              <div className="lc-text-body" style={{ fontSize: "0.85rem", fontWeight: 500, color: "var(--lc-text-primary)", lineHeight: 1.35 }}>
                {item.title}
              </div>
              {item.why_relevant && (
                <div
                  className="lc-text-display"
                  style={{ fontSize: "0.72rem", fontStyle: "italic", color: "var(--lc-text-secondary)", marginTop: 5, lineHeight: 1.5 }}
                >
                  {item.why_relevant}
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                <BasketToggle
                  item={basketItem}
                  added={added}
                  onAdd={() => addToBasket(basketItem)}
                  onRemove={() => removeFromBasket(basketItem)}
                  testid={`basket-toggle-cap-${idx}`}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TalkToConciergePanel() {
  const { theme } = useTheme();
  // v3.23 — phone/whatsapp/email resolve LIVE from tenant contact.
  // Empty fallbacks render an "—" rather than invent numbers.
  const phoneNumber = theme.phone_number || "";
  const phoneDisplay = theme.phone_display || "—";
  const phoneHours = theme.phone_hours || "Mon–Sat · 9 AM – 9 PM IST";
  const whatsappHref = theme.whatsapp_number
    ? `https://wa.me/${theme.whatsapp_number.replace(/[^0-9]/g, "")}`
    : (phoneNumber ? `https://wa.me/${phoneNumber.replace(/[^0-9]/g, "")}` : "#");
  const emailHref = theme.inbound_email
    ? `mailto:${theme.inbound_email}?subject=Concierge%C2%AE%20request%20from%20website`
    : "#";

  return (
    <div className="lc-card" style={{ padding: "1.25rem" }} data-testid="talk-to-concierge-panel">
      <div className="lc-eyebrow mb-3">Talk to a Concierge®</div>

      <div className="space-y-3">
        {/* v3.32 — D2: reorder to WhatsApp → Email → Call. Members prefer
            messaging to calling; phone stays available but not first. */}
        {theme.show_whatsapp_channel && (
          <a
            href={whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            className="lc-btn-ghost"
            style={{ display: "block", width: "100%", padding: "0.75rem 1rem", textAlign: "left" }}
            data-testid="contact-whatsapp"
          >
            <div className="lc-text-body" style={{ fontSize: "0.85rem", fontWeight: 500, display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ color: "var(--lc-highlight)" }}>●</span>
              Continue on WhatsApp
            </div>
            <div className="lc-text-body" style={{ fontSize: "0.7rem", marginTop: 3, color: "var(--lc-text-muted)" }}>
              A Concierge® picks up in this thread.
            </div>
          </a>
        )}

        {theme.show_email_channel && (
          <a
            href={emailHref}
            className="lc-btn-ghost"
            style={{ display: "block", width: "100%", padding: "0.75rem 1rem", textAlign: "left" }}
            data-testid="contact-email"
          >
            <div className="lc-text-body" style={{ fontSize: "0.85rem", fontWeight: 500, display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ color: "var(--lc-highlight)" }}>●</span>
              Email our team
            </div>
            <div className="lc-text-body" style={{ fontSize: "0.7rem", marginTop: 3, color: "var(--lc-text-muted)" }}>
              {theme.inbound_email} · reply within 4 working hours
            </div>
          </a>
        )}

        {theme.show_phone_channel && (
          <a
            href={`tel:${phoneNumber}`}
            style={{
              /* v3.27 — §4 Brass-discipline: the primary phone CTA was
                 a full-brass flood. Now cream with a brass hairline +
                 brass ☎ icon. The brass is jewellery, not the button. */
              display: "block",
              padding: "1rem 1.1rem",
              background: "var(--lc-cream, #FAF8F5)",
              color: "var(--lc-text-primary, #0F3D2E)",
              border: "1px solid var(--lc-accent, #C9973A)",
              borderRadius: "var(--lc-radius-md)",
              textDecoration: "none",
              transition: "background-color var(--lc-dur) var(--lc-ease), border-color var(--lc-dur) var(--lc-ease)",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = "var(--lc-cream-soft, #F0EDE8)";
              e.currentTarget.style.borderColor = "var(--lc-text-primary, #0F3D2E)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = "var(--lc-cream, #FAF8F5)";
              e.currentTarget.style.borderColor = "var(--lc-accent, #C9973A)";
            }}
            data-testid="contact-phone"
          >
            <div className="lc-text-display" style={{ fontSize: "1.05rem", fontWeight: 500, display: "flex", alignItems: "center", gap: 10, letterSpacing: "-0.005em", color: "var(--lc-text-primary, #0F3D2E)" }}>
              <span style={{ color: "var(--lc-accent, #C9973A)", fontSize: "1.15rem" }}>☎</span>
              {phoneDisplay}
            </div>
            <div className="lc-text-body" style={{ fontSize: "0.72rem", marginTop: 6, color: "var(--lc-text-muted)", letterSpacing: "0.02em" }}>
              {phoneHours} · tap to call a senior Concierge®
            </div>
          </a>
        )}
      </div>

      <div
        className="lc-text-body"
        style={{
          fontSize: "0.7rem",
          marginTop: "1rem",
          paddingTop: "0.85rem",
          borderTop: "1px solid var(--lc-border-soft)",
          color: "var(--lc-text-muted)",
          fontStyle: "italic",
          textAlign: "center",
        }}
      >
        Mira is the listener. The Concierge® team delivers.
      </div>
    </div>
  );
}

function PromiseBand() {
  const { trademark } = useTheme();
  return (
    <div
      className="lc-text-body"
      style={{
        padding: "1rem 1.25rem",
        background: "var(--lc-accent)",
        color: "var(--lc-cream)",
        borderRadius: "var(--lc-radius-md)",
        fontSize: "0.75rem",
        lineHeight: 1.6,
        textAlign: "center",
        letterSpacing: "0.01em",
      }}
      data-testid="promise-band"
    >
      <div style={{ fontWeight: 500, marginBottom: 4 }}>{trademark.human_concierge_credit}</div>
      <div style={{ opacity: 0.75, fontSize: "0.7rem" }}>
        {trademark.footer_brand_line}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MIRA PAGE (entry)
// ─────────────────────────────────────────────────────────────────────────────

function MiraPageInner() {
  const [activeContext, setActiveContext] = useState(null);
  const [externalMessage, setExternalMessage] = useState(null);
  const [basket, setBasket] = useState([]); // [{kind, name, city, why, pillar, source}]
  const [cityCapabilities, setCityCapabilities] = useState({ city: "", items: [], source: "pillar_floor" });
  const [submitState, setSubmitState] = useState({ status: "idle", message: "", ticketId: "" });
  // v3.46.3 — Honest "Mira is shaping your picks…" pulse on the right pane.
  // Phase 1.2 streamed prose fast and exposed the ~9s gap where Sonnet is
  // still generating the venue_hints JSON tail. While we wait, the right
  // pane shows a brass pulse instead of the idle empty-state placeholder.
  // Set true the moment the user sends; cleared on partial/complete/error.
  const [isMiraComposing, setIsMiraComposing] = useState(false);
  // Pattern C — step selection (default: all ticked when new steps arrive)
  const [procStepsSelection, setProcStepsSelection] = useState(new Set());
  const [procStepsKey, setProcStepsKey] = useState(""); // tracks when steps change to reset selection
  const { theme } = useTheme();

  // When Mira's reply names a primary_city, fetch the city-capability set
  useEffect(() => {
    const city = activeContext?.primary_city;
    if (!city || city === cityCapabilities.city) return;

    fetch(`${BACKEND_URL}/api/lc/city-capabilities?city=${encodeURIComponent(city)}`)
      .then((r) => r.json())
      .then((data) => setCityCapabilities(data))
      .catch(() => setCityCapabilities({ city, items: [], source: "pillar_floor" }));
  }, [activeContext?.primary_city, BACKEND_URL]); // eslint-disable-line react-hooks/exhaustive-deps

  // v3.44 — Universal "Add to brief" intent. The chat-pane component (where
  // send() lives) dispatches a `lc-basket-add` CustomEvent on window when
  // the backend resolves a venue. We listen here (next to the basket state)
  // and append silently — no toast (the Mira ack bubble is the
  // acknowledgement). Sibling components can't share refs directly, this
  // is the simplest clean bridge.
  useEffect(() => {
    function onAddToBasket(ev) {
      const item = ev?.detail;
      if (!item || !item.name) return;
      addToBasket(item, { silent: true });
    }
    window.addEventListener("lc-basket-add", onAddToBasket);
    return () => window.removeEventListener("lc-basket-add", onAddToBasket);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Pattern C — when a new set of procedural_steps arrives, default-tick all
  useEffect(() => {
    const steps = activeContext?.procedural_steps || [];
    if (steps.length === 0) return;
    const key = steps.map((s) => s.number).join(",");
    if (key === procStepsKey) return;
    setProcStepsKey(key);
    setProcStepsSelection(new Set(steps.map((s) => s.number)));
    // Reset submitState when new pattern C arrives
    if (activeContext?.pattern === "C") {
      setSubmitState({ status: "idle", message: "", ticketId: "" });
    }
  }, [activeContext, procStepsKey]);

  // v3.35 / S1+ — Topic pivot for SAFETY ON CONTEXT CHANGE.
  //
  // The auto-add useEffect (below) handles REPLACE-vs-ACCUMULATE on each
  // new turn. But there's a separate edge case: when a fresh
  // `activeContext` arrives but it has NO venue_hints/product_hints (e.g.
  // gathering_intake_*, mid-conversation prompts), the basket would stay
  // populated from the PREVIOUS turn. That's fine for refinement turns
  // within the same topic — but dangerous when the member has pivoted
  // mid-conversation to a different city or pillar.
  //
  // Rule: if a context arrives with non-null pillar/city AND the basket
  // is non-empty AND NONE of the basket items share either signal with
  // the new context, clear the basket silently with a toast. The
  // member sees a fresh brief matching the new topic.
  useEffect(() => {
    if (!activeContext) return;
    const ctxPillar = (activeContext.pillar_name || "").trim();
    const ctxCity = (activeContext.primary_city || "").trim();
    if (!ctxPillar && !ctxCity) return;
    if (basket.length === 0) return;
    // Skip the pivot check during a turn that brought NEW venue_hints —
    // the auto-add useEffect will REPLACE if appropriate. Pivot-clear is
    // for turns that DON'T bring new items (gathering / refinement /
    // follow-up questions about a different topic).
    if ((activeContext.venue_hints || []).length > 0) return;
    if ((activeContext.product_hints || []).length > 0) return;
    // Compare the new context to each basket item; the basket "still
    // matches" if ANY item shares pillar OR city with the new context.
    // Items without pillar_name/city are treated neutrally (no opinion
    // on whether they belong).
    const stillMatches = basket.some((it) => {
      const itemPillar = (it.pillar_name || it.pillar || "").trim();
      const itemCity = (it.city || "").trim();
      if (!itemPillar && !itemCity) return true; // no opinion → keep
      if (ctxPillar && itemPillar && itemPillar === ctxPillar) return true;
      if (ctxCity && itemCity && itemCity === ctxCity) return true;
      return false;
    });
    if (stillMatches) return;
    setBasket([]);
    setSubmitState({ status: "idle", message: "", ticketId: "" });
    toast.info("Brief cleared — new topic detected.", {
      description: `Now: ${ctxPillar || "—"}${ctxPillar && ctxCity ? " · " : ""}${ctxCity || ""}`,
      duration: 3500,
      id: "brief-pivot-clear",
    });
  }, [activeContext?.pillar_name, activeContext?.primary_city]); // eslint-disable-line react-hooks/exhaustive-deps

  // v3.32 — Option α auto-fill. When Mira composes anything — venue picks,
  // sourcing products, or an itinerary plan — auto-add it to the brief so
  // the right pane fills as the conversation builds. The member's role is
  // editor (Remove what they don't want), not curator (Add each piece they
  // do want). This is the visible proof of "the AI listens."
  //
  // Carve-out: contextual_upsell (paid in-context offers — boat transfer,
  // VIP table, private chef) STAYS opt-in via the explicit "Yes, arrange
  // it" button. Nothing with a price lands in the brief without a deliberate
  // yes (the honesty discipline).
  //
  // v3.35 / S1+ — REPLACE vs ACCUMULATE doctrine.
  //   Pattern A (single-decision discovery, e.g. "Rooftop bars in Bandra")
  //     → REPLACE the basket on every fresh turn. The brief is scoped
  //       to this one question; the next question owns the next brief.
  //       This fixes the stale-carry-over bug: "Real estate expos in
  //       Mumbai" → "Rooftop bars in Bandra" no longer accumulates 10
  //       mismatched items.
  //   Pattern B (sourcing — Royal Pop) → REPLACE — each sourcing turn
  //       owns its brief.
  //   Pattern C (procedural — visa renewal) → REPLACE — single-decision.
  //   Itinerary plan (day-by-day Mykonos) → ACCUMULATE — every day adds
  //       to the trip brief.
  //   Mid-gathering (R26 gathering_intake_*) → SKIP (no venues yet).
  useEffect(() => {
    if (!activeContext) return;
    const venues = activeContext.venue_hints || [];
    const products = activeContext.product_hints || [];
    const itineraryPlan = activeContext.itinerary_plan;
    const tripState = activeContext.trip_state || "";
    const pattern = activeContext.pattern;
    if (/^gathering_intake_/.test(tripState)) return;
    if (venues.length === 0 && products.length === 0) return;
    // Decide REPLACE vs ACCUMULATE.
    const isItinerary = itineraryPlan && Array.isArray(itineraryPlan.days) && itineraryPlan.days.length >= 1;
    const isSingleDecision = pattern === "A" || pattern === "B" || pattern === "C";
    const shouldReplace = isSingleDecision && !isItinerary;
    if (shouldReplace) {
      // Build the new basket from this turn's items, no accumulation.
      const nextItems = [
        ...venues.map((v) => buildBasketItemFromCard(v, "venue")),
        ...products.map((p) => buildBasketItemFromCard(p, "product")),
      ].filter((x) => x && x.name);
      setBasket(nextItems);
      return;
    }
    // Itinerary turn — accumulate (each day adds to the trip brief).
    venues.forEach((v) => {
      const item = buildBasketItemFromCard(v, "venue");
      if (item && item.name) addToBasket(item, { silent: true });
    });
    products.forEach((p) => {
      const item = buildBasketItemFromCard(p, "product");
      if (item && item.name) addToBasket(item, { silent: true });
    });
  }, [activeContext]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleArrangeVenue(venue) {
    // v3.20 — sentinels: refine the existing itinerary or send the whole
    // composed plan to Concierge® as one coherent brief.
    if (venue && venue._refine) {
      setExternalMessage({ text: venue.name, ts: Date.now() });
      return;
    }
    if (venue && venue._sendItinerary) {
      // v3.23 — Right-pane "Send this itinerary to Concierge®" button.
      // Fire the dedicated itinerary-handoff endpoint directly via the
      // chat's handleHandoffYes — NOT a synthetic /api/mira/understand
      // round-trip. This is the ship-blocker fix that ensures the
      // member's click actually triggers the two emails + ticket.
      setExternalMessage({
        action: "handoff",
        ts: Date.now(),
      });
      return;
    }
    setExternalMessage({
      text: `Tell me more about ${venue.name}${venue.city ? ` in ${venue.city}` : ""} — could we arrange this?`,
      ts: Date.now(),
    });
  }

  function handlePillarTap(pillarKey, pillarLabel) {
    // Tapping a pillar = ask Mira what she can do under that pillar (drives Phase 1)
    setExternalMessage({
      text: `Tell me what you can do under ${pillarLabel}.`,
      ts: Date.now(),
    });
  }

  function basketKey(item) {
    return `${item.kind}::${item.name}::${item.city || ""}`;
  }

  function addToBasket(item, opts = {}) {
    const { silent = false } = opts;
    setBasket((prev) => {
      const k = basketKey(item);
      if (prev.some((x) => basketKey(x) === k)) return prev;     // dedupe; toast outside
      return [...prev, item];
    });
    if (silent) return; // v3.32 — auto-add (Option α) skips the toast — the
    // v3.19 — Toast OUTSIDE the setBasket updater so React StrictMode
    // dev-mode double-invocation never fires the notification twice.
    // We read the latest count via a quick rAF defer so the toast copy
    // matches the post-state count.
    setTimeout(() => {
      // We can't synchronously read the new basket length from React state,
      // but the user's action already incremented it. We compute the
      // post-add count from the closure's previous length + 1, falling
      // back to "your brief" copy when count is 1.
      setBasket((current) => {
        const alreadyShown = current.some((x) => basketKey(x) === basketKey(item));
        if (!alreadyShown) return current;
        const newCount = current.length;
        const collection = newCount >= 2 ? "itinerary" : "brief";
        // v3.46.18 — Founder-requested confirmation discipline.
        // Toast is the unmistakable moment of "this just registered".
        // Title = the action confirmed (Fraunces-friendly serif feel from
        // sonner's richColors), description = quiet next-step hint.
        // Duration bumped to 3.5s so the eye that's on the chat thread
        // has time to register the bottom-center cue.
        toast.success(`✓ ${item.name} added to your ${collection}`, {
          description: newCount === 1
            ? "When you have everything you need, tap Send brief to Concierge® team."
            : `${newCount} items in your ${collection} now. Send when ready.`,
          duration: 3500,
          id: `add-${basketKey(item)}`,    // dedupe identical toasts
        });
        return current;     // no state change
      });
    }, 0);
  }

  function removeFromBasket(item) {
    let didRemove = false;
    setBasket((prev) => {
      const next = prev.filter((x) => basketKey(x) !== basketKey(item));
      didRemove = next.length < prev.length;
      return next;
    });
    if (didRemove) {
      toast(`Removed ${item.name} from your brief.`, {
        duration: 2000,
        id: `remove-${basketKey(item)}`,
      });
    }
  }

  function isInBasket(item) {
    return basket.some((x) => basketKey(x) === basketKey(item));
  }

  function toggleStep(number) {
    setProcStepsSelection((prev) => {
      const next = new Set(prev);
      if (next.has(number)) next.delete(number);
      else next.add(number);
      return next;
    });
  }

  async function sendBasketToConcierge() {
    const pattern = activeContext?.pattern || "A";
    // ── BC-002.fix · founder doctrine (9 Feb 2026 PM late) ──
    //
    //   "The member owns the handoff." + "A promise must have
    //    a mechanism." A button the member can tap MUST either
    //    send the brief, or tell them WHY it can't. NEVER silent.
    //
    // The send is honoured when ANY of these carry signal:
    //   1. basket has at least one venue (legacy path)
    //   2. activeContext.brief_items has a venue_candidate · the
    //      right-pane brief the member is looking at IS the brief
    //   3. activeContext.brief_items has any line-item (Move, Stay,
    //      Care · non-venue asks the desk must still receive)
    //   4. activeContext.handoff_moment · Mira reached the handoff
    //   5. activeContext.session_id · the conversation transcript
    //      IS the brief, even mid-thread (the desk reads it)
    //
    // Failing ALL FIVE · we surface an explanatory toast and bail.
    // We do NOT silently swallow the tap · that's the BC-002
    // (promise-vs-mechanism) class of breach the founder named.
    const _briefItemsForGate = activeContext?.brief_items || [];
    const _hasVenueCandidate = _briefItemsForGate.some(
      bi => bi && bi.venue_candidate && bi.venue_candidate.name
    );
    const _hasAnyBriefItem = _briefItemsForGate.length > 0;
    const _canSend = (
      basket.length > 0
      || _hasVenueCandidate
      || _hasAnyBriefItem
      || !!activeContext?.handoff_moment
      || !!activeContext?.session_id
    );
    if (!_canSend) {
      // Honest fail · tell the member WHY it can't send.
      // Pinned by BC-002 regression test family.
      try {
        toast.error("Tell me what you're looking for first", {
          description:
            "I need at least one thing to pass to the team — start by " +
            "telling me what you'd like (a table, a journey, anything).",
          duration: 5500,
        });
      } catch (_) { /* sonner not mounted is non-fatal */ }
      return;
    }
    setSubmitState({ status: "sending", message: "", ticketId: "" });

    // v3.6 — Build a conversation_summary so the Concierge® team sees the
    // full thread, not just the items.
    const transcriptSummary = buildTranscriptSummary(activeContext);

    // v3.39 — Identity capture (soft-progressive). When the member has
    // identified themselves via the welcome banner (or any prior session),
    // ship those values so Zoho's Contact + the desk callback land on the
    // right person — not "Anonymous visitor."
    const memberName = (typeof window !== "undefined" && localStorage.getItem("lc_visitor_name")) || null;
    const memberEmail = (typeof window !== "undefined" && localStorage.getItem("lc_visitor_email")) || null;
    const memberPhone = (typeof window !== "undefined" && localStorage.getItem("lc_visitor_phone")) || null;

    // v3.43 / Phase C — Programme-benefit channel tagging.
    // v3.46.18 (26 May 2026) — CHANNEL POLLUTION FIX. The original logic
    // stamped `benefit_id="club_access"` whenever the localStorage flag
    // `lc_clubsocial_member === "yes"` was set — a flag that persists
    // ACROSS sessions. A member who answered the Club Social gate once
    // had every subsequent Dine / Stay / Move basket routed through the
    // Club Access workflow forever — wrong queue, wrong subject, wrong
    // SLA. (Founder caught this on a Bombay Canteen Dine brief →
    // ticket lc-20260526-e8cdb932 mis-routed to channel="club_access".)
    //
    // Doctrine — same spine as the v3.46.7 band fix and R28.7 operational
    // status: STALE STATE NEVER GATES A CURRENT REQUEST. Current intent
    // (this basket's contents) gates routing.
    //
    // benefit_id="club_access" now requires BOTH:
    //   (1) the member-status flag (localStorage lc_clubsocial_member=yes)
    //   (2) the CURRENT basket actually contains a Club-pillar item
    // Mirrors the _isClubItem test used by BasketHeader's band gate.
    let benefitId = null;
    try {
      const isClubSocialMember = (typeof window !== "undefined" &&
        localStorage.getItem("lc_clubsocial_member") === "yes");
      const basketContainsClubItem = (() => {
        if (!Array.isArray(basket)) return false;
        return basket.some((it) => {
          if (!it) return false;
          if (it.pillar_name === "Club" || it.pillar === "Club") return true;
          const src = (it.source || "").toLowerCase();
          if (src === "lc_clubs" || src === "club_intent") return true;
          const sub = (it.subcategory || "").toLowerCase();
          if (sub.includes("private club") || sub.includes("members' club") ||
              sub.includes("country club") || sub.includes("reciprocal club")) return true;
          return false;
        });
      })();
      if (isClubSocialMember && basketContainsClubItem) {
        benefitId = "club_access";
      }
    } catch (_) {}

    // ── v3.46.19 · Phase 1b core-promise FIX (26 May 2026, founder catch) ──
    // The right-pane already renders every brief_item as a visible
    // line-item row (BasketLineItemRow) — Move, Dine, Care, Stay etc.
    // But the SEND payload was only carrying `basket` (verified venue
    // cards the brain auto-added or the user chose). For non-discovery
    // pillars (Move/Care/Send/Stay-where-no-property-was-picked) there
    // was no matching venue in `basket`, so the ticket items[] missed
    // them entirely — the brief reached the desk with the car detail
    // BURIED in conversation_summary prose, not as a discrete row.
    //
    // Founder's Phase 1b promise: every distinct ask is a VISIBLE row
    // in the brief, equal to the restaurant. So at send time we now
    // merge un-represented line-items into items[] as kind="line_item"
    // — same shape the right-pane already shows.
    //
    // Matching: a line-item is "represented" by a basket venue when
    // either the venue_candidate.name matches a basket item by name,
    // OR the pillar matches a basket item's pillar_name. Otherwise
    // we carry the line-item itself as a row.
    const _briefItems = activeContext?.brief_items || [];
    // v3.46.19 — Phase 1b captured-value extraction (founder catch).
    // The brain currently returns captured_fields as {field: True} sentinels
    // — Phase 2 is value-grounding (mira_brain.py line 564). Until then,
    // we surface the actual values from the conversation_transcript by
    // collecting the last 6 user turns and attaching them to each carried
    // line-item as `member_context`. The Zoho dispatcher renders it under
    // the line-item block so the desk team sees the actual pickup/drop
    // values, not just "captured: pickup, drop".
    // Source: activeContext.transcript_snapshot (already maintained by
    // the MiraChat onResponseMeta effect, line ~1190); messagesRef is
    // scoped to the inner MiraChat component, so we read the snapshot
    // here instead.
    const _convoSnippet = (() => {
      try {
        const snap = (activeContext && activeContext.transcript_snapshot) || [];
        return snap
          .filter(m => m && m.role === "user")
          .slice(-6)
          .map(m => (m.text || "").trim())
          .filter(t => t.length > 0);
      } catch (_) {
        return [];
      }
    })();
    const carriedLineItems = _briefItems
      .filter((li) => {
        if (!li) return false;
        const candName = (li.venue_candidate && li.venue_candidate.name) || null;
        const pillar = li.pillar;
        return !basket.some((b) => {
          if (!b) return false;
          if (candName && b.name === candName) return true;
          if (pillar && b.pillar_name && b.pillar_name === pillar) return true;
          return false;
        });
      })
      .map((li) => {
        const captured = li.captured_fields || {};
        const missing = li.missing_fields || [];
        // Build a "name" that reads as a brief-row title for the desk —
        // mirrors the BasketLineItemRow display.
        const niceName = li.display_name || li.pillar || "Member request";
        return {
          kind: "line_item",
          name: niceName,
          city: captured.city || captured.destination || activeContext?.primary_city || null,
          area: captured.area || captured.pickup_location || null,
          pillar_name: li.pillar || null,
          subcategory: li.subcategory || null,
          item_id: li.item_id || null,
          captured_fields: captured,
          missing_fields: missing,
          is_satisfied: !!li.is_satisfied,
          // v3.46.19 — member's own words for this brief (last 6 user turns)
          // so the desk reads "Narita Terminal 1 arrivals", "Park Hyatt
          // Tokyo, Shinjuku", etc., instead of "pickup: True / drop: True".
          member_context: _convoSnippet,
          source: "phase1b_line_item",
          archive_status: "line_item",
          why: (
            "Phase 1b line-item — concierge to source / arrange. " +
            (missing.length
              ? `Missing from member side at send: ${missing.join(", ")}.`
              : "All operational fields captured.")
          ),
        };
      });
    const mergedItems = [...basket, ...carriedLineItems];

    try {
      const res = await fetch(`${BACKEND_URL}/api/lc/concierge-basket`, {
        method: "POST",
        headers: _authHeadersGlobal(),
        body: JSON.stringify({
          // v3.13 — tenant_id is no longer sent; server derives from token.
          items: mergedItems,
          primary_city: activeContext?.primary_city || cityCapabilities.city || null,
          pattern,
          payment_model: pattern === "B" ? "supplier_direct" : "supplier_direct",
          lc_role: "arranger",
          conversation_summary: transcriptSummary,
          member_name: memberName,
          member_email: memberEmail,
          member_phone: memberPhone,
          // v3.32 — Full chat transcript travels with the brief so the
          // concierge desk has the complete picture in the ticket itself,
          // without having to dig into lc_chat_sessions.
          conversation_transcript: activeContext?.transcript_snapshot || [],
          subject: activeContext?.subject || null,
          // v3.43 — programme-benefit channel routing
          benefit_id: benefitId,
        }),
      });
      const data = await res.json();
      // ── BC-002.fix · backend rejection MUST surface a reason ──
      // The catch block alone caught only network failures. A
      // 4xx/5xx with a JSON body was silently treated as "sent"
      // and the member walked away believing the desk had it.
      // The "never silent" rule applies as much to SOFT failures
      // (backend said no) as to hard ones (network dropped).
      if (!res.ok) {
        const _reason = (data && (data.detail || data.message))
          || "The team couldn't accept this brief just now — please try again.";
        setSubmitState({
          status: "error",
          message: typeof _reason === "string" ? _reason : "Send failed.",
          ticketId: "",
        });
        try {
          toast.error("Brief couldn't be sent", {
            description: typeof _reason === "string" ? _reason
              : "The team couldn't accept this brief just now — please try again.",
            duration: 7000,
          });
        } catch (_) { /* sonner not mounted is non-fatal */ }
        return;
      }
      setSubmitState({ status: "sent", message: data.message, ticketId: data.ticket_id });
      // v3.46.18 — Founder-requested send-confirmation discipline.
      // The bottom-center toast is the unmistakable moment-of-send cue —
      // visible across the screen regardless of where the eye is (chat
      // vs right-pane). The "Brief received" card swap on the right
      // pane stays as today (already wired via submitState.status).
      try {
        toast.success(`✓ Brief sent to Concierge® team`, {
          description: data.ticket_id
            ? `Reference ${data.ticket_id} — they will revert with confirmation.`
            : "Your brief is with the team — they will revert with confirmation.",
          duration: 6000,
        });
      } catch (_) { /* sonner not mounted is non-fatal */ }
      // v3.46.18 — In-chat Mira acknowledgement so the conversation
      // thread itself records the moment. Routes through MiraChat's
      // externalMessage handler (action="send_confirmation"). A primary-
      // city + first-item summary is included so the acknowledgement
      // reads in Mira's voice, not as a system stub.
      try {
        const firstItem = (Array.isArray(basket) && basket[0]) || null;
        const briefBits = [];
        if (firstItem && firstItem.name) briefBits.push(firstItem.name);
        const city = activeContext?.primary_city || cityCapabilities.city || null;
        if (city) briefBits.push(city);
        setExternalMessage({
          action: "send_confirmation",
          ticketId: data.ticket_id,
          briefSummary: briefBits.join(" · ") || null,
          _ts: Date.now(),
        });
      } catch (_) { /* external bus stale is non-fatal */ }
      setBasket([]);
    } catch (err) {
      // ── BC-002.fix · NETWORK failure must also speak ──
      // The right-pane status changes via setSubmitState, but
      // the founder pinned the toast as the visible-across-the-
      // screen cue. Member-attention may be on the chat thread
      // when the click happens · the toast guarantees they SEE
      // the failure.
      setSubmitState({
        status: "error",
        message: "Couldn't reach the team — please call the number below.",
        ticketId: "",
      });
      try {
        toast.error("Brief couldn't reach the team", {
          description:
            "Network hiccup — please try again, or call the number on the page.",
          duration: 8000,
        });
      } catch (_) { /* sonner not mounted is non-fatal */ }
    }
  }

  // v3.6 — Render a human-readable summary of what the Concierge® team will see
  function buildTranscriptSummary(ctx) {
    if (!ctx) return null;
    const lines = [];
    if (ctx.pillar_name) lines.push(`Pillar: ${ctx.pillar_name}`);
    if (ctx.subcategory) lines.push(`Subcategory: ${ctx.subcategory}`);
    if (ctx.primary_city) lines.push(`City: ${ctx.primary_city}`);
    if (Array.isArray(ctx.gathered_details) && ctx.gathered_details.length) {
      lines.push(`Gathered details: ${ctx.gathered_details.join(", ")}`);
    }
    if (ctx.pattern) lines.push(`Pattern: ${ctx.pattern}`);
    if (ctx.sourcing_hint) lines.push(`Note: ${ctx.sourcing_hint}`);
    return lines.length ? lines.join(" · ") : null;
  }

  async function sendProceduralToConcierge() {
    const steps = activeContext?.procedural_steps || [];
    if (steps.length === 0 || procStepsSelection.size === 0) return;
    const selected = steps.filter((s) => procStepsSelection.has(s.number)).map((s) => s.number);
    const skipped = steps.filter((s) => !procStepsSelection.has(s.number)).map((s) => s.number);
    setSubmitState({ status: "sending", message: "", ticketId: "" });
    try {
      const res = await fetch(`${BACKEND_URL}/api/lc/concierge-basket`, {
        method: "POST",
        headers: _authHeadersGlobal(),
        body: JSON.stringify({
          // v3.13 — tenant_id removed from body; server seals via session token.
          // Pass the full step objects + the user-tick selection
          items: steps.map((s) => ({
            kind: "procedural_step",
            number: s.number,
            name: s.title,
            why: s.detail,
            city: activeContext?.primary_city || "",
            selected: procStepsSelection.has(s.number),
            concierge_can_handle: s.concierge_can_handle,
          })),
          primary_city: activeContext?.primary_city || null,
          pattern: "C",
          selected_steps: selected,
          skipped_steps: skipped,
          skipped_reason: skipped.length > 0 ? "member will handle these themselves" : null,
          subject: activeContext?.subject || "Procedural delegation request",
          payment_model: "supplier_direct",
          lc_role: "arranger",
        }),
      });
      const data = await res.json();
      setSubmitState({ status: "sent", message: data.message, ticketId: data.ticket_id });
    } catch (err) {
      setSubmitState({ status: "error", message: "Couldn't reach the team — please call the number below.", ticketId: "" });
    }
  }

  return (
    <div className="lc-page min-h-screen" data-testid="mira-page">
      {/* Pulse animation keyframes */}
      <style>{`
        @keyframes lcPulse {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.3); }
        }
        @keyframes lc-status-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%      { opacity: 0.7; transform: scale(0.95); }
        }
        .lc-status-dot {
          animation: lc-status-pulse 2.4s ease-in-out infinite;
        }
      `}</style>

      {/* Header — concise, doesn't compete with the marketing nav.
          v3.26 — z-index raised + backdrop-filter so it sits cleanly
          above the chat surface on mobile Safari, where sticky can
          otherwise render below the conversation during inertia scroll. */}
      <header
        className="border-b"
        style={{
          background: "var(--lc-bg-elevated, #FFFFFF)",
          backdropFilter: "saturate(180%) blur(10px)",
          WebkitBackdropFilter: "saturate(180%) blur(10px)",
          borderColor: "var(--lc-border-soft)",
          position: "sticky",
          top: 0,
          zIndex: 50,
          isolation: "isolate",
        }}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="lc-text-display"
            style={{
              fontSize: "1.25rem",
              color: "var(--lc-text-primary)",
              textDecoration: "none",
              letterSpacing: "-0.005em",
            }}
            data-testid="header-logo-link"
          >
            Les Concierges<span style={{ color: "var(--lc-highlight)" }}>®</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link
              to="/solutions"
              className="lc-text-body"
              style={{
                fontSize: "0.8125rem",
                color: "var(--lc-text-secondary)",
                textDecoration: "none",
              }}
              data-testid="header-solutions-link"
            >
              Our pillars
            </Link>
            <Link
              to="/connect"
              className="lc-text-body"
              style={{
                fontSize: "0.8125rem",
                color: "var(--lc-text-secondary)",
                textDecoration: "none",
              }}
              data-testid="header-connect-link"
            >
              Connect
            </Link>
          </div>
        </div>
      </header>

      {/* Two-pane body */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 lg:py-8">
        {/* v3.27 — §3.4 — Hide the marketing intro on mobile so the chat
            input sits within the first 100vh. The chat surface header
            already establishes context ("Mira · Your private wellbeing
            desk"). On desktop (≥lg) the intro returns for the editorial
            air it deserves. */}
        <div className="mb-8 hidden lg:block">
          <div className="lc-eyebrow mb-2">Your private wellbeing desk</div>
          <h1
            className="lc-text-display"
            style={{
              fontSize: "var(--lc-text-h2)",
              color: "var(--lc-text-primary)",
              fontWeight: 400,
              lineHeight: 1.15,
              maxWidth: "42rem",
            }}
          >
            Tell Mira what's on your mind. <span style={{ color: "var(--lc-text-secondary)", fontStyle: "italic", fontSize: "0.78em" }}>We take it from there.</span>
          </h1>
        </div>

        <div className="tdc-two-pane">
          {/* Left pane — Mira chat */}
          <div className="tdc-two-pane-chat">
            <MiraChat
              onComposingChange={setIsMiraComposing}
              onResponseMeta={(meta) => {
                // v3.34 — S1: when "Start new chat" or similar fires
                // `reset_basket: true`, clear the brief too. Previously
                // the brief carried over to the new conversation — a
                // safety risk (stale items shipping to the concierge).
                if (meta && meta.reset_basket) {
                  setBasket([]);
                  setSubmitState({ status: "idle", message: "", ticketId: "" });
                }
                // BC-003 · 9 Feb 2026 PM · doctrine fix · sticky merge.
                // Previous behaviour replaced activeContext on every
                // turn — so a thin reply ("Thanks" → chitchat intent →
                // empty brief_items / venue_hints / handoff_moment)
                // would WIPE the right pane down to welcome-mat
                // mid-conversation. Doctrine: a started conversation
                // can never lose its handoff surface. Logic extracted
                // to /lib/mira-active-context for jest coverage —
                // see __tests__/mira-active-context.test.js.
                setActiveContext((prev) => mergeActiveContext(prev, meta));
              }}
              externalMessage={externalMessage}
              onHandoffSend={() => {
                // Pattern C uses the procedural step selection; everything else
                // sends the venue basket. Either way, the same ticket endpoint.
                const pattern = activeContext?.pattern || "A";
                if (pattern === "C" && (activeContext?.procedural_steps || []).length > 0) {
                  return sendProceduralToConcierge();
                }
                return sendBasketToConcierge();
              }}
              submitState={submitState}
            />
          </div>

          {/* Right pane — Pattern-aware: A (venues) / B (sourcing) / C (procedural) */}
          <div className="tdc-two-pane-panel">
            <RightPane
              activeContext={activeContext}
              isComposing={isMiraComposing}
              onArrangeVenue={handleArrangeVenue}
              basket={basket}
              isInBasket={isInBasket}
              addToBasket={addToBasket}
              removeFromBasket={removeFromBasket}
              cityCapabilities={cityCapabilities}
              onSendBasket={sendBasketToConcierge}
              submitState={submitState}
              procStepsSelection={procStepsSelection}
              toggleStep={toggleStep}
              onSendProcedural={sendProceduralToConcierge}
              onPillarTap={handlePillarTap}
            />
          </div>
        </div>

        {/* Mobile promise band (desktop already shows it in right pane) */}
        <div className="lg:hidden mt-4">
          <PromiseBand />
        </div>
      </main>

      {/* Footer — minimal, doesn't compete */}
      <footer
        style={{
          marginTop: "4rem",
          padding: "2rem 1.5rem",
          borderTop: "1px solid var(--lc-border-soft)",
          background: "var(--lc-bg-card)",
        }}
      >
        <div className="max-w-7xl mx-auto text-center">
          <div
            className="lc-text-display"
            style={{ fontSize: "0.9rem", color: "var(--lc-text-secondary)", marginBottom: "0.5rem", fontStyle: "italic" }}
            data-testid="footer-sacred-line"
          >
            The AI listens. The humans deliver.
          </div>
          <div
            className="lc-text-body lc-text-muted"
            style={{ fontSize: "0.72rem", letterSpacing: "0.06em" }}
            data-testid="footer-trademark"
          >
            Les Concierges® · since 1998 · Mira · the Les Concierges® AI
          </div>
        </div>
      </footer>
    </div>
  );
}

export default function MiraPage() {
  return (
    <ThemeProvider tenant={{ theme: DEFAULT_THEME }}>
      <MiraPageInner />
    </ThemeProvider>
  );
}
